import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { money } from '@/lib/finance';
import useSWR from 'swr';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  Camera,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  CircleCheck,
  Clock,
  Crop,
  Image as ImageIcon,
  LayoutGrid,
  ListFilter,
  ListTodo,
  Maximize2,
  Minimize2,
  ArrowUpRight,
  ChevronDown,
  Bot,
  BotOff,
  ZoomIn,
  ZoomOut,
  Moon,
  Settings2,
  UtensilsCrossed,
  ImageOff,
  RefreshCw,
  TrendingUp,
  Bell, ShoppingBasket } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useGlobalNoticeActive } from '@/lib/serverHealth';
import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { useCan } from '@/hooks/useCan';
import { useCurrentZone } from '@/hooks/useCurrentZone';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useMeasuredBox } from '@/hooks/useMeasuredBox';
import { cachedAspect, primeAspect, useImageAspects } from '@/hooks/useImageAspects';
import { justifiedFitAll, justifiedPages, type JustifiedPage } from './justifiedLayout';
import { useSwipePager } from '@/hooks/useSwipePager';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { listOpsTasks, type OpsTaskListItem, type OpsTaskStatus } from '@/api/opsTasks';
import { SubmitReportDialog } from '@/components/SubmitReportDialog';
import { useTelegramAutoLogin } from '@/hooks/useTelegramAutoLogin';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import { usePageTitle, usePageHeaderSlots } from '@/contexts/PageHeaderContext';
import { toAbsoluteUrl, stableImageUrl } from '@/api/url';
import { API_HOST } from '@/api/base';
import { TableProofImage, useTableProof, useTableProofTime, type LiveTableProof } from '@/components/dashboard/table-proof';
import { ImageLightbox, type LightboxCropSpec } from '@/components/ui/image-lightbox';
import { FrameWithPolygon } from '@/components/ui/frame-with-polygon';
import { RobotParts, makeRig, makeJet, blinkAt, bonkPose, knockPose, wavePose, KNOCKS, KNOCK_IN, KNOCK_MS, type BotPose } from './robotRig';
import { DelayedSpinner } from '@/components/ui/delayed-spinner';
import { probeCachedImage, recallPainted, rememberPainted } from '@/lib/imagePaint';
import { PolygonCrop } from '@/components/projection/PolygonCrop';
import { feCropAspect, polysBBox } from '@/utils/cropGeometry';
import { HoverTime, useFrameTime } from '@/components/ui/hover-time';
import { tableSummaryStatus } from '@/lib/tableSummary';
import {
  getTablesSummary,
  getLayoutSummary,
  getQueueSummary,
  holdQueueProblems,
  getZoneOpenStatus,
  rescanTable,
  markRackFixed,
  type LayoutHealth,
  type TablesSummary,
  type DirtyTableSample,
  type LayoutSummary,
  type QueueSummary,
  type RackSample,
  type QueueSample,
  type ShelfMark,
  type ZoneOpenStatus,
} from '@/api/dashboardSummary';
import { listChecklists, type ChecklistDto } from '@/api/ops_tasks';
import type { Paginated } from '@/api/types';
import { getSalesPlanToday, type SalesPlanTodayDto } from '@/api/salesPlan';
import { getReceiptsToday, type ReceiptsTodayDto } from '@/api/receipts';
import { useZoneFeatures } from '@/hooks/useZoneFeatures';
import { Button } from '@/components/ui/button';
import { Lightbulb, Receipt, Users } from 'lucide-react';
import { WelcomeCard } from '@/components/PluginTeaserRow';
import {
  HeaderStatusDot,
  SAMPLE_DOT,
  STATUS_GREEN,
  TONE_DOT,
  type OverallTone,
} from '@/components/dashboard/statusTokens';
import { useAppState } from '@/app/appContext';
import { useKiosk } from '@/contexts/KioskContext';
import { getCurrentUsername } from '@/lib/auth';
import { applyUpdate, consumeJustUpdated, useUpdateReady } from '@/hooks/useAutoReloadOnUpdate';
import { useOpenZone } from '@/hooks/useOpenZone';
import { AllBranchesDashboard } from '@/components/portfolio/AllBranchesDashboard';
import {
  checklistsConfigured,
  layoutConfigured,
  queuesConfigured,
  tablesConfigured,
} from '@/lib/zoneComponents';

// Every dashboard image source goes through this: absolutise it, then stabilise
// the presigned signature so the 10s refresh doesn't re-download/blink it.
const imgSrc = (u: string) => stableImageUrl(toAbsoluteUrl(u, API_HOST));

// A photo from a PREVIOUS day must not masquerade as the floor's current state
// (branches with broken cameras were showing months-old frames) — such tiles
// render a "no frames today" placeholder instead.
const STALE_FRAME_MS = 10 * 60_000;

function isBeforeToday(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return d < new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

// Returns true only after `active` has stayed continuously true for `delayMs`.
// Used to gate the "couldn't load" card: the dashboard summaries are slow and
// SWR keeps retrying, so a card must show its loading skeleton — NOT an error —
// until a failure is genuinely SETTLED (SWR has stopped retrying), and even then
// only after a short debounce so the gaps BETWEEN retries never flash an error.
function useSettledFlag(active: boolean, delayMs: number): boolean {
  const [flag, setFlag] = useState(false);
  useEffect(() => {
    if (!active) {
      setFlag(false);
      return;
    }
    const id = window.setTimeout(() => setFlag(true), delayMs);
    return () => window.clearTimeout(id);
  }, [active, delayMs]);
  return flag;
}


// Маленький экран — телефон в любой ориентации (низкий или узкий): плотная обвязка карточек, мельче
// заголовки разделов и строки задач (Алексей 03.11 #8648: «оч много пустого места — паддинги карточек,
// заголовки… тела задач из чеклиста особенно громоздкие для такого экрана, для большого норм»).
// Меряем окно, а не масштаб дашборда: планшеты и десктопы остаются как были.
const COMPACT_DASH_QUERY = '(max-height: 560px), (max-width: 767px)';
// Обвязка карточки на маленьком экране: отступы, шапка раздела (шрифт, иконка, высота строки).
// Шапка — одна строка 16 px: иконка 12 px, название 11 px, точка статуса (Алексей 03.11 #8665: «заголовки
// карточек всё ещё съедают львиную долю места») — место отдаём фото.
// Единая карточка дашборда (Алексей 03.11 #8699/#8701/#8706): у карточек доски и колонки справа (средний
// чек, «Обычно сейчас берут», чек-лист) — одна рамка, тень, скругление, отступы и одна шапка: значок в
// тонированном кружке + название жирным, на маленьком экране — мельче.
const DASH_CARD = 'rounded-xl border border-border bg-card shadow-[0_1px_2px_rgba(15,23,42,0.04)]';
const dashCardBody = (dense: boolean) => (dense ? 'gap-1 px-2 pb-2 pt-1.5' : 'gap-2 px-3 pb-3 pt-2.5');

function CardHead({ icon: Icon, title, dense, tint = 'bg-primary/10 text-primary', after, right }: {
  icon: React.ElementType;
  title: React.ReactNode;
  dense: boolean;
  /** Цвет кружка и значка. */
  tint?: string;
  /** Сразу за названием (точка статуса). */
  after?: React.ReactNode;
  /** Справа в шапке (счётчик, пейджер, ссылка). */
  right?: React.ReactNode;
}) {
  return (
    <div className={cn('flex shrink-0 items-center justify-between gap-2', dense ? 'h-5' : 'h-6')}>
      <div className={cn('flex min-w-0 items-center font-semibold text-foreground', dense ? 'gap-1.5 text-[12px]' : 'gap-2 text-sm')}>
        <span className={cn('grid shrink-0 place-items-center rounded-full', dense ? 'size-5' : 'size-6', tint)}>
          <Icon className={dense ? 'size-3' : 'size-3.5'} />
        </span>
        <span className="truncate">{title}</span>
        {after}
      </div>
      {right}
    </div>
  );
}
function useCompactDash(): boolean {
  return useMediaQuery(COMPACT_DASH_QUERY);
}

function StatusBadge({ status, className }: { status: OpsTaskStatus; className?: string }) {
  const { t } = useT();
  switch (status) {
    case 'overdue':
      return <Badge variant="destructive" className={className}>{t('tasks.statusOverdue')}</Badge>;
    case 'pre_deadline':
      return <Badge variant="warning" className={className}>{t('tasks.statusPreDeadline')}</Badge>;
    case 'done':
      return <Badge variant="success" className={className}>{t('tasks.statusDone')}</Badge>;
    default:
      return <Badge variant="secondary" className={className}>{t('tasks.statusPending')}</Badge>;
  }
}

function useDeadline() {
  const { t, lang } = useT();
  const locale = lang === 'en' ? 'en-US' : 'ru-RU';
  return (iso: string | null): string => {
    if (!iso) return '';
    const due = new Date(iso);
    const now = new Date();
    const timeStr = due.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const diffDays = Math.round((dueDay - today) / 86400000);
    if (diffDays <= 0) return t('dashboard.cards.tasksDue', { time: timeStr });
    if (diffDays === 1) return t('dashboard.cards.tasksDueTomorrow', { time: timeStr });
    const dateStr = due.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
    return t('dashboard.cards.tasksDue', { time: `${dateStr} ${timeStr}` });
  };
}

// ── Shared building blocks ───────────────────────────────────────────────────
// Status tokens (STATUS_GREEN / TONE_DOT / SAMPLE_DOT / HeaderStatusDot) are
// shared with the all-branches portfolio cards — see statusTokens.tsx.

// Shared SWR config for the live shift-dashboard reads. Differs from the old
// per-card config in two deliberate ways:
//  • shouldRetryOnError: true (bounded) — a transient wedged keep-alive socket
//    self-heals via retry instead of sticking on an error until a manual reload.
//  • refreshInterval: 10s — the board reflects the floor's actual current state.
// The 10s poll does NOT blink the loaders: SWR keeps the current data during a
// same-key background revalidation (isLoading only fires on the first load), so
// the card stays rendered and the new photos swap in place. (We deliberately do
// NOT set keepPreviousData — on a BRANCH switch the key changes and we DO want
// the fresh skeleton, not the previous branch's photos lingering.)
const DASH_SWR = {
  revalidateOnFocus: false,
  shouldRetryOnError: true,
  errorRetryCount: 6,
  errorRetryInterval: 2_000,
  refreshInterval: 10_000,
} as const;

// ── Card 1: ops tasks ────────────────────────────────────────────────────────

// Sort: problems first (open tasks before done), then by status severity so the
// most urgent open task floats to the very top.
const TASK_STATUS_RANK: Record<OpsTaskStatus, number> = {
  overdue: 0,
  pre_deadline: 1,
  created: 2,
  pending: 2,
  done: 3,
};

function MyTasksCard({ zoneId, search, bare, problemsOnly, column }: {
  zoneId: string | null; search: string; bare?: boolean; problemsOnly?: boolean;
  /** Телефон боком: задачи — в правой колонке под средним чеком, строкой на задачу (Алексей 03.11 #8665). */
  column?: boolean;
}) {
  const { t } = useT();
  const formatDue = useDeadline();
  const tasksCelebrating = useCelebrating('tasks');
  const [captureTask, setCaptureTask] = useState<OpsTaskListItem | null>(null);
  const [capturedFiles, setCapturedFiles] = useState<File[]>([]);
  const captureRef = useRef<HTMLInputElement>(null);

  // Tasks scoped to today's local-time window; the BE filter rejects historical
  // and future records, and orders open-first so the top-3 are actionable.
  const { data, error, isValidating, mutate } = useSWR<{ results: OpsTaskListItem[] }>(
    zoneId ? ['dashboard:ops_tasks', zoneId] : null,
    async () =>
      (await listOpsTasks({ zoneId: zoneId!, onlyToday: true })).data,
    DASH_SWR,
  );

  // Used only to gate the "open all tasks" arrow link — shown only when there
  // is actually a checklist to navigate to. Card itself is hidden at page level
  // when checklist is not configured (see WorkerDashboardPage activeTeasers).
  const { data: checklists } = useSWR<Paginated<ChecklistDto>>(
    zoneId ? ['dashboard:checklists-status', zoneId] : null,
    async () => (await listChecklists(zoneId!, 1)).data,
    DASH_SWR,
  );
  const hasChecklist = !!checklists && checklists.results.length > 0;

  // Error UI only when there is NOTHING to show: during the 10s polling, a
  // transient failed tick must keep the last good data on screen (SWR retains
  // it) instead of blinking the card into "couldn't load" and back.
  const apiMissing = !!error && data === undefined;
  // Show the error card only on a SETTLED failure (SWR done retrying), debounced
  // so the slow first load / inter-retry gaps show the skeleton, not an error.
  const showError = useSettledFlag(apiMissing && !isValidating, 2500);
  const loading = data === undefined && !showError;
  // Problems (open tasks) first, then by status severity.
  const allTasks = [...(data?.results ?? [])].sort((a, b) => {
    const ap = a.status === 'done' ? 1 : 0;
    const bp = b.status === 'done' ? 1 : 0;
    if (ap !== bp) return ap - bp;
    return TASK_STATUS_RANK[a.status] - TASK_STATUS_RANK[b.status];
  });
  // "Только проблемы": open tasks only; everything done → quiet all-clear note.
  const all = problemsOnly ? allTasks.filter(tk => tk.status !== 'done') : allTasks;

  // «Только проблемы», все задачи выполнены: список пуст, но это не «нет задач», а
  // «всё сделано» — зелёный, не синий (Алексей 22.10, #1880 п.6).
  const allDoneFiltered = problemsOnly && all.length === 0 && allTasks.length > 0;
  const overallTone: OverallTone = all.length === 0
    ? (allDoneFiltered ? 'ok' : 'none')
    : all.some(t => t.status === 'overdue')
      ? 'critical'
      : all.some(t => t.status !== 'done')
        ? 'attention'
        : 'ok';
  const overallLabel = all.length === 0
    ? (allDoneFiltered ? t('dashboard.status.tasksAllDone') : t('dashboard.cards.tasksEmpty'))
    : all.some(t => t.status !== 'done')
      ? t('dashboard.status.tasksOpen')
      : t('dashboard.status.tasksAllDone');

  function openCapture(task: OpsTaskListItem) {
    setCaptureTask(task);
    if (captureRef.current) {
      captureRef.current.value = '';
      captureRef.current.click();
    }
  }

  // Shared hidden capture input. Single-shot camera (no `multiple` so the OS
  // honours `capture`); more shots / gallery picks happen in the report dialog.
  const hiddenInput = (
    <input
      ref={captureRef}
      type="file"
      accept="image/*"
      capture="environment"
      className="hidden"
      onChange={e => {
        const files = Array.from(e.target.files ?? []);
        if (files.length > 0) setCapturedFiles(files);
        else setCaptureTask(null);
        if (captureRef.current) captureRef.current.value = '';
      }}
    />
  );

  // Пейджер задач — в шапке карточки, рядом с «↗» (Алексей 03.11 #8615): внизу поверх списка он при
  // крупном масштабе закрывал срок и фото единственной задачи на странице.
  const [pagerSlot, setPagerSlot] = useState<HTMLSpanElement | null>(null);
  const dense = useCompactDash();
  const body = (
    <>
      {!zoneId && <EmptyHint text={t('page.selectBranchFirst')} />}
      {zoneId && loading && <Skeletons count={3} />}
      {zoneId && showError && <CardError onRetry={() => mutate()} />}
      {zoneId && !loading && !showError && all.length > 0 && (
        bare ? (
          // Mobile: the section is the whole screen — native scroll works well
          // with the page-level swipe gestures, no pager needed.
          <ul className={cn('min-h-0 flex-1 overflow-auto', dense ? 'space-y-0.5' : 'space-y-1.5')}>
            {all.map(task => (
              <DashTaskItem
                key={task.id}
                task={task}
                isDone={task.status === 'done'}
                photoSrcs={(task.latest_report?.photos ?? []).flatMap(p =>
                  p.image_url ? [imgSrc(p.image_url)] : [],
                )}
                relative={formatDue}
                onCapture={openCapture}
                mini={dense}
                clamp2
              />
            ))}
          </ul>
        ) : (
          // Desktop: the panel is height-bounded → real pagination, fitted to
          // the measured box (recomputed on throttled resize), like the photo
          // grids. Scroll inside a board card was rejected — it hid tasks.
          <TasksPagedList tasks={all} relative={formatDue} onCapture={openCapture} pagerSlot={pagerSlot} mini={dense} />
        )
      )}
      {zoneId && !loading && !showError && all.length === 0 && (
        problemsOnly && allTasks.length > 0 ? (
          <AllOkBody text={t('dashboard.status.tasksAllDone')} />
        ) : (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className={cn('inline-block size-2 shrink-0 rounded-full', STATUS_GREEN)} />
            {t('dashboard.cards.tasksEmpty')}
          </div>
        )
      )}
    </>
  );

  const dialog = captureTask && capturedFiles.length > 0 && (
    <SubmitReportDialog
      task={captureTask}
      initialFiles={capturedFiles}
      onClose={() => { setCaptureTask(null); setCapturedFiles([]); }}
      onSubmitted={() => { setCaptureTask(null); setCapturedFiles([]); mutate(); }}
    />
  );

  // Mobile (bare): no Card / no title — the bottom tab names the section. The
  // list scrolls natively; the page-level horizontal swipe switches tabs.
  if (bare) {
    return <MyTasksCardBare hiddenInput={hiddenInput} body={body} dialog={dialog} />;
  }

  return (
    // В колонке карточка по высоте списка (не тянется пустой), но не выше места под ней.
    <Card className={cn('flex min-h-0 flex-col', column ? 'max-h-full w-full' : 'h-full')}>
      <CardContent className={cn('relative flex min-h-0 flex-col', column ? 'max-h-full' : 'h-full', dashCardBody(dense))}>
        {tasksCelebrating && <InCardCelebrate sectionKey="tasks" />}
        {hiddenInput}
        <CardHead icon={ListTodo} title={t('dashboard.cards.tasks')} dense={dense}
          after={zoneId && !loading && !showError && data !== undefined && !(dense && overallTone === 'ok')
            ? <HeaderStatusDot tone={overallTone} label={overallLabel} /> : null}
          right={
          <div className="flex shrink-0 items-center gap-1">
          <span ref={setPagerSlot} className="contents" />
          {/* На маленьком экране «↗ все задачи» не нужна (Алексей 03.11 #8665: «и так норм тут»). */}
          {hasChecklist && !dense && (
            <Tooltip content={t('dashboard.cards.tasksOpenAll')} side="left">
              <Link
                to={{ pathname: '/usage/tasks', search }}
                className={cn(buttonVariants({ variant: 'ghost', size: 'icon-sm' }), 'shrink-0 text-muted-foreground', dense && 'size-6')}
                aria-label={t('dashboard.cards.tasksOpenAll')}
              >
                {/* «Перейти», а не «на весь экран» (Алексей 22.10, #1881). */}
                <ArrowUpRight className="size-4" />
              </Link>
            </Tooltip>
          )}
          </div>
          } />
        {body}
        {dialog}
      </CardContent>
    </Card>
  );
}

// Mobile bare tasks body — the list scrolls natively (no vertical dot pager);
// the page-level horizontal swipe still switches tabs.
function MyTasksCardBare({
  hiddenInput,
  body,
  dialog,
}: {
  hiddenInput: React.ReactNode;
  body: React.ReactNode;
  dialog: React.ReactNode;
}) {
  return (
    <div className="relative flex h-full min-h-0 flex-col gap-3">
      {hiddenInput}
      {body}
      {dialog}
    </div>
  );
}

// Крупный системный шрифт (Android «размер текста», #8615) увеличивает всё, что в rem: пороги «тесно»
// для строк задач и чипа очереди считаем в тех же единицах, иначе при шрифте ×1,5 они не срабатывали.
function remScale(): number {
  try { return (parseFloat(getComputedStyle(document.documentElement).fontSize) || 16) / 16; } catch { return 1; }
}

// Desktop tasks list with REAL pagination fitted to the measured panel height:
// items flow into pages while their (measured, estimate until then) heights
// fit the box — so the card never scrolls and never clips a task, and the page
// size recomputes on (throttled) resize, exactly like the photo grids.
// Heights are measured from the live <li>s (a wrapping title makes rows vary)
// and cached per task id; a height change beyond tolerance just repaginates.
function TasksPagedList({
  tasks,
  relative,
  onCapture,
  pagerSlot,
  mini = false,
}: {
  tasks: OpsTaskListItem[];
  relative: (iso: string | null) => string;
  onCapture: (task: OpsTaskListItem) => void;
  /** Место в шапке карточки под ‹1/4›; нет — пейджер поверх низа списка, под него резервируем строку. */
  pagerSlot?: HTMLElement | null;
  /** Строка на задачу (маленький экран). */
  mini?: boolean;
}) {
  const GAP = mini ? 2 : 8;
  const { ref, height } = useMeasuredBox<HTMLDivElement>(150);
  const [page, setPage] = useState(0);
  const heightsRef = useRef(new Map<string | number, number>());
  const [, setMeasureTick] = useState(0);

  // Estimate until measured. Rows are a two-column layout (text left, camera
  // button / photo strip right), so height depends on NEITHER photo count NOR
  // status — a row is ~60px, slightly over-estimated here since a title can
  // wrap to two lines; the measured height corrects it. Constant-height rows
  // are what make pagination reliable (the old below-the-text photo grid grew
  // with content and the last task got clipped).
  const ROW_ESTIMATE = mini ? 24 : 66;
  // Низкая панель (телефон боком, крупный масштаб или шрифт) — название задачи в одну строку, фото меньше:
  // иначе даже одна задача не помещалась и обрезалась снизу вместе со сроком (#8615). Решаем по высотам
  // строк в ПОЛНОМ виде (меряются отдельно от компактных), чтобы не качаться между видами.
  const fullH = (task: OpsTaskListItem) => heightsRef.current.get(`f:${task.id}`) ?? ROW_ESTIMATE * remScale();
  const tight = !mini && height > 0 && (height < ROW_ESTIMATE * remScale() * 1.5 || tasks.some(task => fullH(task) > height));
  const heightOf = (task: OpsTaskListItem) => heightsRef.current.get(`${tight ? 'c' : 'f'}:${task.id}`) ?? ROW_ESTIMATE;

  const rowFit = useContext(RowFitContext);
  const natural = tasks.reduce((a, task, i) => a + (i ? GAP : 0) + heightOf(task), 0);
  useEffect(() => { if (rowFit && height > 0) rowFit(Math.round(height - natural)); }, [rowFit, height, natural]);
  useEffect(() => () => rowFit?.(null), [rowFit]);

  const paginate = (boxH: number) => {
    const out: OpsTaskListItem[][] = [];
    let cur: OpsTaskListItem[] = [];
    let used = 0;
    for (const task of tasks) {
      const h = heightOf(task);
      if (cur.length && used + GAP + h > boxH) {
        out.push(cur);
        cur = [];
        used = 0;
      }
      used += (cur.length ? GAP : 0) + h;
      cur.push(task);
    }
    if (cur.length) out.push(cur);
    return out;
  };
  const PAGER_H = pagerSlot ? 0 : 30;
  let pages: OpsTaskListItem[][] = [tasks];
  if (height > 0) {
    pages = paginate(height);
    if (pages.length > 1 && PAGER_H) pages = paginate(Math.max(1, height - PAGER_H));
  }
  const totalPages = Math.max(1, pages.length);
  const safePage = Math.min(Math.max(0, page), totalPages - 1);

  const measureItem = (id: string | number) => (el: HTMLLIElement | null) => {
    if (!el) return;
    const h = el.offsetHeight;
    const key = `${tight ? 'c' : 'f'}:${id}`;
    const prev = heightsRef.current.get(key);
    if (prev === undefined || Math.abs(prev - h) > 2) {
      heightsRef.current.set(key, h);
      setMeasureTick(tk => tk + 1); // repaginate with the real height
    }
  };

  return (
    <div ref={ref} className="relative min-h-0 flex-1 overflow-hidden">
      <ul className={cn('flex h-full flex-col overflow-hidden', mini ? 'gap-0.5' : 'gap-2')} style={pages.length > 1 && PAGER_H ? { paddingBottom: PAGER_H } : undefined}>
        {(pages[safePage] ?? []).map(task => (
          <DashTaskItem
            key={task.id}
            task={task}
            isDone={task.status === 'done'}
            photoSrcs={(task.latest_report?.photos ?? []).flatMap(p =>
              p.image_url ? [imgSrc(p.image_url)] : [],
            )}
            relative={relative}
            onCapture={onCapture}
            measure={measureItem(task.id)}
            compact={tight}
            mini={mini}
            // Совсем низко (масштаб 150 % на телефоне боком): без строки «Готово · Срок» — статус несёт
            // цветная полоса слева, срок — в подсказке к названию.
            noMeta={tight && height < ROW_ESTIMATE * remScale() * 0.85}
          />
        ))}
      </ul>
      {pagerSlot
        ? createPortal(<SectionPager page={safePage} totalPages={totalPages} setPage={setPage} inline />, pagerSlot)
        : <SectionPager page={safePage} totalPages={totalPages} setPage={setPage} />}
    </div>
  );
}

// Compact status visuals for the dashboard task row — mirrors the OpsTasks
// card's accent-rail + leading-token language, scaled down for the dashboard.
function dashTaskMeta(status: OpsTaskStatus) {
  switch (status) {
    case 'overdue':
      return { rail: 'bg-destructive', token: 'text-destructive', Icon: AlertCircle };
    case 'pre_deadline':
      return { rail: 'bg-warning', token: 'text-warning', Icon: Clock };
    case 'done':
      return { rail: 'bg-success', token: 'text-success', Icon: CheckCircle2 };
    default:
      return { rail: 'bg-border', token: 'text-muted-foreground', Icon: Circle };
  }
}

function DashTaskItem({
  task,
  isDone,
  photoSrcs,
  relative,
  onCapture,
  measure,
  compact = false,
  noMeta = false,
  mini = false,
  clamp2 = false,
}: {
  task: OpsTaskListItem;
  isDone: boolean;
  photoSrcs: string[];
  relative: (iso: string | null) => string;
  onCapture: (task: OpsTaskListItem) => void;
  /** Reports the rendered row height (TasksPagedList pagination). */
  measure?: React.Ref<HTMLLIElement>;
  /** Одна строка названия (полностью — в подсказке) и фото поменьше — для низкой панели. */
  compact?: boolean;
  noMeta?: boolean;
  /** Строка на задачу: значок статуса, название, срок; без фото отчёта и бейджа (Алексей 03.11 #8665). */
  mini?: boolean;
  /** В mini — название до двух строк (телефон вертикально), иначе в одну. */
  clamp2?: boolean;
}) {
  const { t } = useT();
  const frameTime = useFrameTime();
  const dense = useCompactDash();
  // В полноэкранном (табло для зала) кнопка «сфотографировать» не нужна и съедает ширину (#3107).
  const { kiosk } = useKiosk();
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const meta = dashTaskMeta(task.status);
  const { Icon } = meta;

  // Photos carry the report's capture time + reporter for the hover chip and the
  // lightbox caption (mirrors the Выкладка/queue photo treatment).
  const report = task.latest_report;
  const reporter = report?.reporter_username
    ? report.reporter_username.replace(/^Менеджеры: |^Вы: /, '')
    : '';
  const photoTimes = (report?.photos ?? []).flatMap(p =>
    p.image_url ? [frameTime(p.created_at)] : [],
  );
  const captionFor = (i: number) =>
    [task.title, reporter, photoTimes[i]].filter(Boolean).join(' · ');

  if (mini) {
    // Статус — значком и цветной полоской слева, срок — справа мелко; полностью — в подсказке.
    const due = task.due_at ? relative(task.due_at) : '';
    // В строке — только время на сегодня («08:00»), без «Срок:»: иначе он съедал название.
    const dueShort = task.due_at && !isBeforeToday(task.due_at) && new Date(task.due_at).toDateString() === new Date().toDateString()
      ? new Date(task.due_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : due;
    return (
      <li ref={measure} className="relative flex min-h-6 shrink-0 items-center gap-1.5 overflow-hidden py-0.5 pl-2 pr-0.5 text-[12px] leading-tight">
        <span className={cn('absolute inset-y-0.5 left-0 w-0.5 rounded-full', meta.rail)} aria-hidden />
        {/* Выполненная — без галки: её показывает зелёная полоска слева (#8669). */}
        {!isDone && <Icon className={cn('size-3.5 shrink-0', meta.token)} />}
        <Tooltip content={[task.title, due].filter(Boolean).join(' · ')}>
          <span className={cn('min-w-0 flex-1 font-medium', clamp2 ? 'line-clamp-2' : 'truncate')}>{task.title}</span>
        </Tooltip>
        {dueShort && <span className={cn('shrink-0 whitespace-nowrap text-[11px] tabular-nums', meta.token)}>{dueShort}</span>}
        {!isDone && !kiosk && (
          <Tooltip content={t('tasks.takePhoto')} side="left">
            <button
              type="button"
              onClick={() => onCapture(task)}
              className="grid size-6 shrink-0 place-items-center rounded-full border border-primary/30 bg-primary/10 text-primary"
              aria-label={t('tasks.takePhoto')}
            >
              <Camera className="size-3" />
            </button>
          </Tooltip>
        )}
      </li>
    );
  }

  return (
    // Two-column row: text (title + status/due) grows left, the action/proof
    // column sits vertically CENTERED on the right. Keeping the camera button
    // (36px) out of the title line matters on wide screens: in the same row it
    // stretched a one-line title to button height and opened a hole between
    // the title and the status row. Done-task photos live in the same right
    // column — previously they rendered BELOW the text while the whole right
    // side sat empty, which doubled the row height for nothing.
    <li ref={measure} className={cn('relative shrink-0 overflow-hidden rounded-md border border-border', dense ? 'pl-2' : 'pl-3')}>
      <span className={cn('absolute inset-y-0 left-0 w-1', meta.rail)} aria-hidden />
      {/* Маленький экран (Алексей 03.11 #8633/#8648: «тела задач громоздкие»): шрифт 12–13 px, название
          не длиннее двух строк (полностью — в подсказке), статус и срок — мелкой строкой, фото мельче. */}
      <div className={cn('flex items-center', dense ? 'gap-1.5 px-1.5 py-1 text-[13px]' : 'gap-2 px-2.5 py-1.5 text-sm', compact && 'py-1')}>
        <div className={cn('flex min-w-0 flex-1 flex-col', compact || dense ? 'gap-0.5' : 'gap-1')}>
          <div className={cn('flex items-start', dense ? 'gap-1.5' : 'gap-2')}>
            <Icon className={cn('shrink-0', dense ? 'mt-px size-3.5' : 'mt-0.5 size-4', meta.token)} />
            <Tooltip content={compact || dense ? [task.title, task.due_at ? relative(task.due_at) : null].filter(Boolean).join(' · ') : null}>
              <span className={cn('min-w-0 flex-1 font-medium', dense ? 'leading-tight' : 'leading-snug',
                compact ? 'truncate' : dense ? 'line-clamp-2' : 'break-words')}>{task.title}</span>
            </Tooltip>
          </div>
          <div className={cn('flex min-w-0 items-center overflow-hidden', dense ? 'gap-1.5 pl-5' : 'gap-2 pl-6', noMeta && 'hidden')}>
            <StatusBadge status={task.status} className={dense ? 'h-4 px-1.5 py-0 text-[10px] leading-none' : undefined} />
            {task.due_at && (
              <span className={cn('flex min-w-0 items-center gap-1 whitespace-nowrap', dense ? 'text-[11px]' : 'text-xs', meta.token)}>
                <Clock className={dense ? 'size-2.5' : 'size-3'} />
                {relative(task.due_at)}
              </span>
            )}
          </div>
        </div>
        {!isDone && !kiosk && (
          // Icon-only on the narrow 2-col dashboard card so the title keeps its
          // width (a labelled button squeezed titles into mid-word wraps). The
          // /usage/tasks button keeps its label — those cards are full-width.
          <Tooltip content={t('tasks.takePhoto')} side="left">
            <button
              type="button"
              onClick={() => onCapture(task)}
              className={cn('grid shrink-0 place-items-center rounded-full border border-primary/30 bg-primary/10 text-primary transition-colors hover:border-primary/50 hover:bg-primary/15', dense ? 'size-8' : 'size-9')}
              aria-label={t('tasks.takePhoto')}
            >
              <Camera className="size-4" />
            </button>
          </Tooltip>
        )}
        {isDone && photoSrcs.length > 0 && (
          // ONE horizontal strip (scrolls sideways if many), width-capped so the
          // title keeps at least half the row: report photos are PROOF, not the
          // dashboard's subject, so the strip never wraps and the row height
          // stays constant regardless of photo count — which keeps the desktop
          // pagination from clipping a photo-heavy task.
          <div className="flex max-w-[50%] shrink-0 gap-1.5 overflow-x-auto">
            {photoSrcs.map((src, i) => (
              <button
                key={src}
                type="button"
                className={cn('group relative shrink-0 overflow-hidden rounded-md border border-border bg-background', compact || dense ? 'size-9' : 'size-12')}
                onClick={() => setLightboxIdx(i)}
              >
                <img src={src} alt="" className="size-full object-cover" />
                <HoverTime time={photoTimes[i]} corner="bottom-right" />
              </button>
            ))}
            {lightboxIdx >= 0 && (
              <ImageLightbox
                srcs={photoSrcs}
                index={lightboxIdx}
                alt={task.title}
                captions={photoSrcs.map((_, i) => captionFor(i))}
                onClose={() => setLightboxIdx(-1)}
                onNavigate={setLightboxIdx}
              />
            )}
          </div>
        )}
      </div>
    </li>
  );
}

// ── Section card shell ──────────────────────────────────────────────────────

// Shared chrome for the image-card sections. Desktop: a framed Card with a
// header (icon + title + status dot + badge) and a fill body. Mobile (bare):
// NO card / NO title — the bottom tab already names the section — just the body
// filling the screen edge-to-edge. h-full + min-h-0 throughout so the adaptive
// ProofGrid inside can measure and fill.
function SectionCardShell({
  bare,
  icon: Icon,
  title,
  tone,
  toneLabel,
  toneDotClass,
  badge,
  sectionKey,
  children,
}: {
  bare?: boolean;
  sectionKey?: string;
  icon: React.ElementType;
  title: string;
  tone?: OverallTone;
  toneLabel?: string;
  /** Colour override for the header circle — the worst inner dot's colour. */
  toneDotClass?: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  const celebrating = useCelebrating(sectionKey);
  const dense = useCompactDash();
  if (bare) {
    return <div className="flex h-full min-h-0 flex-col">{children}</div>;
  }
  return (
    <Card className="flex h-full min-h-0 flex-col">
      <CardContent className={cn('flex h-full min-h-0 flex-col', dashCardBody(dense))}>
        <CardHead icon={Icon} title={title} dense={dense}
          // Маленький экран: зелёную «в норме» в шапке не рисуем — место дорогое, норма и так видна
          // (Алексей 03.11 #8669); жёлтая/красная/серая остаются.
          after={tone && toneLabel && !(dense && tone === 'ok') ? <HeaderStatusDot tone={tone} label={toneLabel} dotClass={toneDotClass} /> : null}
          right={dense && badge ? <span className="origin-right scale-75">{badge}</span> : badge} />
        <div className={cn('relative flex min-h-0 flex-1 flex-col', dense ? 'gap-1' : 'gap-1.5 md:gap-2')}>
          {children}
          {celebrating && sectionKey && <InCardCelebrate sectionKey={sectionKey} />}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Card 2: Cleanliness of tables ───────────────────────────────────────────

// Physical-cluster order for tables: same camera together, then reading order
// on the frame (row band top→bottom, then left→right), then natural name order
// ("Бар у окна 2" right after "Бар у окна 1"). Physically adjacent tables sit
// on the same camera and are marked up one after another, so this puts
// real-world neighbours side by side on the board.
const nameCollator = new Intl.Collator('ru', { numeric: true, sensitivity: 'base' });
function polyCentroid(polygons?: number[][][] | null): { x: number; y: number } | null {
  const pts = polygons?.[0];
  if (!pts?.length) return null;
  let x = 0, y = 0;
  for (const p of pts) { x += p[0]; y += p[1]; }
  return { x: x / pts.length, y: y / pts.length };
}
function physicalCmp(a: ProofUnit, b: ProofUnit): number {
  const camA = String(a.cameraId ?? ''), camB = String(b.cameraId ?? '');
  if (camA !== camB) return camA.localeCompare(camB);
  const ca = polyCentroid(a.polygons), cb = polyCentroid(b.polygons);
  if (ca && cb) {
    const bandA = Math.round(ca.y / 0.25), bandB = Math.round(cb.y / 0.25);
    if (bandA !== bandB) return bandA - bandB;
    if (Math.abs(ca.x - cb.x) > 0.02) return ca.x - cb.x;
  }
  return nameCollator.compare(a.name, b.name);
}

// 4-state colour scale for the shift board, mirroring the per-table strip
// graphs (report/TableStateStrips): занят=green, не на месте=amber,
// не убран=orange, свободен=grey. Alexey asked for the same scale here (#849);
// dirty is ORANGE not red (Alexey 2026-08-24: «грязно оранжевым, чтобы не
// пугать» — red on the live shift board reads as an emergency). Orange-500 stays
// distinct from shifted's amber-500.
const TABLE_STATE_DOT: Record<string, string> = {
  occupied: STATUS_GREEN,
  // «Не на месте» — тёмно-серым, как статусы камер (Алексей 22.10: #1880 п.5 «не оранжевым»,
  // #1905 «голубой выбивается»).
  shifted: 'bg-zinc-700 dark:bg-zinc-300',
  moved: 'bg-zinc-700 dark:bg-zinc-300',
  dirty: 'bg-orange-500',
  free: 'bg-muted-foreground/40',
  // «состояние нельзя определить» (несколько столов в кадре / стол перекрыт) —
  // серый, как «нет данных»: это не состояние стола, а отсутствие вердикта.
  unknown: 'bg-slate-400',
  no_data: 'bg-muted-foreground/20',
  recheck: 'bg-slate-400',
};
const tableStateLabelKey: Record<string, string> = {
  occupied: 'report.tables.stateOccupied',
  shifted: 'report.tables.stateShifted',
  moved: 'report.tables.stateShifted',
  dirty: 'report.tables.stateDirty',
  free: 'report.tables.stateFree',
  unknown: 'report.tables.stateUnknown',
  no_data: 'dashboard.status.noData',
  recheck: 'tables.rechecking',
};
const spoiledReasonKey: Record<string, string> = {
  several_tables: 'tables.spoiledReason.several_tables',
  obstructed: 'tables.spoiledReason.obstructed',
  glare: 'tables.spoiledReason.glare',
};
// Alexey 2026-08-29: occupied tables should sort ahead of free ones on the
// live board — staff scan it to see who needs attention, not empty tables.
// Dirty units keep top priority via hasProblem/rank:0 above this group.
const TABLE_STATE_RANK: Record<string, number> = {
  occupied: 0,
  shifted: 1,
  moved: 1,
  free: 2,
  unknown: 3,
  no_data: 4,
  recheck: 3,
};

function tableProofOf(tbl: DirtyTableSample, t: ReturnType<typeof useT>['t'], unavailable = false): LiveTableProof {
  return {
    id: tbl.id, name: tbl.name,
    cropSrc: tbl.image_url ? imgSrc(tbl.image_url) : null, cropAt: tbl.captured_at,
    frameSrc: tbl.frame_image_url ? imgSrc(tbl.frame_image_url) : null, frameAt: tbl.frame_captured_at,
    polygons: tbl.polygons,
    confirmedLabel: unavailable || !tbl.state || tbl.state === 'no_data' ? t('dashboard.status.noData')
      : t(tableStateLabelKey[tbl.state]),
    // Без «· Перепроверяем» в подписи галереи: это внутренняя кухня, пользователю — только состояние
    // (Алексей 03.11 #8631).
    observedAt: tbl.state_observed_at,
  };
}

type OpenTableGallery = (tables: LiveTableProof[], index: number, mode: DisplayMode) => void;
const TableGalleryContext = React.createContext<OpenTableGallery | null>(null);

// The board can unmount a resolved section. Its open table gallery must survive.
function TableGalleryProvider({ zoneId, children }: { zoneId: string | null; children: React.ReactNode }) {
  const { t } = useT();
  const [gallery, setGallery] = useState<{ tables: LiveTableProof[]; index: number; mode: DisplayMode } | null>(null);
  const { data, error } = useSWR<TablesSummary>(
    zoneId ? ['dashboard:tables', zoneId] : null,
    async () => (await getTablesSummary(zoneId!, { limit: 0 })).data,
    DASH_SWR,
  );
  const status = tableSummaryStatus(data);
  const tables = gallery?.tables.map(previous => {
    const current = status.tables.find(table => String(table.id) === String(previous.id));
    if (current && !error) return tableProofOf(current, t, status.noFrames);
    return { ...previous, cropSrc: null, frameSrc: null, observedAt: null, pending: false,
      confirmedLabel: t(error ? 'dashboard.apiMissing' : 'dashboard.status.noData') };
  });
  return <TableGalleryContext.Provider value={(items, index, mode) => setGallery({ tables: items, index, mode })}>
    {children}
    {gallery && tables && <ImageLightbox
      liveTables={tables}
      srcs={tables.map(table => table.cropSrc)}
      frameSrcs={tables.map(table => table.frameSrc)}
      polygonsList={tables.map(table => table.polygons ?? null)}
      index={gallery.index}
      primary={gallery.mode === 'crop' ? 'crop' : 'frame'}
      holdHint={t(gallery.mode === 'crop' ? 'dashboard.proof.holdForFrame' : 'dashboard.proof.holdForCrop')}
      onClose={() => setGallery(null)}
      onNavigate={index => setGallery(previous => previous && { ...previous, index })}
    />}
  </TableGalleryContext.Provider>;
}

function TablesCleanlinessCard({ zoneId, bare, mode, problemsOnly }: { zoneId: string | null; bare?: boolean; mode?: DisplayMode; problemsOnly?: boolean }) {
  const { t } = useT();
  const { data, error, mutate } = useSWR<TablesSummary>(
    zoneId ? ['dashboard:tables', zoneId] : null,
    async () => (await getTablesSummary(zoneId!, { limit: 0 })).data,
    DASH_SWR,
  );
  const status = tableSummaryStatus(data);
  const showError = !!error;
  const loading = data === undefined && !showError;
  const allUnits: ProofUnit[] = status.tables.map(tbl => {
    // Перепроверка — внутренняя деталь (#8631): на плитке и в подсказке точки — последнее подтверждённое состояние.
    const state = status.noFrames || !tbl.state || tbl.state === 'no_data' ? 'no_data' : tbl.state;
    const dirty = state === 'dirty';
    return {
      id: tbl.id,
      name: tbl.name,
      dot: TABLE_STATE_DOT[state],
      statusLabel: t(tableStateLabelKey[state])
        + (state === 'unknown' && tbl.spoiled_reason && spoiledReasonKey[tbl.spoiled_reason]
          ? ` · ${t(spoiledReasonKey[tbl.spoiled_reason])}` : ''),
      hasProblem: dirty,
      checkPending: !!tbl.recheck_pending,
      rank: dirty ? 0 : TABLE_STATE_RANK[state],
      image_url: tbl.image_url,
      capturedAt: tbl.captured_at,
      tableProof: tableProofOf(tbl, t, status.noFrames),
      frameImageUrl: tbl.frame_image_url,
      frameCapturedAt: tbl.frame_captured_at,
      polygons: tbl.polygons,
      cameraId: tbl.camera_id,
      remindKey: `tables:${tbl.id}`,
      action: dirty
        ? <MarkDoneButton label={t('tables.markCleaned')} checkKey={`tables:${tbl.id}`} onMark={() => rescanTable(tbl.id)} onDone={() => mutate()} />
        : undefined,
    };
  });
  useReportReminders('tables', status.tables.filter(tbl => !error && !status.noFrames && tbl.state === 'dirty')
    .map(tbl => ({ key: `tables:${tbl.id}`, since: null })));
  const units = problemsOnly ? allUnits.filter(unit => unit.hasProblem) : allUnits;
  const overallLabel = showError ? t('dashboard.apiMissing')
    : status.noFrames ? t('dashboard.noFramesToday')
    : status.dirtyCount > 0 ? t('dashboard.status.tablesDirty')
    : status.unavailable ? t('dashboard.status.noData')
    : status.tables.some(tbl => tbl.state === 'unknown') ? t('report.tables.stateUnknown')
    : t('dashboard.status.tablesAllClean');
  const showDot = !!zoneId && !loading;
  return (
    <SectionCardShell
      bare={bare}
      sectionKey="tables"
      icon={UtensilsCrossed}
      title={t('dashboard.cards.tables')}
      // Тон без перепроверки (#8631): перепроверяемый стол не делает шапку серой.
      tone={showDot && !showError ? (status.dirtyCount > 0 ? 'attention'
        : status.unavailable || status.tables.some(tbl => tbl.state === 'unknown') ? 'none' : 'ok') : undefined}
      toneLabel={overallLabel}
      badge={!showError && status.dirtyCount > 0 ? <Badge variant="warning">{status.dirtyCount}</Badge> : undefined}
    >
      {!zoneId ? (
        <EmptyHint text={t('page.selectBranchFirst')} />
      ) : loading ? (
        <CardGridSkeleton mobile={bare} mode={mode} />
      ) : showError ? (
        <CardError onRetry={() => mutate()} />
      ) : status.noFrames ? (
        <EmptyHint text={t('dashboard.noFramesToday')} />
      ) : units.length > 0 ? (
        <ProofGrid units={units} openCropLabel={t('tables.openCrop')} mobile={bare} mode={mode} identityCmp={physicalCmp} />
      ) : status.unavailable ? (
        <EmptyHint text={t('dashboard.status.noData')} />
      ) : status.tables.some(tbl => tbl.state === 'unknown') && !problemsOnly ? (
        // В «только проблемы» голую подпись без фото не показываем — грязных нет, значит «в норме» (Алексей 03.11 #8626).
        <EmptyHint text={overallLabel} />
      ) : problemsOnly && allUnits.length > 0 ? (
        <AllOkBody text={t('dashboard.status.tablesAllClean')} />
      ) : null}
    </SectionCardShell>
  );
}

// «Убрали» / «Поправили» на плитке (столы и стеллажи): кнопка под палец в правом нижнем углу фото, не
// зелёная (#1941/#2053). Раньше подтверждали удержанием — теперь одним нажатием (Алексей 03.11 #8719), а от
// случайного касания — 5 с «Отменить»: отметка показана сразу, запрос уходит по истечении (или сразу, если
// плитка за это время пропала с экрана — нажатие было осознанным). Отмена на сервере не нужна: до неё
// запрос просто не отправлен.
const UNDO_MS = 5000;

// «Проверяем» после нажатия (Алексей 03.11 #8722): отметку отправили — плитка ждёт вердикта обычного
// пайплайна. «Убрали» ставит столу recheck_requested_at (серия кадров по первому кадру, где стол ещё
// похож на грязный, или два чистых кадра подряд), «Поправили» — метку в кэше до двух новых чтений
// стеллажа; пока сводка отдаёт recheck_pending / fixed_pending, на плитке «Проверяем». Нажатия — на
// этом устройстве: без нажатия перепроверку пайплайна не показываем (#8626/#8631).
const CHECK_GRACE_MS = 15_000;   // пока сводка ещё не успела вернуть pending
const CHECK_MAX_MS = 5 * 60_000; // вердикта нет дольше — снимаем, плитка показывает текущее состояние
const checkTaps = new Map<string, number>();
const checkSubs = new Set<() => void>();
function setCheckTap(key: string, at: number | null) {
  if (at == null) checkTaps.delete(key); else checkTaps.set(key, at);
  checkSubs.forEach(f => f());
}
function useCheckTap(key: string | undefined): number | null {
  return useSyncExternalStore(
    (cb) => { checkSubs.add(cb); return () => { checkSubs.delete(cb); }; },
    () => (key ? checkTaps.get(key) ?? null : null),
  );
}

function MarkDoneButton({
  label,
  onMark,
  onDone,
  checkKey,
}: {
  label: string;
  onMark: () => Promise<unknown>;
  onDone: () => void;
  /** Ключ плитки (remindKey): после отправки на ней — «Проверяем» до вердикта пайплайна. */
  checkKey?: string;
}) {
  const { t } = useT();
  const [phase, setPhase] = useState<'idle' | 'pending' | 'busy' | 'done'>('idle');
  const [left, setLeft] = useState(0);
  const [reqError, setReqError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const tick = useRef<number | null>(null);
  const pending = useRef(false);
  const markRef = useRef(onMark);
  markRef.current = onMark;
  const clear = () => {
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
    if (tick.current) { window.clearInterval(tick.current); tick.current = null; }
  };
  const keyRef = useRef(checkKey);
  keyRef.current = checkKey;
  // Плитка размонтировалась во время «Отменить» — отметку не теряем.
  useEffect(() => () => {
    clear();
    if (pending.current) {
      if (keyRef.current) setCheckTap(keyRef.current, Date.now());
      void markRef.current().catch(() => { if (keyRef.current) setCheckTap(keyRef.current, null); });
    }
  }, []);

  async function send() {
    clear();
    pending.current = false;
    setPhase('busy');
    setReqError(null);
    try {
      await markRef.current();
      if (keyRef.current) setCheckTap(keyRef.current, Date.now());
      setPhase('done');
      window.setTimeout(() => { setPhase('idle'); onDone(); }, 700);
    } catch (_e) {
      setReqError(t('common.error'));
      setPhase('idle');
    }
  }
  const tap = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (phase !== 'idle') return;
    pending.current = true;
    setPhase('pending');
    setReqError(null);
    const until = Date.now() + UNDO_MS;
    setLeft(Math.ceil(UNDO_MS / 1000));
    tick.current = window.setInterval(() => setLeft(Math.max(0, Math.ceil((until - Date.now()) / 1000))), 250);
    timer.current = window.setTimeout(() => { void send(); }, UNDO_MS);
  };
  const undo = (e: React.MouseEvent) => {
    e.stopPropagation();
    clear();
    pending.current = false;
    setPhase('idle');
  };
  const marked = phase !== 'idle';
  return (
    <>
      <Tooltip content={label}><button
        type="button"
        onClick={tap}
        onPointerDown={(e) => e.stopPropagation()}
        disabled={marked}
        aria-label={label}
        aria-pressed={marked}
        // Пустая — светлый кружок с бледной галкой (#2098–#2133: «ещё не отмечено», не зелёная); отмечено —
        // зелёный кружок с белой галкой. Зона нажатия — с запасом через before:-inset-2.
        className={cn("group/done absolute bottom-2.5 right-2.5 z-20 grid size-10 touch-manipulation select-none place-items-center rounded-full shadow-md backdrop-blur transition-colors before:absolute before:-inset-2 before:content-[''] active:scale-90",
          marked ? 'bg-success text-white' : 'bg-background/80 text-foreground')}
      >
        <svg viewBox="0 0 24 24" className="size-7" aria-hidden>
          <path d="M5.5 12.6l4.1 4.1L18.5 7.8" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"
            className={marked ? (phase === 'busy' ? 'animate-pulse' : '') : 'opacity-20 transition-opacity group-hover/done:opacity-45'} />
        </svg>
      </button></Tooltip>
      {phase === 'pending' && (
        <button
          type="button"
          onClick={undo}
          onPointerDown={(e) => e.stopPropagation()}
          className="absolute bottom-3.5 right-14 z-20 flex items-center gap-1 whitespace-nowrap rounded-full bg-background/90 px-2.5 py-1 text-xs font-medium text-foreground shadow-md backdrop-blur animate-in fade-in duration-150 hover:bg-background"
        >
          {t('dashboard.markUndo')}<span className="tabular-nums text-muted-foreground">{left}</span>
        </button>
      )}
      {reqError && <div className="absolute bottom-14 right-1.5 z-20 rounded bg-background/90 px-1.5 py-0.5 text-xs text-destructive">{reqError}</div>}
    </>
  );
}

// Колонка плашек разделов «в норме» вдоль правого края (#1899/#1911/#1912): при «всё выполнено»
// рядом с роботом, при проблемах (#1941) — рядом с доской проблемных разделов. Раздел без кадров
// за сегодня — серая иконка: он не «в норме», а слепой.
// compact (#2053): когда место нужно проблемным разделам — только иконка с бейджем-галочкой
// у правого края, подпись — в подсказке. Переход compact ↔ полные плашки — анимацией ширины
// (#2061: «выезд с анимацией, не скачком»).
// Средний чек сегодня (Алексей 23.10 #2143): плашка среди «в норме», если подключены чеки; при
// падении против обычного (тот же день недели за 4 прошлые недели в те же часы — #2374: выходные
// с буднями не сравниваем) — жёлтая/оранжевая/красная, но остаётся
// плашкой справа, доску не раздувает.
const AvgCheckContext = React.createContext<ReceiptsTodayDto | null>(null);
// «среднее прошлых вторников» под риской полосы (Алексей 03.11 #8717).
const WEEKDAY_PAST_RU = ['воскресений', 'понедельников', 'вторников', 'сред', 'четвергов', 'пятниц', 'суббот'];
const WEEKDAY_PAST_EN = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];
const AVG_TONE: Record<string, { ring: string; bg: string; fg: string; dot: string }> = {
  attention: { ring: 'border-amber-400', bg: 'bg-amber-400/15', fg: 'text-amber-600 dark:text-amber-300', dot: 'bg-amber-400' },
  warning: { ring: 'border-orange-500', bg: 'bg-orange-500/15', fg: 'text-orange-600 dark:text-orange-300', dot: 'bg-orange-500' },
  critical: { ring: 'border-red-600', bg: 'bg-red-600/15', fg: 'text-red-600 dark:text-red-400', dot: 'bg-red-600' },
};

function AvgCheckPill({ data, compact, className }: { data: ReceiptsTodayDto; compact: boolean; className?: string }) {
  const { t, lang } = useT();
  const dense = useCompactDash();
  const avg = data.avg_check ?? 0;
  const ch = data.change;
  const tone = AVG_TONE[data.tone ?? ''];
  const rub = (v: number) => money(`${Math.round(v).toLocaleString(lang === 'en' ? 'en-US' : 'ru-RU')} ₽`);
  const pct = ch == null ? '' : `${ch > 0 ? '+' : ch < 0 ? '−' : ''}${Math.abs(Math.round(ch * 100))} %`;
  // «−0 %» читается как просадка — при округлении в ноль пишем «так же, как прошлые вт» (Алексей 27.10 #2960).
  const same = ch != null && Math.round(ch * 100) === 0;
  // Подсказка — одна короткая строка (#2514).
  const tip = data.baseline_avg_check
    ? (lang === 'en' ? `Usually by now: ${rub(data.baseline_avg_check)}` : `Обычно к этому часу: ${rub(data.baseline_avg_check)}`)
    : (lang === 'en' ? `${data.count ?? 0} checks today` : `Чеков сегодня: ${data.count ?? 0}`);
  const body = data.baseline_avg_check ? (
    // Сумма, полоса и сравнение — одним элементом (Алексей 03.11 #8694): «+6 %» — плашкой на конце
    // заливки, под риской середины — с чем сравниваем («прошлые вт»).
    <div className="flex items-center gap-2">
      <span className="shrink-0 text-sm font-semibold tabular-nums">{rub(avg)}</span>
      <PaceBar
        ratio={avg / data.baseline_avg_check}
        delta={pct ? (same ? '±0 %' : pct) : ''}
        deltaClass={tone ? tone.dot : same || ch == null ? 'bg-muted-foreground/70' : ch > 0 ? 'bg-success' : 'bg-red-500'}
        baseLabel={t('dashboard.avgCheck.baseline', { day: (lang === 'en' ? WEEKDAY_PAST_EN : WEEKDAY_PAST_RU)[new Date().getDay()] })}
      />
    </div>
  ) : (
    <span className="text-sm font-semibold tabular-nums">{rub(avg)}</span>
  );
  if (!compact) {
    return (
      <Tooltip content={tip} side="left">
        <div aria-label={tip} className={cn(DASH_CARD, tone?.ring, 'flex w-full min-w-0 flex-col transition-all duration-500 ease-out', dashCardBody(dense), className)}>
          <CardHead icon={Receipt} title={t('dashboard.avgCheck.title')} dense={dense} tint={tone ? cn(tone.bg, tone.fg) : undefined} />
          {body}
        </div>
      </Tooltip>
    );
  }
  // Свёрнутая колонка — кружок со значком; overflow-hidden здесь нет: он срезал значок-галку (#8692).
  return (
    <Tooltip content={tip} side="left">
      <div aria-label={tip} className={cn('relative flex size-12 items-center rounded-[1.5rem] border bg-card px-[5px] shadow-sm', tone?.ring, className)}>
        <span className={cn('relative grid size-9 shrink-0 place-items-center rounded-full', tone ? tone.bg : 'bg-primary/10')}>
          <Receipt className={cn('size-4.5', tone ? tone.fg : 'text-primary')} />
          <span className={cn('absolute -bottom-1 -right-1 grid size-4.5 place-items-center rounded-full ring-2 ring-card', tone ? tone.dot : 'bg-success')}>
            {tone ? <span className="text-[9px] font-bold leading-none text-white">!</span> : <Check className="size-2.5 text-white" strokeWidth={4} />}
          </span>
        </span>
      </div>
    </Tooltip>
  );
}

// Темп к обычному (Алексей 25.10 #2456): ±3 % — ровно, нейтрально; впереди — зелёный; отстаём — спокойный
// голубой, без тревоги («должно выглядеть хорошо и когда не хватает, и когда побили»).
type Pace = 'behind' | 'even' | 'ahead';
const paceOf = (ratio: number): Pace => (ratio > 1.03 ? 'ahead' : ratio < 0.97 ? 'behind' : 'even');
const PACE_FILL: Record<Pace, string> = { behind: 'bg-primary/60', even: 'bg-primary', ahead: 'bg-success' };

/** Полоса среднего чека: шкала −25 %…+25 % к обычному, отметка «обычно» посередине (Алексей 25.10 #2480:
 *  от нуля не смотрится). Заливка всегда слева — «как обычно» = полполосы, чтобы было чувство достижения,
 *  а не пустая полоса из центра (Алексей 27.10 #2960). */
function PaceBar({ ratio, delta, deltaClass, baseLabel }: { ratio: number; delta: string; deltaClass: string; baseLabel: string }) {
  const pace = paceOf(ratio);
  const w = Math.max(4, Math.min(100, ((ratio - 0.75) / 0.5) * 100));
  // Плашка «+6 %» — по центру на конце заливки, но не вылезает за края полосы.
  const chipAt = Math.max(12, Math.min(88, w));
  return (
    <div className="relative min-w-0 flex-1 pb-2.5">
      <div className="relative h-4">
        <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-muted">
          <div className={cn('absolute left-0 top-0 h-full rounded-full transition-all duration-700', PACE_FILL[pace])}
            style={{ width: `${w}%` }} />
          <div className="absolute -top-0.5 h-2.5 w-0.5 -translate-x-1/2 rounded-full bg-foreground/40" style={{ left: '50%' }} />
        </div>
        {delta && (
          <span className={cn('absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full px-1.5 text-[10px] font-semibold leading-4 tabular-nums text-white shadow-sm ring-2 ring-card transition-all duration-700', deltaClass)}
            style={{ left: `${chipAt}%` }}>
            {delta}
          </span>
        )}
      </div>
      {/* Под риской середины, во всю ширину полосы (по центру = под риской), не шире её. */}
      <span className="absolute inset-x-0 bottom-0 truncate text-center text-[9px] leading-none text-muted-foreground">
        {baseLabel}
      </span>
    </div>
  );
}

// Подсказка под средним чеком (#2383/#2384 «лайт фича с лампочкой», #2385 «идея для повышения», #2456): две
// ходовые позиции этих часов. Компактно и без шифра (Алексей 26.10 #2607 «3 из 4 непонятно, место
// расходуется»): строка = название + «сегодня N, обычно M» словами; цифра «сегодня» — в цвет темпа.
const PACE_TEXT: Record<Pace, string> = { behind: 'text-primary', even: 'text-foreground', ahead: 'text-success' };

// Компактно (Алексей 03.11 #8615: «"Обычно сейчас берут" блок оч высокий»): лампочка — в строке
// заголовка, а не отдельной колонкой слева, поэтому названия получают всю ширину и реже переносятся;
// отступы между позициями — минимальные. `limit` — сколько позиций показать, когда колонка не влезает.
function UpsellTipsCard({ tips, compact, limit, className }: {
  tips: { name: string; share: number; today: number; usual: number }[];
  compact: boolean;
  limit?: number;
  className?: string;
}) {
  const { t, lang } = useT();
  const dense = useCompactDash();
  const tipText = lang === 'en' ? 'Sold today vs usually by this hour' : 'Сколько продали сегодня и сколько обычно к этому часу';
  const shown = limit != null ? tips.slice(0, limit) : tips;
  if (compact) {
    return (
      <Tooltip content={tipText} side="left">
        <div aria-label={tipText} className={cn('relative flex size-12 items-center rounded-[1.5rem] border bg-card px-[5px] shadow-sm', className)}>
          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-amber-400/15">
            <Lightbulb className="size-4.5 text-amber-500" />
          </span>
        </div>
      </Tooltip>
    );
  }
  return (
    <Tooltip content={tipText} side="left">
      <div aria-label={tipText} className={cn(DASH_CARD, 'flex w-full min-w-0 flex-col transition-all duration-500 ease-out', dashCardBody(dense), className)}>
        <CardHead icon={Lightbulb} title={t('dashboard.upsell.title')} dense={dense} tint="bg-amber-400/15 text-amber-500" />
        {/* Тело — компактное, как было (#8665/#8701): название до двух строк, под ним «сегодня · обычно». */}
        <div className="flex min-w-0 flex-col divide-y divide-border/60">
          {shown.map((tip) => {
            const today = Math.round(tip.today);
            const usual = Math.max(1, Math.round(tip.usual));
            const pace = paceOf(today / usual);
            return (
              <div key={tip.name} className="py-0.5 first:pt-0 last:pb-0">
                <div className="line-clamp-2 text-[13px] font-semibold leading-tight">{tip.name}</div>
                <div className="truncate text-[11px] leading-snug text-muted-foreground">
                  {lang === 'en' ? 'today' : 'сегодня'} <span className={cn('font-semibold tabular-nums', PACE_TEXT[pace])}>{today}</span>
                  {' · '}{lang === 'en' ? 'usually' : 'обычно'} <span className="tabular-nums">{usual}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Tooltip>
  );
}

// Колонка справа (средний чек, подсказка, разделы «в норме») ОБЯЗАНА влезать по высоте при любом
// размере экрана и масштабе (Алексей 03.11 #8615: «карточки вылезают за пределы экрана… мб иконки в
// грид можно сворачивать»). Не влезает — сворачиваем по шагам, пока не влезет: 1) плашки разделов —
// в ряд иконок; 2) у подсказки одна позиция; 3) подсказка уходит. Средний чек остаётся всегда.
// row — портретный планшет: та же колонка, но полосой под доской (колонка в 256 px отнимала у доски
// треть ширины).
const FOLD_MAX = 3;
function OkSectionPills({ sections, noFrames, compact = false, row = false, className, tasks }: {
  sections: DashSection[];
  noFrames: Record<string, boolean>;
  compact?: boolean;
  row?: boolean;
  className?: string;
  /** Телефон боком: список задач под средним чеком и подсказкой (#8665) — ужимается первым. */
  tasks?: React.ReactNode;
}) {
  const { t } = useT();
  const avgCheck = useContext(AvgCheckContext);
  const dense = useCompactDash();
  const boxRef = useRef<HTMLDivElement>(null);
  const [fold, setFold] = useState(0);
  const [boxSize, setBoxSize] = useState('');
  // Размер колонки поменялся (поворот, масштаб, окно) — пробуем снова без сворачивания.
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBoxSize(`${el.clientWidth}x${el.clientHeight}`));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useLayoutEffect(() => { setFold(0); }, [boxSize, sections.length, compact, row]);
  // После каждой отрисовки: не влезло — ещё шаг. Сходится не больше чем за FOLD_MAX проходов, до отрисовки.
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el || fold >= FOLD_MAX) return;
    if (el.scrollHeight > el.clientHeight + 1) setFold(f => Math.min(FOLD_MAX, f + 1));
  });
  const showAvg = !!avgCheck?.connected && avgCheck.avg_check != null;
  const tips = avgCheck?.connected ? avgCheck.tips ?? [] : [];
  const iconGrid = compact || fold >= 1;
  const rowItem = row ? 'w-auto min-w-[14rem] flex-1' : undefined;
  return (
    <div ref={boxRef} className={cn(
      'flex min-h-0 shrink-0 transition-[width] duration-500 ease-out',
      row ? 'w-full flex-row flex-wrap items-stretch justify-center gap-2'
        // Дети не сжимаются (shrink-0): иначе подсказка молча обрезалась, а не сворачивалась по шагам.
        // Сжимается только список задач (у него свои страницы).
        : cn('flex-col items-end justify-center-safe self-stretch', dense ? 'gap-1.5' : 'gap-2.5', compact ? 'w-12' : 'w-64 max-w-[40%]'),
      className,
    )}>
      {/* Средний чек — первым в колонке (#2386 «на самый верх списка»), под ним — подсказка. */}
      {showAvg && <AvgCheckPill data={avgCheck!} compact={compact} className={cn('shrink-0', rowItem)} />}
      {tips.length > 0 && fold < 3 && (
        <UpsellTipsCard tips={tips} compact={compact} limit={fold >= 2 ? 1 : undefined} className={cn('shrink-0', rowItem)} />
      )}
      {tasks && <div className="flex min-h-[4.5rem] w-full flex-col">{tasks}</div>}
      {sections.length > 0 && (
        <div className={cn('flex shrink-0 gap-2.5', row ? 'contents' : iconGrid ? 'flex-row flex-wrap justify-end gap-1.5' : 'w-full flex-col')}>
          {sections.map(s => {
            const meta = SECTION_META[s.key];
            const Icon = meta?.icon ?? CheckCircle2;
            const blind = !!noFrames[s.key];
            const title = meta ? t(meta.title) : s.key;
            const icon = iconGrid && !row;
            return (
              <Tooltip key={s.key} content={`${title} · ${s.okLabel}`} side="left">
                <div
                  aria-label={`${title}: ${s.okLabel}`}
                  className={cn(
                    // Без overflow-hidden: срезал галку «в норме» скруглением плашки (#8692), текст прячет свой контейнер.
                    'relative flex h-12 min-w-0 items-center gap-3 border bg-card shadow-sm transition-all duration-500 ease-out',
                    // «В норме»: приглушена только зелёная галка, плашка ниже — 44 px (Алексей 28.10
                    // #3112/#3119: «приглушить только зелёную галку», не всю плашку). Иконка, её кружок и
                    // тень — в полную силу, как у среднего чека (Алексей 03.11 #8615: «иконки всё ещё блеклые»).
                    // Свёрнуто из-за высоты — без анимации ширины: пока плашки ужимались, ряд иконок
                    // переносился по одной в строку, и колонка сворачивалась лишние шаги.
                    icon ? cn('w-12 shrink-0 rounded-[1.5rem] px-[5px]', !compact && 'transition-none') : row ? 'h-11 min-w-[11rem] flex-1 rounded-xl px-3' : 'h-11 w-full rounded-xl px-3',
                  )}
                >
                  {/* Фон иконки — голубой в тон иконке, не зелёный (#1910: «грязно-зелёный бг»). */}
                  <span className={cn('relative grid size-9 shrink-0 place-items-center rounded-full', blind ? 'bg-muted' : 'bg-primary/10')}>
                    <Icon className={cn('size-4.5', blind ? 'text-muted-foreground' : 'text-primary')} />
                    <span className={cn('absolute -bottom-1 -right-1 grid size-4 place-items-center rounded-full ring-2 ring-card transition-opacity duration-300',
                      blind ? 'bg-muted-foreground' : 'bg-success/45')}>
                      {blind ? <ImageOff className="size-2.5 text-white" /> : <Check className="size-2.5 text-white" strokeWidth={4} />}
                    </span>
                  </span>
                  <div className={cn('min-w-0 flex-1 overflow-hidden whitespace-nowrap transition-opacity', icon ? 'opacity-0 duration-150' : 'opacity-100 delay-200 duration-300')}>
                    {/* «в норме» — галочкой на иконке, без подписи (Алексей 25.10 #2484); «нет кадров» — подписью. */}
                    <div className="truncate text-sm font-semibold">{title}</div>
                    {blind && <div className="truncate text-xs text-muted-foreground">{s.okLabel}</div>}
                  </div>
                </div>
              </Tooltip>
            );
          })}
        </div>
      )}
    </div>
  );
}

// «Только проблемы» с проблемами (#1941/#2053/#2061): доска проблемных разделов + плашки «в норме»
// справа. Выезд плашек с подписями отнимает у доски ~208 px, панели сжимаются пропорционально.
// Каждая панель пробует свою раскладку на той ширине и отвечает, во сколько раз изменятся
// плитки. Выезжаем, если ни одна плитка не уменьшится больше чем на 15 %; убираем, если
// свернувшись, плитки выросли бы больше чем на ~21 % (зазор — чтобы не дёргалось).
// Колонка раскрыта — w-64, но не шире 40 % (узкий экран / крупный масштаб, #8615); свёрнута — w-12.
const PILLS_W = 256, PILLS_MAX_SHARE = 0.4, PILLS_COMPACT_W = 48;
const PILLS_MAX_SHRINK = 0.85;
function ProblemsBoard({ boardSections, sidePills, weightOf, noFrames, row = false, tasks }: {
  boardSections: DashSection[];
  sidePills: DashSection[];
  weightOf: (key: string) => number;
  noFrames: Record<string, boolean>;
  /** Портретный планшет: плашки полосой под доской, без проб ширины. */
  row?: boolean;
  tasks?: React.ReactNode;
}) {
  const { ref, width } = useMeasuredBox<HTMLDivElement>(150);
  const dense = useCompactDash();
  // Ответы проб с пометкой k: после переключения старые ответы (для другого k) не считаются.
  const [slack, setSlack] = useState<Record<string, { k: number; scale: number }>>({});
  const onSlack = useCallback((key: string, scale: number | null, k: number) => {
    setSlack(m => {
      if (scale == null) { if (m[key]?.k !== k) return m; const n = { ...m }; delete n[key]; return n; }
      return m[key]?.k === k && m[key].scale === scale ? m : { ...m, [key]: { k, scale } };
    });
  }, []);
  const [autoExpanded, setExpanded] = useState(false);
  // Средний чек и «Обычно сейчас берут» в колонке — она всегда раскрыта (Алексей 25.10 #2437: «это важно»):
  // иконкой их не прочитать. Без них — как раньше, по месту для доски.
  const avgCheck = useContext(AvgCheckContext);
  const pinned = !!avgCheck?.connected && (avgCheck.avg_check != null || !!avgCheck.tips?.length);
  const expanded = row || pinned || !!tasks || autoExpanded;
  const pillsW = Math.min(PILLS_W, width * PILLS_MAX_SHARE);
  const extra = Math.max(0, pillsW - PILLS_COMPACT_W);
  const boardW = Math.max(1, width - (expanded ? pillsW : PILLS_COMPACT_W) - (dense ? 6 : 12));
  // Компактно: «станет уже» (k < 1); выехали: «станет шире» (k > 1).
  const probeK = width > 0 && !row
    ? Math.round((expanded ? (boardW + extra) / boardW : Math.max(0.3, (boardW - extra) / boardW)) * 100) / 100
    : undefined;
  const scales = Object.values(slack).filter(v => v.k === probeK).map(v => v.scale);
  // Загрузка (#2133 «карточки справа колбасит, то выезжают, то прячутся»): разделы и фото
  // приходят по очереди, каждый ответ пробы двигал решение. Решаем, только когда ответили ВСЕ
  // панели, и решение должно продержаться 1,5 с; после переключения — 4 с без новых.
  const want = scales.length < boardSections.length ? null
    : !expanded ? Math.min(...scales) >= PILLS_MAX_SHRINK
      : !(Math.max(...scales) > 1 / PILLS_MAX_SHRINK + 0.03);
  const lockUntil = useRef(0);
  useEffect(() => {
    if (row || pinned || want == null || want === expanded) return;
    const wait = Math.max(1500, lockUntil.current - performance.now());
    const id = window.setTimeout(() => { lockUntil.current = performance.now() + 4000; setExpanded(want); }, wait);
    return () => window.clearTimeout(id);
  }, [want, expanded, pinned]);
  return (
    <div ref={ref} className={cn('flex min-h-0 flex-1', dense ? 'gap-1.5' : 'gap-3', row && 'flex-col')}>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <DesktopBoard sections={boardSections} weightOf={weightOf} problemsOnly onSlack={onSlack} probeK={probeK} />
      </div>
      <OkSectionPills sections={sidePills} noFrames={noFrames} compact={!expanded && !tasks} row={row} tasks={tasks} />
    </div>
  );
}

// ── План продаж (Алексей 22.10, #1942): без чеков — утренний чеклист. Главное — что
// предлагаем сейчас (крупно), ниже напоминания, «дальше» и цели дня строками.
// hero (#2053): при «всё выполнено» план — главное на экране, шрифт крупнее, без пустого поля.
export function SalesPlanCard({ data, bare, hero }: { data: SalesPlanTodayDto; bare?: boolean; hero?: boolean }) {
  const { t } = useT();
  const empty = data.offers_now.length === 0;
  return (
    <SectionCardShell bare={bare} sectionKey="sales_plan" icon={TrendingUp} title={t('dashboard.cards.salesPlan')}>
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
        <div className={cn('rounded-xl bg-primary/10', hero ? 'px-5 py-4' : 'px-3 py-2.5')}>
          <div className={cn('font-medium uppercase tracking-wide text-primary', hero ? 'text-sm' : 'text-xs')}>{t('dashboard.salesPlan.now')}</div>
          {empty ? (
            <div className="mt-1 text-sm text-muted-foreground">{t('dashboard.salesPlan.nothingNow')}</div>
          ) : (
            <ul className="mt-1 flex flex-col gap-1">
              {data.offers_now.map(r => (
                <li key={r.id} className="flex items-baseline justify-between gap-2">
                  <span className={cn('font-semibold leading-tight', hero ? 'text-3xl md:text-4xl' : 'text-lg md:text-xl')}>{r.title}</span>
                  {r.window && <span className={cn('shrink-0 text-muted-foreground', hero ? 'text-base' : 'text-xs')}>{r.window}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
        {data.reminders.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {data.reminders.map(r => (
              <span key={r.id} className={cn('inline-flex items-center gap-1 rounded-full border px-2.5 py-1', hero ? 'text-lg' : 'text-sm')}>
                <Bell className="size-3.5 text-primary" />
                {r.title}
                {r.window && <span className="text-xs text-muted-foreground">{r.window}</span>}
              </span>
            ))}
          </div>
        )}
        {data.offers_later.length > 0 && (
          <div className={hero ? 'text-lg' : 'text-sm'}>
            <span className="text-muted-foreground">{t('dashboard.salesPlan.later')}: </span>
            {data.offers_later.map((r, i) => (
              <span key={r.id}>{i > 0 && ', '}{r.title}{r.window && <span className="text-xs text-muted-foreground"> {r.window}</span>}</span>
            ))}
          </div>
        )}
        {data.targets.length > 0 && (
          <div className="flex flex-col gap-1">
            <div className="text-xs font-medium text-muted-foreground">{t('dashboard.salesPlan.targets')}</div>
            {data.targets.map(r => (
              <div key={r.id} className={cn('flex items-center justify-between gap-2 rounded-lg border', hero ? 'px-4 py-2.5 text-lg' : 'px-2.5 py-1.5 text-sm')}>
                <span className="flex min-w-0 items-center gap-2"><Circle className="size-3.5 shrink-0 text-muted-foreground" /><span className="truncate">{r.title}</span></span>
                {r.target_qty != null && <span className="shrink-0 font-semibold tabular-nums">{r.target_qty} {t('dashboard.salesPlan.pcs')}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </SectionCardShell>
  );
}

// ── Card 3: Layout summary ──────────────────────────────────────────────────

// ── Shared "proof grid" — paginated, occupancy-sorted, problem-aware list of
// monitored units (racks → Выкладка, tables → Чистота столов). Reuses the
// existing ImageLightbox as a cross-unit gallery (prev/next arrows on desktop,
// tap on mobile) with the unit name + status kept under the image.

// Occupancy rank: LEAST occupied (emptiest = worst) first, so problem units
// float to the top of the sort. Mirrors the dashboard's "problems first" intent.
const RACK_OCCUPANCY_RANK: Record<RackSample['status'], number> = {
  grey: 0, // почти пустая — worst
  orange: 1,
  yellow: 2,
  green: 3,
  unknown: 4,
};

interface ProofUnit {
  tableProof?: LiveTableProof;
  id: string | number;
  /** Ключ плитки для напоминаний робота (AI447): «раздел:id». */
  remindKey?: string;
  name: string;
  subtitle?: string | null;
  /** Colour key for the status dot. */
  dot: string;
  statusLabel: string;
  /** Status text without the "· с HH:MM" suffix — used by the flip flash,
   *  where "just went empty" + a since-time from hours ago reads as nonsense. */
  statusShort?: string;
  hasProblem: boolean;
  /** Sort key: least occupancy (worst) first. */
  rank: number;
  image_url: string | null;
  /** ISO timestamp of the CROP (may lag — debounced/stale is acceptable). */
  capturedAt?: string | null;
  /** F5: full source frame (not the crop) + target outline(s) in 0..1 coords,
   *  for the optional full-frame view. null/absent → the crop is used. */
  frameImageUrl?: string | null;
  /** ISO timestamp of the FULL frame (the camera's freshest) — shown instead of
   *  capturedAt when the tile is displaying the full frame. */
  frameCapturedAt?: string | null;
  polygons?: number[][][] | null;
  /** Camera the unit's polygon lives on — physical-cluster ordering key. */
  cameraId?: number | string | null;
  /** Пайплайн ещё не вынес вердикт после отметки человека (столы — recheck_pending, стеллажи —
   *  fixed_pending). Сам по себе ничего не показывает: «Проверяем» — только после нажатия (#8722). */
  checkPending?: boolean;
  /** Optional inline action rendered under the photo (e.g. "mark cleaned"). */
  action?: React.ReactNode;
  /** Выкладка: полки — линии по низу и мини-шкала «сколько / норма» слева от фото (#2088). */
  shelves?: ShelfMark[] | null;
}

// Причины статуса выкладки на фото (Алексей 23.10, #2088): слева от фото — тонкая горизонтальная
// линия по низу каждой полки и над ней мини-шкала: заливка — заполненность, риска — порог.
// Полка ниже порога — янтарная. Минимум места и внимания; подробности — в подсказке.
// Координаты: полный кадр — как есть; кроп стеллажа — обратная перспектива четырёхугольника
// стеллажа (тот же порядок вершин, что у кропа: TL, TR, BR, BL), иначе — по рамке.
function quadInverse(q: number[][]): ((x: number, y: number) => [number, number]) | null {
  if (q.length !== 4) return null;
  const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = q;
  const sx = x0 - x1 + x2 - x3, sy = y0 - y1 + y2 - y3;
  let a: number, b: number, c: number, d: number, e: number, f: number, g: number, h: number;
  if (Math.abs(sx) < 1e-9 && Math.abs(sy) < 1e-9) {
    a = x1 - x0; b = x3 - x0; c = x0; d = y1 - y0; e = y3 - y0; f = y0; g = 0; h = 0;
  } else {
    const dx1 = x1 - x2, dx2 = x3 - x2, dy1 = y1 - y2, dy2 = y3 - y2;
    const den = dx1 * dy2 - dx2 * dy1;
    if (Math.abs(den) < 1e-12) return null;
    g = (sx * dy2 - dx2 * sy) / den; h = (dx1 * sy - sx * dy1) / den;
    a = x1 - x0 + g * x1; b = x3 - x0 + h * x3; c = x0;
    d = y1 - y0 + g * y1; e = y3 - y0 + h * y3; f = y0;
  }
  // Обратная матрица 3×3 [[a b c] [d e f] [g h 1]].
  const A = e - f * h, B = f * g - d, C = d * h - e * g;
  const det = a * A + b * B + c * C;
  if (Math.abs(det) < 1e-12) return null;
  const i00 = A / det, i01 = (c * h - b) / det, i02 = (b * f - c * e) / det;
  const i10 = B / det, i11 = (a - c * g) / det, i12 = (c * d - a * f) / det;
  const i20 = C / det, i21 = (b * g - a * h) / det, i22 = (a * e - b * d) / det;
  return (x, y) => {
    const w = i20 * x + i21 * y + i22;
    return [(i00 * x + i01 * y + i02) / w, (i10 * x + i11 * y + i12) / w];
  };
}

// Маленькая плитка (Десерты на Ратуше, Алексей 28.10 #3133: «фото такое маленькое, что кнопки не
// влезают… полно места по ширине — индикаторы за фото вынести»): шкалы полок — колонкой слева от
// плитки, сверху вниз как полки, горизонтальными капсулами с риской нормы.
const SMALL_TILE_H = 150;
const ASIDE_W = 64;
/** Строка шкалы сбоку: капсула 10 px + зазор 4 px. Не помещаются все полки по высоте плитки — не выносим. */
const ASIDE_ROW_H = 14;
function ShelfMarksAside({ shelves }: { shelves: ShelfMark[] }) {
  const low = (s: ShelfMark) => Math.max(...(s.points?.length ? s.points : s.bottom).map(p => p[1]), 0);
  const rows = [...shelves].sort((a, b) => low(a) - low(b));
  return (
    <span className="pointer-events-none absolute bottom-0 flex flex-col justify-end gap-1" style={{ right: '100%', marginRight: 6, width: ASIDE_W - 6 }} aria-hidden>
      {rows.map(s => {
        const fill = Math.max(0, Math.min(1, s.pct));
        const thr = Math.max(0, Math.min(1, s.thr));
        return (
          <Tooltip key={s.name} content={`${s.name}: ${Math.round(s.pct * 100)} % · норма ${Math.round(s.thr * 100)} %`}><span className="flex items-center gap-1">
            <span className="relative h-2.5 flex-1 overflow-hidden rounded-full bg-foreground/10">
              <span className={cn('absolute inset-y-0 left-0 rounded-full', s.low ? 'bg-amber-400' : 'bg-success/80')} style={{ width: `${fill * 100}%` }} />
              <span className="absolute inset-y-0 w-px bg-foreground/70" style={{ left: `${thr * 100}%` }} />
            </span>
            <span className={cn('w-7 text-right text-[10px] tabular-nums', s.low ? 'font-medium text-foreground' : 'text-muted-foreground')}>{Math.round(s.pct * 100)}%</span>
          </span></Tooltip>
        );
      })}
    </span>
  );
}

function ShelfMarks({ shelves, polygons, full, labels = true, captionBelow = true }: {
  shelves: ShelfMark[]; polygons?: number[][][] | null; full: boolean; labels?: boolean;
  /** Внизу плитки есть подпись стеллажа — шкалы до неё не доходят. Нет подписи (маленький экран) — до низа
   *  плитки (Алексей 03.11 #8690: «шкалы сжаты, будто подпись ещё есть»). */
  captionBelow?: boolean;
}) {
  // Запас снизу под подпись стеллажа (px): 28 — под плашку с названием, 4 — просто край плитки.
  const capH = captionBelow ? 28 : 4;
  let map: (x: number, y: number) => [number, number] = (x, y) => [x, y];
  if (!full) {
    const q = polygons?.length === 1 && polygons[0].length === 4 ? quadInverse(polygons[0]) : null;
    const b = polygons?.length ? polysBBox(polygons) : null;
    if (q) map = q;
    else if (b) map = (x, y) => [(x - b.x0) / (b.x1 - b.x0), (y - b.y0) / (b.y1 - b.y0)];
    else return null;
  }
  // Шкалы по полкам (#2151 → #2156 «непонятно, что уровень, серым страшновато» → #2167 «шкалы были
  // лучше, если проработать стиль»): у каждой полки — капсула-индикатор у левого края фото на
  // подложке как у остальных плашек; заливка снизу (зелёная — в норме, янтарная — ниже), норма —
  // стрелка-указатель справа (#2282). У полки ниже нормы — плашка «48 %» и линия по её низу.
  const rows = shelves.map(s => {
    // Низ полки — две нижние вершины в координатах плитки (bottom_points_indices бывают не низом, #3188).
    const pts = s.points?.length && s.points.length >= 3
      ? s.points.map(([x, y]) => map(x, y)).sort((a, b) => b[1] - a[1]).slice(0, 2)
      : s.bottom.map(([x, y]) => map(x, y));
    if (pts.length < 2) return null;
    const [u, v] = pts[0][0] <= pts[1][0] ? pts[0] : pts[1];
    return v > 0.02 && v < 1.02 ? { s, u, v: Math.min(0.99, v) } : null;
  }).filter((r): r is { s: ShelfMark; u: number; v: number } => !!r).sort((a, b) => a.v - b.v);
  if (!rows.length) return null;
  const gaps = rows.slice(1).map((r, i) => r.v - rows[i].v);
  const typical = gaps.length ? gaps.reduce((a, b) => a + b, 0) / gaps.length : 0.3;
  return (
    <span className="pointer-events-none absolute inset-0 z-[5]" aria-hidden>
      {rows.map((r, i) => {
        const top = i ? rows[i - 1].v + 0.015 : Math.max(0.02, r.v - typical);
        const h = Math.max(0.04, r.v - top - 0.005);
        const fill = Math.max(0, Math.min(1, r.s.pct));
        const thr = Math.max(0, Math.min(1, r.s.thr));
        return (
          <Tooltip key={r.s.name} content={`${r.s.name}: ${Math.round(r.s.pct * 100)} % · норма ${Math.round(r.s.thr * 100)} %`}><span>
            <span className="absolute left-1.5 w-3 rounded-full bg-background/80 p-[3px] shadow-sm backdrop-blur"
              // Шкала кончается над подписью стеллажа внизу кадра (26 px), а не заходит на неё на мелких
              // плитках (Алексей 28.10 #3120: «шкала всё ещё залазит за надпись „Выпечка“»).
              style={{ top: `min(${top * 100}%, calc(100% - ${capH + 10}px))`, height: `max(8px, min(${h * 100}%, calc(100% - ${capH}px - ${top * 100}%)))` }}>
              <span className="relative block size-full overflow-hidden rounded-full bg-foreground/10">
                <span className={cn('absolute inset-x-0 bottom-0 rounded-full', r.s.low ? 'bg-amber-400' : 'bg-success/80')} style={{ height: `${fill * 100}%` }} />
              </span>
              {/* Норма — стрелка-указатель справа и тонкая светлая риска внутри капсулы, как на
                  приборной шкале (Алексей 24.10 #2282: «нужный уровень покрасивее»). */}
              <span className="absolute inset-x-[3px] h-px bg-background/95" style={{ bottom: `calc(3px + ${thr} * (100% - 6px))` }} />
              <span className="absolute -right-[6px] size-0 border-y-[4px] border-r-[5px] border-y-transparent border-r-foreground/80 drop-shadow-[0_0_1px_rgba(255,255,255,0.8)]"
                style={{ bottom: `calc(3px + ${thr} * (100% - 6px) - 4px)` }} />
            </span>
            {r.s.low && labels && (
              <>
                <span className="absolute left-5 h-px bg-amber-400/90 shadow-[0_0_2px_rgba(0,0,0,0.45)]"
                  style={{ top: `min(${r.v * 100}%, calc(100% - ${capH}px))`, width: `calc(${Math.max(2, Math.min(100, r.u * 100 + 3))}% - 1.25rem)` }} />
                <span className="absolute left-6 flex -translate-y-full items-center gap-1 whitespace-nowrap rounded bg-background/85 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-foreground/90 shadow-sm backdrop-blur"
                  // Нижняя полка: плашка не опускается ниже подписи стеллажа внизу кадра (Алексей 28.10
                  // #3096: «47 % из 50 % накладывается на чип „Выпечка“»). Норма — стрелка на шкале, в
                  // тексте только заполненность.
                  style={{ top: `min(calc(${r.v * 100}% - 3px), calc(100% - ${capH - 2}px))` }}>
                  {Math.round(r.s.pct * 100)} %
                </span>
              </>
            )}
          </span></Tooltip>
        );
      })}
    </span>
  );
}

// F5: opt-in via build flag. When `VITE_DASHBOARD_FULL_FRAME=true`, the image
// sections show the FULL camera frame with the target polygon(s) outlined
// (bright border, transparent fill — like the heatmap) instead of the tight
// crop. Default off → the crop impl is untouched. Falls back to the crop per
// tile whenever the full frame is missing (pruned) or fails to load.
const FULL_FRAME_DASHBOARD = import.meta.env.VITE_DASHBOARD_FULL_FRAME === 'true';

// Desktop crop/full toggle: 'full' shows full frames at once (click → lightbox);
// 'crop' shows the tight crops at once, with the full frame + outline revealed on
// press-and-hold. Mobile ignores this (always 'full').
type DisplayMode = 'full' | 'crop';

// Both view controls share ONE shape + neutral style so they read as a pair:
// each shows its CURRENT state (icon + label) and flips on click. `block` makes
// the button full-width — used in the mobile popup where they stack so the
// labels never wrap.
// В шапке — один стиль с селектором филиала рядом: та же рамка, фон, тень, высота и ЧЁРНЫЙ текст,
// серая только иконка (Алексей 28.10 #3118/#3126/#3127: «серый текст у кнопок», «в единый стиль»).
const VIEW_BTN =
  'inline-flex h-9 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-border ' +
  'bg-card px-3 text-sm text-foreground shadow-sm transition-colors hover:bg-muted/40 [&>svg]:text-muted-foreground';
// В плавающей панели масштаба — в её стиле: прозрачные «призрачные» пилюли, как −/+ рядом
// (Алексей 22.10, #1882: «в стиле хедера, а надо в стиле этой панельки»).
const VIEW_BTN_BAR =
  'inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-full ' +
  'px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground';

// Shows the CURRENT view (Полный кадр / Кроп) and flips to the other on click.
function ModeSwitchButton({ value, onChange, block, bar }: { value: DisplayMode; onChange: (m: DisplayMode) => void; block?: boolean; bar?: boolean }) {
  const { t } = useT();
  // Кнопка показывает, НА ЧТО переключит, а не текущее состояние (Алексей 22.10, #1904).
  const Icon = value === 'full' ? Crop : ImageIcon;
  return (
    <Tooltip content={t('dashboard.display.label')} side={bar ? 'top' : 'bottom'}>
      <button
        type="button"
        onClick={() => onChange(value === 'full' ? 'crop' : 'full')}
        className={cn(bar ? VIEW_BTN_BAR : VIEW_BTN, block && 'w-full')}
      >
        <Icon className="size-4 shrink-0" />
        {value === 'full' ? t('dashboard.display.crop') : t('dashboard.display.full')}
      </button>
    </Tooltip>
  );
}

// Mirrors ModeSwitchButton: shows the CURRENT data scope (Все данные / Только
// проблемы) and flips on click — same neutral style, so the two read as a pair
// rather than one toggle + one filter chip. (Problems-only hides every healthy
// unit and shrinks each clean section to a quiet "all clear" note.)
function ProblemsOnlyButton({ value, onChange, block, bar }: { value: boolean; onChange: (v: boolean) => void; block?: boolean; bar?: boolean }) {
  const { t } = useT();
  // Как и кроп/кадр — показываем, на что переключит (#1904).
  const Icon = value ? LayoutGrid : ListFilter;
  return (
    <Tooltip content={t('dashboard.filter.label')} side={bar ? 'top' : 'bottom'}>
      <button
        type="button"
        aria-pressed={value}
        onClick={() => onChange(!value)}
        className={cn(bar ? VIEW_BTN_BAR : VIEW_BTN, block && 'w-full')}
      >
        <Icon className="size-4 shrink-0" />
        {value ? t('dashboard.filter.all') : t('dashboard.filter.problems')}
      </button>
    </Tooltip>
  );
}

interface ViewControlsProps {
  mode: DisplayMode;
  onMode: (m: DisplayMode) => void;
  problemsOnly: boolean;
  onProblemsOnly: (v: boolean) => void;
  /** В плавающей панели масштаба — её стиль. */
  bar?: boolean;
}

function DashboardViewControls({ mode, onMode, problemsOnly, onProblemsOnly, bar }: ViewControlsProps) {
  return (
    <div className={cn('flex items-center', bar ? 'gap-0.5' : 'gap-1.5')}>
      {FULL_FRAME_DASHBOARD && <ModeSwitchButton value={mode} onChange={onMode} bar={bar} />}
      <ProblemsOnlyButton value={problemsOnly} onChange={onProblemsOnly} bar={bar} />
    </div>
  );
}

// Mobile (i) popup variant: the controls STACK full-width so the labels get the
// whole popup width and never wrap, with a short heading for context.
function DashboardViewControlsField({ mode, onMode, problemsOnly, onProblemsOnly }: ViewControlsProps) {
  const { t } = useT();
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium text-muted-foreground">{t('dashboard.controls.label')}</div>
      <div className="flex flex-col gap-1.5">
        {FULL_FRAME_DASHBOARD && <ModeSwitchButton value={mode} onChange={onMode} block />}
        <ProblemsOnlyButton value={problemsOnly} onChange={onProblemsOnly} block />
      </div>
    </div>
  );
}

// CLIENT-SIDE crop spec for a tile: the camera's fresh full frame + the unit's
// polygons, rendered through PolygonCrop. Used whenever the frame is at least
// as fresh as the stored crop — which it virtually always is (the stored crops
// lag the pipeline by design), so the crop view shows the floor's CURRENT
// state and the press-and-hold full frame is the SAME frame as the crop.
type FeCrop = { frameSrc: string; polygons: number[][][] };

// Units whose tile has rendered the client-side crop at least once. The tile keeps
// rendering it (see ProofTile), so the layout must size it by the SAME crop: sizing
// by the stored crop while the tile drew the frame crop letterboxed the photo —
// light bands above/below and a gap in the panel (Alexey #2097, Ratusha racks:
// the ring-matched frame is often older than the newest stored crop).
const feStickyUnits = new Set<string | number>();

function feCropSticky(
  u: Pick<ProofUnit, 'id' | 'frameImageUrl' | 'polygons' | 'capturedAt' | 'frameCapturedAt'>,
): FeCrop | null {
  const fe = feCropOf(u);
  if (fe) return fe;
  if (feStickyUnits.has(u.id) && u.frameImageUrl && u.polygons?.length) {
    return { frameSrc: imgSrc(u.frameImageUrl), polygons: u.polygons };
  }
  return null;
}

function feCropOf(
  u: Pick<ProofUnit, 'frameImageUrl' | 'polygons' | 'capturedAt' | 'frameCapturedAt'>,
): FeCrop | null {
  if (!u.frameImageUrl || !u.polygons?.length) return null;
  if (
    u.capturedAt && u.frameCapturedAt &&
    new Date(u.frameCapturedAt).getTime() < new Date(u.capturedAt).getTime()
  ) {
    return null; // stored crop is somehow newer than the frame — trust it
  }
  return { frameSrc: imgSrc(u.frameImageUrl), polygons: u.polygons };
}

// <img> that double-buffers src changes: keeps the LAST loaded image painted
// until the new one has decoded, then swaps in place — no loader, no blank
// flash on the 10s refresh ticks. (Browsers clear an <img> the moment its src
// changes; this probes the new src off-screen first.)
function SwapImage({
  src,
  alt,
  className,
  onAspect,
}: {
  src: string;
  alt: string;
  className?: string;
  onAspect?: (ar: number) => void;
}) {
  // Memory-cached images resolve SYNCHRONOUSLY here, so a tile remount (page
  // flip on mobile) paints the image on the very first frame — no spinner
  // flash. A cache MISS still tries the painted-path memory: a re-minted
  // presign is the same picture under a new querystring, so keep painting the
  // previously decoded url while the fresh one loads and swaps in place.
  const [shown, setShown] = useState<string | null>(() => {
    const hit = probeCachedImage(src);
    if (hit) return src;
    return recallPainted(src)?.src ?? null;
  });
  useEffect(() => {
    let alive = true;
    const img = new Image();
    const done = () => {
      if (!alive) return;
      rememberPainted(src, img.naturalWidth || 1, img.naturalHeight || 1);
      setShown(src);
      onAspect?.(img.naturalWidth / Math.max(1, img.naturalHeight));
    };
    img.onload = done;
    img.src = src;
    if (img.complete && img.naturalWidth) done();
    return () => { alive = false; };
    // callbacks captured per effect run; keyed on the src that matters.
  }, [src]);
  if (!shown) return <DelayedSpinner />;
  return (
    <img
      src={shown}
      alt={alt}
      className={className}
      // A recalled stale-presign url can 403 if the browser evicted it —
      // drop to the loading state and let the fresh src take over.
      onError={() => { if (shown !== src) setShown(s => (s === shown ? null : s)); }}
    />
  );
}

// Renders a tile's image: the full frame + outline when `full` (and a frame is
// available); in crop view the client-side crop from the fresh frame (`fe`),
// falling back to the stored crop image. Reports displayed aspects upward.
function ProofTileMedia({
  unit,
  cropSrc,
  full,
  fe,
  onAspect,
  onFrameUnavailable,
  onFeUnavailable,
}: {
  unit: ProofUnit;
  cropSrc: string | null;
  full: boolean;
  /** Client-side crop (fresh frame + polygons); null → stored crop. */
  fe: FeCrop | null;
  /** Reports (aspect, src of the image that aspect belongs to) — the caller
   *  feeds the layout's aspect cache, so it must know WHICH image it measured
   *  (a tile in full mode can be falling back to its crop). */
  onAspect?: (ar: number, src: string) => void;
  onFrameUnavailable?: () => void;
  onFeUnavailable?: () => void;
}) {
  const frameSrc = unit.frameImageUrl ? imgSrc(unit.frameImageUrl) : null;
  if (full && frameSrc && unit.polygons?.length) {
    return (
      <FrameWithPolygon
        src={frameSrc}
        polygons={unit.polygons}
        alt={unit.name}
        onUnavailable={onFrameUnavailable}
        onAspect={ar => onAspect?.(ar, frameSrc)}
        holdPrevious
      />
    );
  }
  if (fe) {
    return (
      <PolygonCrop
        frameSrc={fe.frameSrc}
        polygons={fe.polygons}
        alt={unit.name}
        className="absolute inset-0 size-full object-contain"
        onUnavailable={onFeUnavailable}
        onFrameAspect={ar => onAspect?.(ar, fe.frameSrc)}
      />
    );
  }
  if (cropSrc) {
    return (
      <SwapImage
        src={cropSrc}
        alt={unit.name}
        className="absolute inset-0 size-full object-contain"
        onAspect={ar => onAspect?.(ar, cropSrc)}
      />
    );
  }
  return (
    <span className="flex size-full items-center justify-center">
      <ImageOff className="size-7 text-muted-foreground" />
    </span>
  );
}

// One proof tile: a framed card with the image (full frame or crop per `mode`)
// + a thin name/status caption. Click opens the lightbox. In 'crop' mode, a
// deliberate press-and-HOLD (not a tap) reveals the full frame + outline as a
// peek, releasing returns to the crop.
function ProofTile({
  unit,
  cropSrc,
  cropTime,
  frameTimeLabel,
  openCropLabel,
  mode,
  onActivate,
  onAspect,
  marksOutside = false,
  size,
}: {
  /** Размер плитки (десктопная раскладка): от него зависит, что из подписей помещается на фото. */
  size?: { w: number; h: number };
  unit: ProofUnit;
  cropSrc: string | null;
  cropTime: string;
  frameTimeLabel: string;
  openCropLabel: string;
  mode: DisplayMode;
  onActivate: () => void;
  onAspect: (ar: number, src: string) => void;
  /** Шкалы полок нарисованы снаружи плитки (ShelfMarksAside) — на фото их не дублируем. */
  marksOutside?: boolean;
}) {
  const { t, lang } = useT();
  const tableFrameTime = useTableProofTime();
  const [peeking, setPeeking] = useState(false);
  const tableMedia = useTableProof(unit.tableProof, mode === 'full' || peeking ? 'frame' : 'crop');
  const remindCtx = useContext(RemindContext);
  const tappedAt = useCheckTap(unit.remindKey);
  const tapAge = tappedAt != null ? Date.now() - tappedAt : Infinity;
  const checking = tapAge < CHECK_MAX_MS && (!!unit.checkPending || tapAge < CHECK_GRACE_MS);
  const [, recheckTick] = useState(0);
  // Вердикт пришёл (или ждать дольше нечего) — нажатие забываем; иначе перерисовка, когда кончится окно.
  useEffect(() => {
    if (tappedAt == null || !unit.remindKey) return;
    if (!checking) { setCheckTap(unit.remindKey, null); return; }
    const wait = (unit.checkPending ? CHECK_MAX_MS : CHECK_GRACE_MS) - tapAge;
    const id = window.setTimeout(() => recheckTick(n => n + 1), Math.max(0, wait) + 50);
    return () => window.clearTimeout(id);
  }, [tappedAt, checking, unit.checkPending, unit.remindKey]);
  const reminding = !!unit.remindKey && remindCtx?.active === unit.remindKey;
  const [frameFailed, setFrameFailed] = useState(false);
  // Client-side crop failed (frame 404'd) → fall back to the stored crop.
  const [feFailed, setFeFailed] = useState(false);
  const frameSrc = unit.frameImageUrl ? imgSrc(unit.frameImageUrl) : null;
  // A frame that failed once (e.g. its image 404'd during a transient network
  // wedge, or got pruned) must NOT stay downgraded to the crop forever: re-arm
  // on a new frame URL or a mode flip so "Полный кадр" actually re-attempts the
  // full frame instead of silently sticking on the crop until a page reload.
  useEffect(() => { setFrameFailed(false); setFeFailed(false); }, [frameSrc, mode]);
  const hasFull = FULL_FRAME_DASHBOARD && !frameFailed && !!frameSrc && !!unit.polygons?.length;
  const showFull = mode === 'full' && hasFull;
  // STICKY client-side-crop choice: feCropOf's freshness comparison flaps for
  // tables (each new occupancy makes the stored crop momentarily "newer" than
  // the camera frame), and every flap swapped PolygonCrop ↔ SwapImage — a
  // component-type change that repaints from blank (the white blink). Once a
  // tile has rendered the client-side crop, keep rendering it while a frame +
  // polygons exist; the seconds-level freshness delta is invisible, the
  // repaint flash is not.
  const fe = mode === 'crop' && !feFailed ? feCropSticky(unit) : null;
  if (fe) feStickyUnits.add(unit.id);
  const canPeek = mode === 'crop' && hasFull;
  // The chip reflects the IMAGE on screen: the frame's (fresh) time when the
  // tile shows the full frame OR a client-side crop cut from it, otherwise the
  // stored crop's (possibly stale) time.
  const frameBased = showFull || !!fe;
  const time = unit.tableProof ? (tableMedia.image ? tableFrameTime(tableMedia.image.capturedAt) : '')
    : frameBased && frameTimeLabel ? frameTimeLabel : cropTime;
  const displayedIso = unit.tableProof ? tableMedia.image?.capturedAt : frameBased
    ? unit.frameCapturedAt ?? unit.capturedAt
    : unit.capturedAt;
  // Hard freshness floor: a photo from a previous day must not pass for the
  // floor's current state — broken-camera branches were showing months-old
  // frames. Render an explicit "no frames today" placeholder instead.
  const outdated = !unit.tableProof && isBeforeToday(displayedIso);
  // Кадр старше 10 минут (камера/регистратор залипли, а буфер отдаёт последнее) — чип времени
  // янтарный и с «N мин назад», чтобы старое фото не выдавалось за текущее (Алексей 23.10, #2034).
  const staleMin = (() => {
    const ms = displayedIso ? Date.now() - new Date(displayedIso).getTime() : NaN;
    return Number.isFinite(ms) && ms >= STALE_FRAME_MS ? Math.floor(ms / 60_000) : null;
  })();

  // Status-flip feedback. Tiles never remount on live refreshes (flat keyed
  // grid), so each tile can watch its own good↔problem transitions:
  //  • good→problem: two calm colour pulses over the photo + a transient pill
  //    with the status dot and the NEW status text (attention, not alarm);
  //  • problem→good: the pill alone, with a green done tick and the new label
  //    ("Стол чистый") — a small win to acknowledge, no blinking.
  // First render (prev === undefined) never fires — a freshly opened page is
  // not a state CHANGE.
  const prevProblem = useRef<boolean | undefined>(undefined);
  const [flash, setFlash] = useState<{ kind: 'problem' | 'ok'; label: string } | null>(null);
  useEffect(() => {
    const prev = prevProblem.current;
    prevProblem.current = unit.hasProblem;
    if (prev === undefined || prev === unit.hasProblem) return;
    setFlash({ kind: unit.hasProblem ? 'problem' : 'ok', label: unit.statusShort ?? unit.statusLabel });
    const timer = window.setTimeout(() => setFlash(null), 6100);
    return () => window.clearTimeout(timer);
    // statusLabel is captured at flip time on purpose — label-only changes
    // (e.g. the issue-since clock ticking) must not re-flash.
  }, [unit.hasProblem]);

  const holdTimer = useRef<number | null>(null);
  const held = useRef(false);
  const start = useRef<{ x: number; y: number } | null>(null);
  const clearHold = () => { if (holdTimer.current) { window.clearTimeout(holdTimer.current); holdTimer.current = null; } };
  const onDown = (e: React.PointerEvent) => {
    if (!canPeek || !e.isPrimary) return;
    start.current = { x: e.clientX, y: e.clientY };
    held.current = false;
    clearHold();
    holdTimer.current = window.setTimeout(() => { held.current = true; setPeeking(true); }, 300);
  };
  const onMove = (e: React.PointerEvent) => {
    const s = start.current;
    if (!s || peeking) return;
    if (Math.abs(e.clientX - s.x) > 12 || Math.abs(e.clientY - s.y) > 12) clearHold();
  };
  const onUp = () => { clearHold(); start.current = null; setPeeking(false); };
  const onClick = () => {
    if (held.current) { held.current = false; return; } // it was a hold, not a click
    onActivate();
  };
  useEffect(() => () => clearHold(), []);

  // A tile without today's photo has no current state: the last known one (e.g.
  // «не на месте» from a camera that died days ago) must not keep its colour.
  // Grey dot, same as a free table (Alexey 29.09, LeFort «Столик с диваном»).
  const captionDot = outdated ? 'bg-muted-foreground/40' : unit.dot;
  const captionLabel = outdated ? t('dashboard.noFramesToday') : unit.statusLabel;
  // Оверлеи не должны закрывать фото (Алексей 03.11 #8615/#8631: «подписи вылезают куда попало», «они все
  // картинки закрывают»). Подписи — в CSS-пикселях и с масштабом не сжимаются, поэтому по размеру плитки
  // убираем лишнее: по углам, в одну строку, на мелкой плитке — мельче. Узкая — от подписи остаётся точка
  // статуса (имя — в подсказке); время — только где под него есть место, устаревшее — всегда.
  const tileW = size?.w ?? Infinity, tileH = size?.h ?? Infinity;
  // На маленьком экране время на плитке — только устаревшее (Алексей 03.11 #8669: «убрать время со стеллажей»),
  // а от подписи столов и стеллажей — только кружок статуса, имя — в подсказке (#8673).
  const dense = useCompactDash();
  const dotOnly = dense || tileW < 72;
  const smallTile = tileW < 140 || tileH < 110;
  const showTime = !dotOnly && tileH >= 56 && (staleMin != null || (!dense && tileW >= 120 && tileH >= 72));
  // Проценты полок — только где плитка вмещает и шкалы, и подпись стеллажа под ними.
  const showMarks = tileH >= 72 && tileW >= 72;
  const markLabels = tileH >= 110 && tileW >= 110;
  const captionRow = (
    <div className="flex shrink-0 items-center gap-1.5 px-2 py-1.5 text-xs">
      <Tooltip content={captionLabel} side="top">
        <span
          tabIndex={0}
          role="img"
          aria-label={captionLabel}
          className={cn('size-2.5 shrink-0 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring', captionDot)}
        />
      </Tooltip>
      <span className="min-w-0 flex-1 truncate font-medium">{unit.name}</span>
    </div>
  );

  if (outdated) {
    const dateLabel = displayedIso
      ? new Date(displayedIso).toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU', {
          day: 'numeric',
          month: 'short',
        })
      : '';
    return (
      <div className="relative flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-card duration-300 animate-in fade-in">
        <div className="relative min-h-0 flex-1 bg-muted">
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 p-2 text-center">
            <ImageOff className="size-6 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{t('dashboard.noFramesToday')}</span>
            {dateLabel && (
              <span className="text-[10px] text-muted-foreground/70">{dateLabel}</span>
            )}
          </span>
        </div>
        {captionRow}
        {unit.action}
      </div>
    );
  }

  return (
    // NO mount animation: tiles remount on every mobile page flip, and the
    // fade-in read as "весь дашборд моргает" — cached images now paint on the
    // first frame instead.
    <div data-remind={unit.remindKey} className="relative flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-card">
      {reminding && <span aria-hidden className="pointer-events-none absolute inset-0 z-30 animate-pulse rounded-lg ring-4 ring-inset ring-amber-400" />}
      <button
        type="button"
        className="group relative min-h-0 flex-1 select-none overflow-hidden bg-muted"
        aria-label={openCropLabel}
        onClick={onClick}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
        onPointerCancel={onUp}
      >
        {unit.tableProof ? <TableProofImage image={tableMedia.image} loading={tableMedia.loading} name={unit.name} onAspect={onAspect} /> : <ProofTileMedia
          unit={unit}
          cropSrc={cropSrc}
          full={showFull}
          fe={fe}
          onAspect={onAspect}
          onFrameUnavailable={() => setFrameFailed(true)}
          onFeUnavailable={() => setFeFailed(true)}
        />}
        {unit.shelves?.length && !marksOutside && showMarks ? <ShelfMarks shelves={unit.shelves} polygons={unit.polygons} full={showFull} labels={markLabels} captionBelow={!dense} /> : null}
        {!unit.tableProof && canPeek && peeking && (
          <span className="absolute inset-0 bg-muted">
            <FrameWithPolygon src={frameSrc!} polygons={unit.polygons!} alt={unit.name} />
          </span>
        )}
        {time && showTime && (
          <span className={cn('pointer-events-none absolute right-1 top-1 max-w-[calc(100%-0.5rem)] truncate rounded text-[10px] font-medium tabular-nums shadow-sm backdrop-blur',
            smallTile ? 'px-1 py-px leading-tight' : 'px-1.5 py-0.5',
            staleMin != null ? 'bg-amber-400/95 text-amber-950' : 'bg-background/85 text-foreground/90')}>
            {time}{staleMin != null && ` · ${staleMin >= 60 ? `${Math.floor(staleMin / 60)} ч ${staleMin % 60} мин` : `${staleMin} мин`} назад`}
          </span>
        )}
        {!unit.tableProof && flash?.kind === 'problem' && (
          <span
            aria-hidden
            className={cn('tile-blink pointer-events-none absolute inset-0', unit.dot)}
          />
        )}
        {!unit.tableProof && flash && (
          <span className="flash-badge pointer-events-none absolute bottom-7 left-1/2 z-10 flex max-w-[calc(100%-0.75rem)] -translate-x-1/2 items-center gap-1.5 rounded-full border border-border/50 bg-background/90 px-2.5 py-1 text-xs font-medium text-foreground shadow-md backdrop-blur">
            {flash.kind === 'ok' ? (
              <CircleCheck className="size-3.5 shrink-0 text-green-500" aria-hidden />
            ) : (
              <span className={cn('size-2.5 shrink-0 rounded-full', unit.dot)} aria-hidden />
            )}
            <span className="truncate">{flash.label}</span>
          </span>
        )}
      </button>
      {/* Подпись — плашкой на фото, а не отдельной строкой под ним: строки подписей,
          шапки и отступы съедали место у самих фото (Алексей 22.10, #1880 п.4).
          Стеллаж на маленьком экране — без кружка: статус уже показывают цветные шкалы полок (Алексей 03.11
          #8683/#8684). Где шкал нет (нет данных о полках или плитка для них мала) — кружок остаётся. */}
      {!(dense && unit.shelves?.length && (marksOutside || showMarks)) && (
      <div className={cn('pointer-events-auto absolute bottom-1 left-1 z-10 flex items-center gap-1 rounded-md bg-background/85 leading-tight shadow-sm backdrop-blur',
        // Кнопка «Убрали» в правом нижнем углу — подпись до неё не доходит.
        unit.action ? 'max-w-[calc(100%-3.5rem)]' : 'max-w-[calc(100%-0.5rem)]',
        dotOnly ? 'p-1' : smallTile ? 'px-1 py-px text-[10px]' : 'px-1.5 py-0.5 text-[11px]')}>
        <Tooltip content={dotOnly ? `${unit.name} · ${captionLabel}` : captionLabel} side="top">
          <span tabIndex={0} role="img" aria-label={captionLabel}
            className={cn('size-2 shrink-0 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring', captionDot)} />
        </Tooltip>
        {/* Только название: состояние стола («Свободен / Занят / Не на месте») показывает точка, текстом
            оно закрывало фото (Алексей 03.11 #8631). */}
        {!dotOnly && <span className="min-w-0 truncate font-medium">{unit.name}</span>}
      </div>
      )}
      {/* «Проверяем» — только после нажатия «Убрали»/«Поправили» (#8722): по фото проходит мягкий луч,
          по центру — плашка с «думающими» точками. Снимается вердиктом пайплайна (см. useCheckTap). */}
      {checking && (
        <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden rounded-lg" role="status" aria-live="polite">
          <div className="absolute inset-0 bg-sky-950/20" />
          <div className="absolute inset-0 animate-[shimmer-sweep_1.8s_ease-in-out_infinite] bg-[linear-gradient(90deg,transparent_25%,rgba(125,211,252,0.45)_50%,transparent_75%)]" />
          <div className={cn('absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-background/90 font-medium text-foreground shadow-md backdrop-blur',
            smallTile || dense ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs')}>
            <span className="flex items-center gap-0.5" aria-hidden>
              {[0, 160, 320].map(d => <span key={d} className="size-1 animate-bounce rounded-full bg-sky-500" style={{ animationDelay: `${d}ms` }} />)}
            </span>
            {t('dashboard.checking')}
          </div>
        </div>
      )}
      {unit.action}
    </div>
  );
}

// Shared page control. Mobile: a floating right-edge dot rail on a glass base
// (translucent + blur) so it reads clearly over photos without a layout row.
// Desktop: a compact ‹n/m› overlay bottom-right. `hidden` tucks the mobile rail
// half off-screen + dims it (used by the auto-hide-on-idle behaviour).
function SectionPager({
  page,
  totalPages,
  setPage,
  mobile,
  hidden,
  inline,
}: {
  page: number;
  totalPages: number;
  setPage: (p: number) => void;
  mobile?: boolean;
  hidden?: boolean;
  /** В строке шапки карточки, а не поверх её низа. */
  inline?: boolean;
}) {
  const { t } = useT();
  const dense = useCompactDash();
  if (totalPages <= 1) return null;
  if (mobile) {
    return (
      <div
        className={cn(
          'pointer-events-auto absolute right-1.5 top-1/2 z-10 flex -translate-y-1/2 flex-col items-center gap-2 rounded-full border border-border/40 bg-card/60 px-1 py-1.5 shadow-md backdrop-blur-md transition-all duration-300',
          hidden && 'translate-x-1/2 opacity-30',
        )}
      >
        {Array.from({ length: totalPages }).map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={String(i + 1)}
            onClick={() => setPage(i)}
            className={cn(
              'rounded-full transition-all',
              i === page ? 'size-2.5 bg-primary' : 'size-2 bg-muted-foreground/50 hover:bg-muted-foreground/80',
            )}
          />
        ))}
      </div>
    );
  }
  return (
    <div className={cn('pointer-events-auto flex items-center gap-1 text-muted-foreground',
      inline ? 'shrink-0' : 'absolute bottom-1 right-1 z-10 rounded-md border border-border/40 bg-card/70 px-1 py-0.5 shadow-sm backdrop-blur')}>
      <button type="button" className={cn('grid place-items-center rounded hover:bg-muted hover:text-foreground disabled:opacity-40', inline && dense ? 'size-4' : 'size-6')} disabled={page === 0} onClick={() => setPage(page - 1)} aria-label={t('dashboard.pager.prev')}>
        <ChevronLeft className="size-3.5" />
      </button>
      <span className="text-[11px] tabular-nums">{inline ? `${page + 1}/${totalPages}` : t('dashboard.proof.page', { page: page + 1, total: totalPages })}</span>
      <button type="button" className={cn('grid place-items-center rounded hover:bg-muted hover:text-foreground disabled:opacity-40', inline && dense ? 'size-4' : 'size-6')} disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)} aria-label={t('dashboard.pager.next')}>
        <ChevronRight className="size-3.5" />
      </button>
    </div>
  );
}

// Tile order must be CALM — the ONLY thing allowed to order tiles is the
// status category (worst first), everything else is frozen spatial memory:
//
//  • While the card settles (first data, aspects being probed) the order is
//    the category-first packing below, re-derived freely. Settling ends when
//    every unit's aspect is known OR a hard deadline passes — ONCE, and never
//    re-enters (the old `knownAspects > known` re-entry re-adopted a fresh
//    packing after every crop-URL rotation, reshuffling perfectly calm green
//    tables on every poll).
//  • After settle a unit KEEPS ITS SLOT. A status flip moves it only when the
//    grid actually paginates — on a single page everything is visible, so the
//    dot/flash carries the signal and position stays put; across pages a
//    freshly-bad unit must surface in the front (page 1) category block.
//  • New units slot in at the end of their category block; removed units just
//    vacate their slot.
//
// Within a category the order comes from `identityCmp` when given (tables:
// physical clusters — same camera, then position on the frame, then natural
// name order) or aspect-desc packing (racks: rows pack tightly) — computed at
// settle time and then frozen.
interface StableOrderOpts {
  identityCmp?: (a: ProofUnit, b: ProofUnit) => number;
  /** Whether the grid paginated on the previous render. */
  paginated: boolean;
}

// Category key: problems by severity rank first; everything without a problem is ONE category
// (Алексей 29.10 #3221: «не пересортировывать, если стол свободен/занят/нет данных/не на месте») —
// within it the order is the identity/packing order, so the optimal layout stays fixed.
const OK_CAT = 100;
// Ниже этой высоты фото плитка «мелкая» — тогда проблемы выделяются и едут вперёд (#3221).
const COMFORT_IMG_H = 120;
// Пол высоты фото на планшете/десктопе: ниже подпись и время не помещаются на плитку (#8615).
const DESK_FLOOR_H = 56;
const MORE_KEY = '__more';
const catOf = (u: ProofUnit) => (u.hasProblem ? u.rank : OK_CAT);
const SETTLE_DEADLINE_MS = 12_000;

function useStableOrder(
  units: ProofUnit[],
  aspectFor: (u: ProofUnit) => number,
  knownAspects: number,
  { identityCmp, paginated }: StableOrderOpts,
): ProofUnit[] {
  const ref = useRef<{
    order: (string | number)[];
    cat: Map<string | number, number>;
    settled: boolean;
    mountedAt: number;
  } | null>(null);

  const withinCmp = (a: ProofUnit, b: ProofUnit): number => {
    if (identityCmp) return identityCmp(a, b);
    const d = aspectFor(b) - aspectFor(a);
    return Math.abs(d) > 0.05 ? d : String(a.id).localeCompare(String(b.id));
  };
  const baseCmp = (a: ProofUnit, b: ProofUnit) =>
    catOf(a) - catOf(b) || withinCmp(a, b);

  const st = ref.current;
  const mountedAt = st?.mountedAt ?? Date.now();
  const settled =
    (st?.settled ?? false) ||
    (units.length > 0 &&
      (knownAspects >= units.length || Date.now() - mountedAt > SETTLE_DEADLINE_MS));

  let result: ProofUnit[];
  if (!st || !st.settled) {
    // Settling: free re-derive of the category-first packing.
    result = [...units].sort(baseCmp);
  } else {
    const byId = new Map(units.map(u => [u.id, u]));
    // Start from the frozen order, drop vanished units.
    const kept = st.order.filter(id => byId.has(id));
    const keptSet = new Set(kept);
    // A flip may move a unit ONLY when the grid paginates; otherwise it keeps
    // its slot and only the stored category is refreshed on commit.
    const moved: ProofUnit[] = [];
    let working = kept;
    if (paginated) {
      // Only a NEW problem moves forward (#3221): resolved problems and state changes among OK units
      // keep their slot — the tile just recolours.
      const flips = units.filter(u => {
        const was = st.cat.get(u.id);
        return keptSet.has(u.id) && was !== undefined && was >= OK_CAT && catOf(u) < OK_CAT;
      });
      if (flips.length) {
        const flipIds = new Set(flips.map(u => u.id));
        working = working.filter(id => !flipIds.has(id));
        moved.push(...flips);
      }
    }
    const additions = units.filter(u => !keptSet.has(u.id));
    // Insert moved + new units at the END of their category block (stable for
    // everyone already placed). Category of placed units = stored (frozen) cat.
    const placed = working.map(id => ({ id, cat: st.cat.get(id) ?? catOf(byId.get(id)!) }));
    for (const u of [...moved, ...additions].sort(baseCmp)) {
      const k = catOf(u);
      let at = placed.length;
      for (let i = 0; i < placed.length; i++) {
        if (placed[i].cat > k) { at = i; break; }
      }
      placed.splice(at, 0, { id: u.id, cat: k });
    }
    result = placed.map(p => byId.get(p.id)!).filter(Boolean);
  }

  // Commit AFTER render (writing refs during render breaks with StrictMode
  // double-rendering); keyed on what can change the frozen order.
  const sig =
    units.map(u => `${u.id}:${catOf(u)}`).join(',') +
    `|${settled ? 1 : 0}|${paginated ? 1 : 0}|${settled ? 'x' : knownAspects}`;
  useEffect(() => {
    ref.current = {
      order: result.map(u => u.id),
      // Stored category: for un-moved units under no-pagination keep the OLD
      // category so a later pagination doesn't retroactively shuffle them —
      // they move only on their NEXT flip while paginated.
      cat: new Map(result.map(u => {
        const prev = ref.current?.settled && !paginated ? ref.current.cat.get(u.id) : undefined;
        return [u.id, prev ?? catOf(u)] as const;
      })),
      settled,
      mountedAt,
    };
    // result is a pure function of (units, cache, ref) — sig covers the inputs.
  }, [sig]);

  return result;
}

// Adaptive proof grid — justified rows. The tile area MEASURES its available
// box (useMeasuredBox, throttled on resize) and lays the units out in
// justified rows (see justifiedLayout.ts): every row's tiles keep their OWN
// aspect ratio scaled to a common row height that makes the row span the panel
// exactly — cell shape == image shape, so the panel is photo surface, not
// letterbox "curtains". Problem units sort first AND get taller rows; OK units
// are re-ordered by aspect so rows pack tightly. Pagination falls out of the
// row flow and recomputes on every (throttled) resize and on crop↔full
// switches — deterministically, so flipping back restores the same layout.
// A timestamp chip stays pinned top-right; the only caption is a thin name +
// status-circle line. Pager: a floating dot rail (mobile) or ‹n/m› (desktop).

function ProofGrid({
  units,
  openCropLabel,
  mobile = false,
  mode = 'full',
  identityCmp,
}: {
  units: ProofUnit[];
  openCropLabel: string;
  mobile?: boolean;
  mode?: DisplayMode;
  /** Within-category order (see useStableOrder); default = aspect packing. */
  identityCmp?: (a: ProofUnit, b: ProofUnit) => number;
}) {
  const { t, lang } = useT();
  const frameTime = useFrameTime();
  const [page, setPage] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const openTableGallery = useContext(TableGalleryContext);

  const GAP = 8;
  // Подпись — плашкой на самом фото (#1880), строки под фото нет: резерв 32 px делал плитку выше
  // картинки — светлые полосы сверху и снизу («паддинги и щель», Алексей #2097).
  const CAPTION_H = 0;
  const { ref, width, height } = useMeasuredBox<HTMLDivElement>(150);

  // What a tile will DISPLAY in this mode:
  //  • full view → the full frame (aspect = frame's natural aspect);
  //  • crop view + fresh frame → the CLIENT-SIDE crop (aspect computed from
  //    the polygons + frame aspect — pure math, no image bytes needed, and
  //    constant across refreshes since the polygon doesn't move);
  //  • otherwise → the stored crop image (aspect probed).
  const usesFullFrame = (u: ProofUnit) =>
    FULL_FRAME_DASHBOARD && mode === 'full' && !!u.frameImageUrl && !!u.polygons?.length;
  const feOf = (u: ProofUnit) => (mode === 'crop' ? feCropSticky(u) : null);

  // Probe ONLY the stored crops that will actually display (tiny images);
  // frame aspects arrive from the tiles that render them (primeAspect via
  // onAspect / onFrameAspect) and are near-uniform per section, so the
  // measured-median default below covers the not-yet-seen ones.
  const { aspectOf, bump } = useImageAspects(
    units.map(u =>
      !usesFullFrame(u) && !feOf(u) && u.image_url ? imgSrc(u.image_url) : null,
    ),
  );
  const median = (xs: number[]) =>
    xs.length ? [...xs].sort((a, b) => a - b)[Math.floor(xs.length / 2)] : undefined;
  const frameDefault =
    median(
      units
        .map(u => (u.frameImageUrl ? aspectOf(imgSrc(u.frameImageUrl)) : undefined))
        .filter((a): a is number => a !== undefined),
    ) ?? 16 / 9;
  const cropDefault =
    median(
      units
        .map(u => (u.image_url ? aspectOf(imgSrc(u.image_url)) : undefined))
        .filter((a): a is number => a !== undefined),
    ) ?? 4 / 3;
  // Last KNOWN aspect per unit: stored-crop URLs change on every new
  // occupancy (tables get a fresh crop ~every minute), so a src-keyed cache
  // alone misses right after each poll and the tile would fall back to the
  // default aspect — reflowing the rows (and visibly shuffling tiles) even
  // though the table's crop shape never changes. Remember the unit's last
  // real aspect and use it over the generic default while the new src probes.
  const unitAspect = useRef(new Map<string | number, number>());
  const aspectFor = (u: ProofUnit): number => {
    let known: number | undefined;
    if (usesFullFrame(u)) {
      known = aspectOf(imgSrc(u.frameImageUrl!));
    } else {
      const fe = feOf(u);
      if (fe) {
        known = feCropAspect(fe.polygons, aspectOf(fe.frameSrc) ?? frameDefault) ?? undefined;
      } else if (u.image_url) {
        known = aspectOf(imgSrc(u.image_url));
      }
    }
    if (known !== undefined) {
      unitAspect.current.set(u.id, known);
      return known;
    }
    return unitAspect.current.get(u.id)
      ?? (usesFullFrame(u) ? frameDefault : cropDefault);
  };
  const knownAspectCount = units.filter(u =>
    usesFullFrame(u) || feOf(u)
      ? aspectOf(imgSrc(u.frameImageUrl!)) !== undefined
      : u.image_url
        ? aspectOf(imgSrc(u.image_url)) !== undefined
        : false,
  ).length;

  // Pagination state feeds the NEXT render's ordering decision (whether a
  // status flip may move a tile) — one-render lag is fine, flips are rare.
  const paginatedRef = useRef(false);
  const sorted = useStableOrder(units, aspectFor, knownAspectCount, {
    identityCmp,
    paginated: paginatedRef.current,
  });

  // Сначала — раскладка без увеличения проблемных плиток (#3221): если так всё видно крупно, её и держим —
  // выросшая «грязная» плитка перестраивала строки и дёргала весь экран.
  const flatItems = sorted.map(u => ({ key: u.id, aspect: aspectFor(u), big: false }));
  const layoutItems = sorted.map(u => ({ key: u.id, aspect: aspectFor(u), big: u.hasProblem }));
  const baseOpts = {
    width,
    height,
    gap: GAP,
    captionH: CAPTION_H,
    minImgH: mobile ? 140 : 110,
    maxImgH: height, // engine caps at height - captionH
    targetImgH: mobile ? 200 : 170,
    bigScale: 1.35,
  };
  // Не-мобильная раскладка: листания нет вообще — всё на одну страницу, максимум площади
  // фото (Алексей 20.10 #1631). Пейджер — только телефону.
  // Но не мельче DESK_FLOOR_H (Алексей 03.11 #8615: при крупном масштабе плитки ужимались до 28 px,
  // подписи вылезали за фото, ряды — за панель). Не влезает и так — хвост сворачивается в плитку «+N»
  // (по клику — галерея с первой скрытой); проблемные стоят впереди, поэтому скрываются зелёные.
  const fitDesk = (items: typeof flatItems) => {
    const fits = (ps: JustifiedPage[]) => ps.length > 0 && ps[0].usedH <= height + 1;
    const all = justifiedFitAll(items, baseOpts, DESK_FLOOR_H);
    if (fits(all) || items.length < 2) return all;
    const withMore = (k: number) => justifiedFitAll([...items.slice(0, k), { key: MORE_KEY, aspect: 0.8, big: false }], baseOpts, DESK_FLOOR_H);
    let lo = 0, hi = items.length - 1, best: JustifiedPage[] | null = null;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      const ps = withMore(mid);
      if (fits(ps)) { lo = mid; best = ps; } else hi = mid - 1;
    }
    return best ?? withMore(lo);
  };
  const flatPages = mobile ? justifiedPages(flatItems, baseOpts) : fitDesk(flatItems);
  const flatMinH = Math.min(...flatPages.flatMap(pg => pg.rows.map(r => r.imgH)), Infinity);
  // Тесно — листается или картинки мельче удобного: тогда выделяем проблемы размером и пускаем их вперёд.
  const crowded = flatPages.length > 1 || flatMinH < COMFORT_IMG_H;
  const pages = crowded
    ? (mobile ? justifiedPages(layoutItems, baseOpts) : fitDesk(layoutItems))
    : flatPages;
  const shownKeys = new Set(pages.flatMap(pg => pg.rows.flatMap(r => r.tiles.map(tl => tl.key))));
  const hiddenUnits = !mobile && shownKeys.has(MORE_KEY) ? sorted.filter(u => !shownKeys.has(u.id)) : [];
  // FIT-FIRST (desktop): if the full set CAN be shown on one screen by
  // accepting smaller tiles, prefer that over pagination — a fully visible
  // board can be frozen, and everyone keeps their spatial memory of where
  // each table/rack lives. Ladder: progressively lower floors + flatter
  // problem emphasis; first single-page candidate wins. Mobile keeps its
  // pager (small screens genuinely can't fit a zone).
  paginatedRef.current = crowded;
  const unitById = new Map<string | number, ProofUnit>(sorted.map(u => [u.id, u]));

  const totalPages = Math.max(1, pages.length);
  const safePage = Math.min(Math.max(0, page), totalPages - 1);

  // Проба (#2061): во сколько раз изменится площадь плиток, если ширина панели станет ×k.
  const probe = useContext(SlackContext);
  const tileArea = (ps: JustifiedPage[]) =>
    ps.reduce((acc, pg) => acc + pg.rows.reduce((a2, row) => a2 + row.tiles.reduce((a3, tl) => a3 + tl.width * row.imgH, 0), 0), 0);
  const probeScale = (() => {
    if (!probe || mobile || width <= 0 || height <= 0 || !layoutItems.length) return null;
    const base = tileArea(pages);
    if (base <= 0) return null;
    const other = tileArea(justifiedFitAll(layoutItems, { ...baseOpts, width: width * probe.k }));
    return Math.round(Math.sqrt(other / base) * 100) / 100; // линейный масштаб
  })();
  useEffect(() => { probe?.report(probeScale); }, [probe, probeScale]);
  useEffect(() => () => probe?.report(null), [probe]);
  const demand = useContext(DemandContext);
  const demandR = (() => {
    if (!demand || mobile || width <= 0 || height <= 0 || pages.length !== 1) return null;
    const pg = pages[0];
    const cw = Math.max(0, ...pg.rows.map(r => r.tiles.reduce((a, tl) => a + tl.width, 0) + GAP * Math.max(0, r.tiles.length - 1)));
    if (cw <= 0 || pg.usedH <= 0) return null;
    const r = (cw * Math.min(2.5, height / pg.usedH)) / width;
    return Math.round(Math.min(2.5, Math.max(0.4, r)) * 100) / 100;
  })();
  useEffect(() => { demand?.(demandR); }, [demand, demandR, width, height]);
  useEffect(() => () => demand?.(null), [demand]);

  const galleryUnits = sorted.filter(u => u.image_url || feCropOf(u));
  const gallerySrcs = galleryUnits.map(u => (u.image_url ? imgSrc(u.image_url) : null));
  // Full-frame view (F5): per-unit full frame + outline for the lightbox, aligned
  // with gallerySrcs (the crops). null when no frame → lightbox shows the crop.
  const galleryFrames = galleryUnits.map(u =>
    FULL_FRAME_DASHBOARD && u.frameImageUrl ? imgSrc(u.frameImageUrl) : null,
  );
  const galleryPolys = galleryUnits.map(u => u.polygons ?? null);
  // Client-side crops for the lightbox too — the crop you zoom into is cut from
  // the SAME fresh frame the press-and-hold reveals, never a stale stored crop.
  const galleryCrops: (LightboxCropSpec | null)[] = galleryUnits.map(u => feCropOf(u));
  const galleryUnit = lightboxIdx >= 0 ? galleryUnits[lightboxIdx] : null;
  const openLightbox = (unit: ProofUnit) => {
    const idx = galleryUnits.findIndex(u => u.id === unit.id);
    if (idx >= 0) {
      if (unit.tableProof && openTableGallery) openTableGallery(galleryUnits.map(u => u.tableProof!), idx, mode);
      else setLightboxIdx(idx);
    }
  };

  // Tile placement for an arbitrary page (pure) — used for the live page AND
  // for the outgoing snapshot during the mobile page-slide below.
  // room — свободное место слева от плитки (до соседа или края панели): шкалы полок выносим наружу
  // только туда, где пусто, иначе проценты ложились на соседнюю плитку (#8615).
  const moreBox: { cur: { x: number; y: number; w: number; h: number } | null } = { cur: null };
  const placeFor = (pageIdx: number) => {
    const rows = pages[pageIdx]?.rows ?? [];
    const usedH = pages[pageIdx]?.usedH ?? 0;
    const yOffset = Math.max(0, (height - usedH) / 2);
    const placed: { unit: ProofUnit; x: number; y: number; w: number; h: number; room: number }[] = [];
    let y = yOffset;
    for (const row of rows) {
      const rowW =
        row.tiles.reduce((s, t) => s + t.width, 0) + GAP * (row.tiles.length - 1);
      let x = row.fill ? 0 : Math.max(0, (width - rowW) / 2);
      let prevRight = 0;
      for (const tile of row.tiles) {
        const unit = unitById.get(tile.key);
        if (unit) placed.push({ unit, x, y, w: tile.width, h: row.imgH + CAPTION_H, room: x - prevRight });
        else if (tile.key === MORE_KEY) moreBox.cur = { x, y, w: tile.width, h: row.imgH + CAPTION_H };
        prevRight = x + tile.width + GAP;
        x += tile.width + GAP;
      }
      y += row.imgH + CAPTION_H + GAP;
    }
    // Stable child ORDER (by unit id) so React matches keys without moves in
    // the element list; the position is pure CSS.
    placed.sort((a, b) => String(a.unit.id).localeCompare(String(b.unit.id)));
    return placed;
  };

  // Mobile page swipe = vertical CAROUSEL, not an instant swap: the outgoing
  // page stays rendered (non-interactive snapshot) and slides off while the
  // incoming page slides in from the swiped-toward side — one continuous
  // surface. Desktop pager keeps the instant swap.
  const prevPageRef = useRef(safePage);
  const [pageSlide, setPageSlide] = useState<{ dir: 1 | -1; page: number } | null>(null);
  const [slideEntered, setSlideEntered] = useState(true);
  // A finger-drag page change (below) has ALREADY animated — only dot taps /
  // programmatic page changes replay the carousel slide here.
  const dragCommit = useRef(false);
  if (prevPageRef.current !== safePage) {
    if (mobile && height > 0 && !dragCommit.current) {
      setPageSlide({ dir: safePage > prevPageRef.current ? 1 : -1, page: prevPageRef.current });
      setSlideEntered(false);
    }
    dragCommit.current = false;
    prevPageRef.current = safePage;
  }

  // R1/R2 (mobile): the page follows the FINGER — the live page and the
  // incoming neighbour ride together while dragging and snap on release. The
  // dot rail auto-hides until you interact.
  const { dotsVisible, ownsPull, touchHandlers, drag, liveRef, ghostRef } = useSwipePager({
    page: safePage,
    totalPages,
    setPage,
    enabled: mobile,
    size: height,
    onDragStart: () => setPageSlide(null),
    onBeforeCommit: () => { dragCommit.current = true; },
  });
  useEffect(() => {
    if (!pageSlide) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setSlideEntered(true));
    });
    const drop = window.setTimeout(() => setPageSlide(null), 340);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(drop);
    };
    // snapshot identity is covered by the page indices
  }, [pageSlide?.page, safePage]);

  // Живая страница — до разметки: заодно заполняет moreBox (место плитки «+N»).
  const livePlaced = placeFor(safePage);
  const morePlace = moreBox.cur;
  // Крайний случай (Алексей 03.11 #8675): даже плитки по 56 px не вмещают все столы — тогда кружки статуса
  // без фото (имя и состояние — в подсказке, нажатие — галерея). Место выросло — снова фото.
  if (!mobile && hiddenUnits.length > 0 && sorted.every(u => u.tableProof)) {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div ref={ref} className="flex min-h-0 flex-1 flex-wrap content-center items-center justify-center gap-1 overflow-hidden">
          {sorted.map(u => (
            <Tooltip key={u.id} content={`${u.name} · ${u.statusLabel}`}>
              <button
                type="button"
                aria-label={`${u.name}: ${u.statusLabel}`}
                data-remind={u.remindKey}
                onClick={() => openLightbox(u)}
                className={cn('size-4 shrink-0 rounded-full ring-offset-1 ring-offset-card', u.dot, u.hasProblem && 'ring-2 ring-orange-500/40')}
              />
            </Tooltip>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div
      className="relative flex min-h-0 flex-1 flex-col"
      {...touchHandlers}
      // On non-first pages, claim the pull gesture so the global pull-to-refresh
      // stands down and a downward swipe pages back instead (R1).
      data-owns-pull={ownsPull ? '' : undefined}
    >
      {/* FLAT absolutely-positioned tiles (like DesktopBoard's flat children):
          nesting tiles inside per-row wrappers meant any row reflow (an aspect
          settling, a new crop landing) REPARENTED tiles → React remounted them
          → the canvas/img lost its painted state → a white flash + loader on
          live refreshes. Flat keyed children at one parent only ever MOVE
          (animated), never remount — photos update strictly in place. */}
      <div ref={ref} className="relative min-h-0 flex-1 overflow-hidden">
        <div
          ref={liveRef}
          className="absolute inset-0"
          style={pageSlide ? {
            transform: `translateY(${slideEntered ? 0 : pageSlide.dir * height}px)`,
            transition: slideEntered ? 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
          } : undefined}
        >
          {livePlaced.map(p => {
            const aside = !!p.unit.shelves?.length && p.h < SMALL_TILE_H && p.room >= ASIDE_W
              && (p.unit.shelves?.length ?? 0) * ASIDE_ROW_H <= p.h;
            return (
            <div
              key={p.unit.id}
              className="absolute"
              style={{
                left: p.x,
                top: p.y,
                width: p.w,
                height: p.h,
                transition: 'left 300ms ease, top 300ms ease, width 300ms ease, height 300ms ease',
              }}
            >
              {aside && <ShelfMarksAside shelves={p.unit.shelves!} />}
              <ProofTile
                marksOutside={aside}
                size={mobile ? undefined : p}
                unit={p.unit}
                cropSrc={p.unit.image_url ? imgSrc(p.unit.image_url) : null}
                cropTime={frameTime(p.unit.capturedAt)}
                frameTimeLabel={p.unit.frameCapturedAt ? frameTime(p.unit.frameCapturedAt) : ''}
                openCropLabel={openCropLabel}
                mode={mode}
                onActivate={() => openLightbox(p.unit)}
                onAspect={(ar, src) => {
                  if (primeAspect(src, ar)) bump();
                }}
              />
            </div>
            );
          })}
          {morePlace && hiddenUnits.length > 0 && (() => {
            const mp = morePlace;
            const worst = hiddenUnits.find(u => u.hasProblem);
            const label = lang === 'en' ? `${hiddenUnits.length} more` : `Ещё ${hiddenUnits.length}`;
            return (
              <Tooltip content={hiddenUnits.map(u => u.name).join(', ')}>
              <button
                type="button"
                aria-label={label}
                onClick={() => openLightbox(hiddenUnits[0])}
                className="absolute flex items-center justify-center gap-1.5 rounded-lg border border-border bg-muted/70 text-sm font-semibold tabular-nums text-foreground transition-colors hover:bg-muted"
                style={{ left: mp.x, top: mp.y, width: mp.w, height: mp.h, transition: 'left 300ms ease, top 300ms ease, width 300ms ease, height 300ms ease' }}
              >
                {worst && <span className={cn('size-2 shrink-0 rounded-full', worst.dot)} aria-hidden />}
                +{hiddenUnits.length}
              </button>
              </Tooltip>
            );
          })()}
        </div>
        {/* Incoming neighbour while a finger-drag is in flight: starts one page
            off along the drag direction and rides with the finger (the hook
            drives both transforms imperatively). Non-interactive. */}
        {drag && (
          <div
            ref={ghostRef}
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{ transform: `translateY(${drag.dir * height}px)` }}
          >
            {placeFor(drag.neighbor).map(p => (
              <div
                key={p.unit.id}
                className="absolute"
                style={{ left: p.x, top: p.y, width: p.w, height: p.h }}
              >
                <ProofTile
                  unit={p.unit}
                  cropSrc={p.unit.image_url ? imgSrc(p.unit.image_url) : null}
                  cropTime={frameTime(p.unit.capturedAt)}
                  frameTimeLabel={p.unit.frameCapturedAt ? frameTime(p.unit.frameCapturedAt) : ''}
                  openCropLabel={openCropLabel}
                  mode={mode}
                  onActivate={() => {}}
                  onAspect={() => {}}
                />
              </div>
            ))}
          </div>
        )}
        {/* Outgoing page snapshot: rides off in the opposite direction so the
            swipe reads as one continuous surface. Non-interactive; dropped as
            soon as the slide lands. */}
        {pageSlide && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              transform: `translateY(${slideEntered ? -pageSlide.dir * height : 0}px)`,
              transition: slideEntered ? 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
            }}
          >
            {placeFor(pageSlide.page).map(p => (
              <div
                key={p.unit.id}
                className="absolute"
                style={{ left: p.x, top: p.y, width: p.w, height: p.h }}
              >
                <ProofTile
                  unit={p.unit}
                  cropSrc={p.unit.image_url ? imgSrc(p.unit.image_url) : null}
                  cropTime={frameTime(p.unit.capturedAt)}
                  frameTimeLabel={p.unit.frameCapturedAt ? frameTime(p.unit.frameCapturedAt) : ''}
                  openCropLabel={openCropLabel}
                  mode={mode}
                  onActivate={() => {}}
                  onAspect={() => {}}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <SectionPager page={safePage} totalPages={totalPages} setPage={setPage} mobile={mobile} hidden={mobile && !dotsVisible} />

      {galleryUnit && (
        <ImageLightbox
          srcs={gallerySrcs}
          frameSrcs={galleryFrames}
          polygonsList={galleryPolys}
          cropSpecs={galleryCrops}
          // The lightbox follows the view: crop view opens the (fresh) crop
          // with hold-to-see-the-frame; full view opens the outlined frame
          // with hold-to-see-the-crop. Same frame either way.
          primary={mode === 'crop' ? 'crop' : 'frame'}
          holdHint={mode === 'crop' ? t('dashboard.proof.holdForFrame') : t('dashboard.proof.holdForCrop')}
          index={lightboxIdx}
          alt={galleryUnit.name}
          captions={galleryUnits.map(u => [
            u.name,
            u.statusLabel,
            frameTime(
              (FULL_FRAME_DASHBOARD || feCropOf(u)) && u.frameCapturedAt
                ? u.frameCapturedAt
                : u.capturedAt,
            ),
          ].filter(Boolean).join(' · '))}
          onClose={() => setLightboxIdx(-1)}
          onNavigate={setLightboxIdx}
        />
      )}
    </div>
  );
}

function LayoutSummaryCard({ zoneId, bare, mode, problemsOnly }: { zoneId: string | null; bare?: boolean; mode?: DisplayMode; problemsOnly?: boolean }) {
  const { t } = useT();
  const frameTime = useFrameTime();
  const { data, error, isValidating, mutate } = useSWR<LayoutSummary>(
    zoneId ? ['dashboard:layout', zoneId] : null,
    async () => (await getLayoutSummary(zoneId!)).data,
    DASH_SWR,
  );
  // Error UI only when there is NOTHING to show: during the 10s polling, a
  // transient failed tick must keep the last good data on screen (SWR retains
  // it) instead of blinking the card into "couldn't load" and back.
  const apiMissing = !!error && data === undefined;

  const nonGreenCount = (data?.red_count ?? 0) + (data?.yellow_count ?? 0);

  const allUnits: ProofUnit[] = (data?.sample ?? []).map(rack => ({
    id: rack.id,
    name: rack.name,
    subtitle: rack.rack_type,
    // «Поправили» нажато, камера ещё не подтвердила — нейтральная пульсирующая точка (#1645).
    dot: rack.fixed_pending ? 'bg-sky-500 animate-pulse' : SAMPLE_DOT[rack.status],
    // Photo is always the latest; when there's an open issue, the dot's tooltip
    // carries WHEN it started (e.g. "Частично опустела · с 12:50").
    statusLabel: rack.fixed_pending
      ? t('layout.status.fixedPending')
      : rack.occluded
      ? (rack.occluded_since
          ? `${t('layout.status.occluded')} · ${t('dashboard.lastClearFrame', { time: frameTime(rack.occluded_since) })}`
          : t('layout.status.occluded'))
      : rack.issue_since
        ? `${t(`layout.status.${rack.status}`)} · ${t('dashboard.issueSince', { time: frameTime(rack.issue_since) })}`
        : t(`layout.status.${rack.status}`),
    statusShort: rack.occluded ? t('layout.status.occludedShort') : t(`layout.status.${rack.status}`),
    // A rack is a "problem" when it's not full and not unknown — i.e. it has
    // emptied out enough to need attention. Matches the warning-badge count.
    hasProblem: rack.status !== 'green' && rack.status !== 'unknown',
    rank: RACK_OCCUPANCY_RANK[rack.status],
    image_url: rack.image_url,
    capturedAt: rack.captured_at,
    frameImageUrl: rack.frame_image_url,
    frameCapturedAt: rack.frame_captured_at,
    polygons: rack.polygons,
    shelves: rack.shelves,
    remindKey: `layout:${rack.id}`,
    checkPending: !!rack.fixed_pending,
    // «Поправили» у каждого проблемного стеллажа (Алексей 20.10 #1639) — как «Убрали» у стола.
    action: rack.status !== 'green' && rack.status !== 'unknown' && !rack.occluded && !rack.fixed_pending
      ? <MarkDoneButton label={t('layout.markFixed')} checkKey={`layout:${rack.id}`} onMark={() => markRackFixed(rack.id)} onDone={() => mutate()} />
      : undefined,
  }));
  const units = problemsOnly ? allUnits.filter(u => u.hasProblem) : allUnits;
  // Явные проблемы — для напоминаний робота: не перекрыт, «Поправили» не нажато.
  useReportReminders('layout', (data?.sample ?? [])
    .filter(r => r.status !== 'green' && r.status !== 'unknown' && !r.occluded && !r.fixed_pending)
    .map(r => ({ key: `layout:${r.id}`, since: r.issue_since ? Date.parse(r.issue_since) : null })));

  // No frames this (zone-local) day → every status above is stale. The panel
  // greys out and says so instead of painting confident circles.
  const noFrames = !!data && data.has_frames_today === false;

  // Overall layout status for the header circle — computed from the racks
  // (this card carried no overall caption before).
  const overallTone: OverallTone = !data || data.total === 0 || noFrames
    ? 'none'
    : (data.red_count ?? 0) > 0
      ? 'critical'
      : nonGreenCount > 0
        ? 'attention'
        : 'ok';
  const overallLabel = noFrames
    ? t('dashboard.noFramesToday')
    : !data || data.total === 0
      ? t('dashboard.status.noData')
      : nonGreenCount > 0
        ? t('dashboard.status.layoutAttention')
        : t('dashboard.status.layoutAllFull');

  // The header circle carries the WORST inner dot's colour — an amber
  // "attention" circle over all-yellow rows read as a phantom orange problem.
  const worstStatus = (data?.sample ?? [])
    .filter((r) => r.status !== 'unknown')
    .sort((a, b) => RACK_OCCUPANCY_RANK[a.status] - RACK_OCCUPANCY_RANK[b.status])[0]?.status;
  const worstDotClass = overallTone !== 'none' && worstStatus ? SAMPLE_DOT[worstStatus] : undefined;

  // Error card only on a SETTLED failure (SWR done retrying), debounced so
  // the slow first load / inter-retry gaps show the skeleton, not an error.
  const showError = useSettledFlag(apiMissing && !isValidating, 2500);
  const loading = data === undefined && !showError;
  const showDot = !!zoneId && !loading && !showError;
  return (
    <SectionCardShell
      bare={bare}
      sectionKey="layout"
      icon={ShoppingBasket}
      title={t('dashboard.cards.layout')}
      tone={showDot ? overallTone : undefined}
      toneLabel={overallLabel}
      toneDotClass={worstDotClass}
      badge={data && !noFrames && data.total > 0 && nonGreenCount > 0 ? <Badge variant="warning">{nonGreenCount}</Badge> : undefined}
    >
      {!zoneId ? (
        <EmptyHint text={t('page.selectBranchFirst')} />
      ) : loading ? (
        <CardGridSkeleton mobile={bare} mode={mode} />
      ) : showError ? (
        <CardError onRetry={() => mutate()} />
      ) : noFrames ? (
        <EmptyHint text={t('dashboard.noFramesToday')} />
      ) : data && data.total > 0 && units.length > 0 ? (
        <ProofGrid units={units} openCropLabel={t('layout.openCrop')} mobile={bare} mode={mode} />
      ) : problemsOnly && allUnits.length > 0 ? (
        <AllOkBody text={t('dashboard.status.layoutAllFull')} />
      ) : null}
    </SectionCardShell>
  );
}

// ── Card 4: Queue snapshot ─────────────────────────────────────────────────

// Worst (red) first so over-limit queues lead the grid.
const QUEUE_STATUS_RANK: Record<QueueSample['status'], number> = {
  red: 0,
  yellow: 1,
  green: 2,
  unknown: 3,
};

// `mode` is intentionally NOT a prop here — the queue is always shown full-frame
// regardless of the board's crop/full toggle.
function QueueCard({ zoneId, bare, problemsOnly }: { zoneId: string | null; bare?: boolean; problemsOnly?: boolean }) {
  const { t } = useT();
  const { data, error, isValidating, mutate } = useSWR<QueueSummary>(
    zoneId ? ['dashboard:queues', zoneId] : null,
    async () => holdQueueProblems(zoneId!, (await getQueueSummary(zoneId!)).data),
    DASH_SWR,
  );
  // Error UI only when there is NOTHING to show: during the 10s polling, a
  // transient failed tick must keep the last good data on screen (SWR retains
  // it) instead of blinking the card into "couldn't load" and back.
  const apiMissing = !!error && data === undefined;

  // No frames this (zone-local) day → statuses are stale; grey the panel and
  // say «Нет кадров за сегодня» instead of an error/greens.
  const noFrames = !!data && data.has_frames_today === false;

  const health: LayoutHealth = data?.configured ? data.health : 'green';
  // Overall queue status for the header circle (replaces the headline caption).
  const overallTone: OverallTone = !data || !data.configured || noFrames
    ? 'none'
    : health === 'red' ? 'critical' : health === 'yellow' ? 'attention' : 'ok';
  const overallLabel = noFrames
    ? t('dashboard.noFramesToday')
    : !data || !data.configured
      ? t('dashboard.status.noData')
      : health === 'red'
        ? t('dashboard.cards.queueRed', { n: data.over_limit_count })
        : health === 'yellow'
          ? t('dashboard.cards.queueYellow', { n: data.at_limit_count })
          : t('dashboard.status.queueOk');

  // Header circle colour = the worst queue dot's colour (amber vs yellow-400
  // mismatch otherwise).
  const worstQueueStatus = (data?.queues ?? [])
    .filter((q) => q.status !== 'unknown')
    .sort((a, b) => QUEUE_STATUS_RANK[a.status] - QUEUE_STATUS_RANK[b.status])[0]?.status;
  const worstDotClass =
    overallTone !== 'none' && worstQueueStatus ? SAMPLE_DOT[worstQueueStatus] : undefined;

  const singleQueue = data?.configured && data.queues.length === 1;
  const allQueueUnits: ProofUnit[] = (data?.queues ?? []).map(q => ({
    id: q.id,
    name: q.name,
    dot: SAMPLE_DOT[q.status],
    statusLabel: q.current === null
      ? t('queue.unknown')
      : t('queue.size', { n: q.current, max: q.max_allowed }),
    hasProblem: q.status === 'red' || q.status === 'yellow',
    rank: QUEUE_STATUS_RANK[q.status],
    image_url: q.image_url,
    capturedAt: q.captured_at,
    frameImageUrl: q.frame_image_url,
    frameCapturedAt: q.frame_captured_at,
    polygons: q.polygons,
  }));
  const queueUnits = problemsOnly ? allQueueUnits.filter(u => u.hasProblem) : allQueueUnits;

  // Error card only on a SETTLED failure (SWR done retrying), debounced so
  // the slow first load / inter-retry gaps show the skeleton, not an error.
  const showError = useSettledFlag(apiMissing && !isValidating, 2500);
  const loading = data === undefined && !showError;
  const showDot = !!zoneId && !loading && !showError;
  return (
    <SectionCardShell
      bare={bare}
      sectionKey="queue"
      icon={Users}
      title={t('dashboard.cards.queue')}
      tone={showDot ? overallTone : undefined}
      toneLabel={overallLabel}
      toneDotClass={worstDotClass}
      badge={data && !noFrames && data.configured && data.over_limit_count + data.at_limit_count > 0 ? <Badge variant="warning">{data.over_limit_count + data.at_limit_count}</Badge> : undefined}
    >
      {!zoneId ? (
        <EmptyHint text={t('page.selectBranchFirst')} />
      ) : loading ? (
        <CardGridSkeleton mobile={bare} mode="full" />
      ) : showError ? (
        <CardError onRetry={() => mutate()} />
      ) : noFrames ? (
        <EmptyHint text={t('dashboard.noFramesToday')} />
      ) : data && data.configured ? (
        singleQueue ? (
          problemsOnly && queueUnits.length === 0 ? (
            <AllOkBody text={t('dashboard.status.queueOk')} />
          ) : (
            <SingleQueueDisplay sample={data.queues[0]} />
          )
        ) : queueUnits.length > 0 ? (
          // Queue is always the full frame (+ lane outline), never a tight crop
          // — the lane crop reads worse than the whole scene.
          <ProofGrid units={queueUnits} openCropLabel={t('queue.openCrop')} mobile={bare} mode="full" />
        ) : problemsOnly && allQueueUnits.length > 0 ? (
          <AllOkBody text={t('dashboard.status.queueOk')} />
        ) : null
      ) : null}
    </SectionCardShell>
  );
}

// Single queue: drop the redundant queue name (the card header already says
// "Очереди") and lean into the one available frame. The media box HUGS the
// image's aspect inside the measured card area (instead of stretching a
// bordered box across the whole panel and letterboxing the photo in it), so a
// wide desktop column shows a photo, not curtains. The queue is ALWAYS shown as
// the full frame (with the lane outline when available) regardless of the
// crop/full toggle — its source image is already the whole frame, and a tight
// lane crop reads worse than the full scene.
function SingleQueueDisplay({ sample }: { sample: QueueSample }) {
  const { t } = useT();
  const dense = useCompactDash();
  const frameTime = useFrameTime();
  const { ref, width, height } = useMeasuredBox<HTMLDivElement>(150);
  const [, setArTick] = useState(0);
  const imageSrc = sample.image_url ? imgSrc(sample.image_url) : null;
  const frameSrc = sample.frame_image_url ? imgSrc(sample.frame_image_url) : null;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // F5: the queue frame is already the full frame — just overlay the lane
  // outline(s) when the flag is on, falling back to the plain image on failure.
  const [frameFailed, setFrameFailed] = useState(false);
  // Re-arm on a new frame so a transient image failure doesn't strand the queue
  // on the plain image until reload (see ProofTile for the same fix).
  useEffect(() => { setFrameFailed(false); }, [frameSrc]);
  const showFrame = FULL_FRAME_DASHBOARD && !frameFailed && !!frameSrc && !!sample.polygons?.length;

  // Aspect of what's displayed → the media box hugs it. Re-rendered via
  // setArTick once the actual image reports its natural aspect.
  const ar =
    (frameSrc ? cachedAspect(frameSrc) : undefined) ??
    (imageSrc ? cachedAspect(imageSrc) : undefined) ??
    16 / 9;
  const fitW = width > 0 && height > 0 ? Math.min(width, height * ar) : 0;
  const fitH = ar > 0 ? fitW / ar : 0;
  const prime = (src: string) => (a: number) => {
    if (primeAspect(src, a)) setArTick(v => v + 1);
  };

  const displayedIso = showFrame
    ? sample.frame_captured_at ?? sample.captured_at
    : sample.captured_at;
  const time = frameTime(displayedIso);
  const outdated = isBeforeToday(displayedIso);
  const sizeText = sample.current === null
    ? t('queue.unknown')
    : t('queue.size', { n: sample.current, max: sample.max_allowed });
  const caption = [t('dashboard.cards.queue'), sizeText, time].filter(Boolean).join(' · ');

  // Мелкое фото (крупный масштаб, #8615): чип в одну строку и плотнее, время — где есть высота.
  const small = fitW > 0 && (fitW < 180 * remScale() || fitH < 90);
  // Маленький экран (#8673): от плашки — кружок статуса и мелкое «1/5», без «человек».
  const statusChip = dense ? (
    <span aria-label={sizeText} className="absolute bottom-1 right-1 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background/85 px-1 py-0.5 text-[10px] font-medium leading-none tabular-nums shadow-sm backdrop-blur">
      <span className={cn('size-2 shrink-0 rounded-full', SAMPLE_DOT[sample.status])} />
      {sample.current !== null && <span>{sample.current}/{sample.max_allowed}</span>}
    </span>
  ) : (
    <span className={cn('absolute inline-flex max-w-[calc(100%-0.5rem)] items-center gap-1.5 whitespace-nowrap rounded-md bg-background/85 font-medium shadow-sm backdrop-blur',
      small ? 'bottom-1 right-1 px-1.5 py-0.5 text-[11px]' : 'bottom-2 right-2 px-2 py-1 text-xs')}>
      <span className={cn('size-2 shrink-0 rounded-full', SAMPLE_DOT[sample.status])} />
      {small && sample.current !== null && <Users className="size-3 shrink-0 text-muted-foreground" aria-hidden />}
      <span className="metric-value truncate">{small && sample.current !== null ? `${sample.current} / ${sample.max_allowed}` : sizeText}</span>
    </span>
  );

  return (
    <div ref={ref} className="relative flex min-h-0 flex-1 items-center justify-center">
      {outdated ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-border bg-muted px-6 py-8 text-muted-foreground">
          <ImageOff className="size-7" />
          <span className="text-sm">{t('dashboard.noFramesToday')}</span>
          <span className="inline-flex items-center gap-1.5 text-xs">
            {/* Stale reading — grey, not the last status colour (same rule as table tiles). */}
            <span className="size-2 shrink-0 rounded-full bg-muted-foreground/40" />
            <span className="metric-value">{sizeText}</span>
          </span>
        </div>
      ) : imageSrc ? (
        <button
          type="button"
          className="group relative overflow-hidden rounded-lg border border-border bg-muted"
          style={{ width: fitW || '100%', height: fitH || '100%' }}
          aria-label={t('queue.openCrop')}
          onClick={() => setLightboxOpen(true)}
        >
          {showFrame ? (
            <FrameWithPolygon
              src={frameSrc!}
              polygons={sample.polygons!}
              alt={sample.name}
              onUnavailable={() => setFrameFailed(true)}
              onAspect={prime(frameSrc!)}
              holdPrevious
            />
          ) : (
            <SwapImage
              src={imageSrc!}
              alt={sample.name}
              className="absolute inset-0 size-full object-contain"
              onAspect={prime(imageSrc!)}
            />
          )}
          {time && !dense && !(fitH > 0 && fitH < 56) && (
            <span className="pointer-events-none absolute left-1 top-1 rounded bg-background/85 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-foreground/90 shadow-sm backdrop-blur">
              {time}
            </span>
          )}
          {statusChip}
        </button>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-lg border border-border bg-muted text-muted-foreground">
          <Users className="size-8" />
          <span className="inline-flex items-center gap-1.5 text-xs">
            <span className={cn('size-2 shrink-0 rounded-full', SAMPLE_DOT[sample.status])} />
            <span className="metric-value">{sizeText}</span>
          </span>
        </div>
      )}
      {lightboxOpen && imageSrc && (
        <ImageLightbox
          srcs={[imageSrc]}
          frameSrcs={frameSrc && sample.polygons?.length ? [frameSrc] : undefined}
          polygonsList={frameSrc && sample.polygons?.length ? [sample.polygons ?? null] : undefined}
          index={0}
          alt={sample.name}
          captions={[caption]}
          onClose={() => setLightboxOpen(false)}
          onNavigate={() => {}}
        />
      )}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function EmptyHint({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <AlertTriangle className="size-4" /> {text}
    </div>
  );
}

function Skeletons({ count }: { count: number }) {
  return (
    <ul className="space-y-1.5">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className="h-6 animate-pulse rounded-md bg-muted" />
      ))}
    </ul>
  );
}

// Structural placeholder for the image-card body. Mirrors the SHAPE the real
// ProofGrid will take so the skeleton doesn't snap from a tiny 2×2 into a column
// of big full-width frames: full-frame tiles are landscape and pack FEWER per
// row (1 on phones), crops are squarer and pack 2-up. Honours the build flag —
// when full-frame is off everything is a crop regardless of `mode`.
function CardGridSkeleton({ mobile, mode }: { mobile?: boolean; mode?: DisplayMode }) {
  const full = FULL_FRAME_DASHBOARD && mode !== 'crop';
  const cols = mobile ? (full ? 1 : 2) : full ? 2 : 3;
  const count = mobile ? (full ? 2 : 4) : full ? 4 : 6;
  return (
    <div
      className="grid min-h-0 flex-1 gap-2"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridAutoRows: '1fr' }}
      aria-hidden
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="size-full animate-pulse rounded-lg bg-muted" />
      ))}
    </div>
  );
}

// Всё выполнено: Линза-робот честно летает от края до края экрана (Алексей 22.10, #1877).
// Спрайт-лист с прозрачностью (public/brand/robot_sheet_v2.webp): 0 — глаза открыты, 1 — закрыты,
// 2…13 — полёт (смотрит вправо, влево — зеркало), 14…21 — бьётся лбом в экран (тап),
// 22…29 — качается на месте (изредка, пока висит; Алексей 22.10, #1951–#1953).
// Позицию, покачивание и кадр двигаем сами через ref (без ререндера React на каждый кадр).
// Reduced motion — стоит по центру.
// v3 (#2033): тот же лист, вычищены отдельные крошки по краям кадров и сглажен край альфы.
// Кадр 320×320: робот обычного размера занимает полосу 240 px (сверху и снизу по 40 px —
// место под «лоб в экран», где он крупнее). `height` = высота этой полосы.
const ROBOT_AR = 320 / 240;   // ширина кадра / высота полосы робота
const ROBOT_FH = 320 / 240, ROBOT_PAD = 40 / 240;
// Корпус в кадре не по центру (слева место под шлейф полёта): x 113…277 из 320. Зеркалим
// вокруг центра корпуса и «от края до края» считаем по корпусу, а не по кадру (#1888).
const BODY_L = 113 / 320, BODY_R = 277 / 320, BODY_C = 195 / 320;
// Плашки «всё в порядке» по разделам (#1899): иконка и название раздела.
const SECTION_META: Record<string, { icon: React.ElementType; title: string }> = {
  tasks: { icon: ListTodo, title: 'dashboard.cards.tasks' },
  tables: { icon: UtensilsCrossed, title: 'dashboard.cards.tables' },
  layout: { icon: ShoppingBasket, title: 'dashboard.cards.layout' },
  queue: { icon: Users, title: 'dashboard.cards.queue' },
  sales_plan: { icon: TrendingUp, title: 'dashboard.cards.salesPlan' },
};
// Смена сцены «были проблемы → всё в порядке» (#2036/#2080): на 1,6 с крупная галка
// «Отлично, всё в порядке» поверх, плашки разделов въезжают справа, робот выезжает из-за
// края. Только на НАБЛЮДАЕМОМ переходе: при загрузке страницы (данные ещё грузятся) — нет.
type Celebrate = 'off' | 'on' | 'fade';
function SceneSwitch({ showRobot, ready, robot, board }: {
  showRobot: boolean;
  ready: boolean;
  robot: (celebrate: Celebrate) => React.ReactNode;
  board: React.ReactNode;
}) {
  const prev = useRef<boolean | null>(null);
  const [celebrate, setCelebrate] = useState<Celebrate>('off');
  useEffect(() => {
    if (!ready) return;
    const was = prev.current;
    prev.current = showRobot;
    if (was !== false || !showRobot) return;
    setCelebrate('on');
    const a = window.setTimeout(() => setCelebrate('fade'), 1300);
    const b = window.setTimeout(() => setCelebrate('off'), 1900);
    return () => { window.clearTimeout(a); window.clearTimeout(b); };
  }, [showRobot, ready]);
  return <>{showRobot ? robot(celebrate) : board}</>;
}

// Звук стука лбом по стеклу (#2137): синтез WebAudio, без файла — короткий глухой удар
// (затухающий низкий тон) + щелчок стекла (отфильтрованный шум). Браузер даёт звук только после
// первого касания страницы; до него — тихо, без ошибок.
const KNOCK_SOUND = false;
let knockAudio: AudioContext | null = null;
function playKnock() {
  try {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    knockAudio ??= new AC();
    const ac = knockAudio;
    if (ac.state === 'suspended') void ac.resume();
    const t = ac.currentTime;
    const out = ac.createGain();
    out.gain.value = 0.45;
    out.connect(ac.destination);
    // Стекло (#2145 «совсем не похоже на стекло»): костяшкой по стеклу — щелчок + несколько
    // негармоничных высоких мод с быстрым затуханием; «тела» почти нет.
    const modes: [number, number, number][] = [[1180, 0.5, 0.09], [2710, 0.32, 0.065], [4290, 0.2, 0.045], [6050, 0.1, 0.03], [320, 0.16, 0.035]];
    for (const [f, a, d] of modes) {
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(f * (0.98 + Math.random() * 0.04), t);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(a, t + 0.002);
      g.gain.exponentialRampToValueAtTime(0.0001, t + d * 2.2);
      o.connect(g).connect(out);
      o.start(t); o.stop(t + d * 2.4);
    }
    const len = Math.floor(ac.sampleRate * 0.006);
    const buf = ac.createBuffer(1, len, ac.sampleRate);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < len; i++) ch[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 2;
    const noise = ac.createBufferSource();
    noise.buffer = buf;
    const hp = ac.createBiquadFilter();
    hp.type = 'highpass'; hp.frequency.value = 3000;
    const ng = ac.createGain();
    ng.gain.value = 0.5;
    noise.connect(hp).connect(ng).connect(out);
    noise.start(t);
  } catch { /* без звука */ }
}

// ── Напоминания по висящим проблемам (Алексей 23.10 #2068, AI447) ─────────────────────
// Явная проблема (выкладка/стол) висит → робот выезжает из-за края к её плитке, врезается в
// экран и говорит «Кажется, уже N минут»; плитка подсвечивается, планшет вибрирует. График от
// начала проблемы: 5, 10, дальше шаг ×1,5 (17,5 → 28,75 → …). Вход со старой проблемой — одно
// напоминание через 20 с, дальше по графику. Время начала: выкладка — issue_since с бэка;
// столы — когда дашборд впервые увидел проблему (бэк времени не отдаёт).
type RemindItem = { key: string; since: number | null };
type RemindApi = { report: (section: string, items: RemindItem[]) => void; active: string | null };
const RemindContext = React.createContext<RemindApi | null>(null);
const REMIND_ENTRY_MS = 20_000;
// #2146 («вылетает несколько раз за 5 минут»): на весь экран не чаще раза в 5 мин; «вход» — только
// первые 60 с после открытия страницы; пропавшая на время проблема (мигнула, перекрыли, нажали
// «Поправили» и не подтвердилось) помнит свой график 10 мин, а не начинает с нового «входа».
const REMIND_MIN_GAP_MS = 5 * 60_000;
const REMIND_ENTRY_WINDOW_MS = 60_000;
const REMIND_FORGET_MS = 10 * 60_000;

function remindThreshold(i: number): number {
  let t = 5, step = 5;
  for (let k = 0; k < i; k++) { t += step; step *= 1.5; }
  return t * 60_000;
}
function remindsDue(ageMs: number): number {
  let n = 0;
  while (n < 50 && ageMs >= remindThreshold(n)) n++;
  return n;
}

type RemindState = { lastFired: number; sent: Record<string, number>; seen: Record<string, number>; since: Record<string, number> };
const REMIND_STORE = 'dash:reminders';
function loadRemindState(): RemindState {
  const empty: RemindState = { lastFired: 0, sent: {}, seen: {}, since: {} };
  try {
    const v = JSON.parse(localStorage.getItem(REMIND_STORE) ?? 'null') as RemindState | null;
    if (!v) return empty;
    const now = Date.now();
    const keep = Object.keys(v.seen ?? {}).filter(k => now - v.seen[k] < REMIND_FORGET_MS);
    const pick = (o: Record<string, number> = {}) => Object.fromEntries(keep.filter(k => k in o).map(k => [k, o[k]]));
    return { lastFired: v.lastFired ?? 0, sent: pick(v.sent), seen: pick(v.seen), since: pick(v.since) };
  } catch { return empty; }
}
function saveRemindState(v: RemindState) {
  try { localStorage.setItem(REMIND_STORE, JSON.stringify(v)); } catch { /* приватный режим */ }
}

function useProblemReminders(): { api: RemindApi; active: { key: string; minutes: number } | null; done: () => void } {
  const sections = useRef(new Map<string, RemindItem[]>());
  // Состояние напоминаний переживает перезагрузку (#2152: «после обновления страницы робот
  // повторно прилетает»): сколько напоминаний было по проблеме, её начало и время последнего вылета.
  const saved = useRef(loadRemindState());
  const firstSeen = useRef(new Map<string, number>());
  const sent = useRef(new Map<string, number>(Object.entries(saved.current.sent)));
  const lastSeen = useRef(new Map<string, number>(Object.entries(saved.current.seen)));
  const earliest = useRef(new Map<string, number>(Object.entries(saved.current.since)));
  const mountedAt = useRef(Date.now());
  const lastFired = useRef(saved.current.lastFired);
  const [active, setActive] = useState<{ key: string; minutes: number } | null>(null);
  const activeRef = useRef(active);
  activeRef.current = active;
  const report = useCallback((section: string, items: RemindItem[]) => { sections.current.set(section, items); }, []);
  useEffect(() => {
    const id = window.setInterval(() => {
      const now = Date.now();
      const all = [...sections.current.values()].flat();
      for (const it of all) lastSeen.current.set(it.key, now);
      if (all.length) saveRemindState({ lastFired: lastFired.current, sent: Object.fromEntries(sent.current), seen: Object.fromEntries(lastSeen.current), since: Object.fromEntries(earliest.current) });
      for (const [k, at] of [...lastSeen.current]) {
        if (now - at > REMIND_FORGET_MS) { lastSeen.current.delete(k); firstSeen.current.delete(k); sent.current.delete(k); earliest.current.delete(k); }
      }
      if (activeRef.current || document.visibilityState !== 'visible') return;
      if (now - lastFired.current < REMIND_MIN_GAP_MS) return;
      const entry = now - mountedAt.current < REMIND_ENTRY_WINDOW_MS;
      let best: { key: string; age: number; due: number } | null = null;
      for (const it of all) {
        const seenAt = firstSeen.current.get(it.key) ?? now;
        if (!firstSeen.current.has(it.key)) firstSeen.current.set(it.key, now);
        // Начало проблемы не прыгает вперёд (#2148: «то 88 минут, то 6 про один стеллаж»): бэк
        // иногда не отдаёт время (или оно пересчиталось) — держим самое раннее, что видели.
        // Не раньше начала суток: проблема со вчерашнего вечера не «висит 25 часов» (Алексей 28.10 #3153).
        const dayStart = new Date(now).setHours(0, 0, 0, 0);
        const since = Math.max(dayStart, Math.min(it.since ?? seenAt, earliest.current.get(it.key) ?? Infinity, seenAt));
        earliest.current.set(it.key, since);
        const age = now - since;
        const due = remindsDue(age);
        // Вход (страницу только открыли): одно напоминание по уже старой проблеме. Появилась
        // позже — график с текущего места, без немедленного вылета.
        if (!sent.current.has(it.key)) sent.current.set(it.key, entry ? Math.max(0, due - 1) : due);
        if (due <= (sent.current.get(it.key) ?? 0)) continue;
        if (now - seenAt < REMIND_ENTRY_MS) continue;
        if (!best || age > best.age) best = { key: it.key, age, due };
      }
      if (!best) return;
      sent.current.set(best.key, best.due);
      lastFired.current = now;
      saveRemindState({ lastFired: now, sent: Object.fromEntries(sent.current), seen: Object.fromEntries(lastSeen.current), since: Object.fromEntries(earliest.current) });
      setActive({ key: best.key, minutes: Math.max(1, Math.floor(best.age / 60_000)) });
    }, 5000);
    return () => window.clearInterval(id);
  }, []);
  const api = useMemo(() => ({ report, active: active?.key ?? null }), [report, active]);
  const done = useCallback(() => setActive(null), []);
  return { api, active, done };
}

/** Секция сообщает свои висящие проблемы для напоминаний. */
function useReportReminders(section: string, items: RemindItem[]) {
  const ctx = useContext(RemindContext);
  const sig = items.map(i => `${i.key}@${i.since ?? ''}`).join('|');
  useEffect(() => { ctx?.report(section, items); }, [ctx?.report, section, sig]);
  useEffect(() => () => ctx?.report(section, []), [ctx?.report, section]);
}

function ruForm(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return `${n} ${one}`;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return `${n} ${few}`;
  return `${n} ${many}`;
}
// «1 час 28 минут» (#2148), винительный: «уже 1 час 21 минуту».
function minutesRu(n: number): string {
  const h = Math.floor(n / 60), m = n % 60;
  const hs = h ? ruForm(h, 'час', 'часа', 'часов') : '';
  const ms = m || !h ? ruForm(m, 'минуту', 'минуты', 'минут') : '';
  return [hs, ms].filter(Boolean).join(' ');
}

// Робот-напоминание поверх доски: вылетает из-за правого края к плитке, врезается в экран,
// облачко «Кажется, уже N минут», улетает обратно. ~6,5 с, потом onDone.
function ReminderRobot({ targetKey, minutes, onDone }: { targetKey: string; minutes: number; onDone: () => void }) {
  const { lang } = useT();
  const botRef = useRef<HTMLDivElement>(null);
  const sayRef = useRef<HTMLDivElement>(null);
  const [say, setSay] = useState<boolean | null>(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  // По меньшей стороне экрана и с полом 64 px (было 96 по высоте — на телефоне боком робот занимал пятую часть, #8615).
  const [height] = useState(() => Math.round(Math.min(150, Math.max(64, Math.min(window.innerHeight, window.innerWidth) * 0.16))));
  const fw = Math.round(height * ROBOT_AR);
  useEffect(() => {
    const bot = botRef.current;
    if (!bot) return;
    const el = document.querySelector(`[data-remind="${CSS.escape(targetKey)}"]`);
    const r = el?.getBoundingClientRect();
    const W = window.innerWidth, H = window.innerHeight;
    // Над правой частью плитки, голова — в верхней трети; внутри экрана.
    const cx = r ? r.left + r.width * 0.68 : W * 0.6;
    const cy = r ? r.top + Math.min(r.height * 0.3, r.height - height * 0.6) : H * 0.4;
    const tx = Math.min(W - BODY_R * fw - 8, Math.max(8 - BODY_L * fw, cx - BODY_C * fw));
    const ty = Math.min(H - height - 8, Math.max(8, cy - height / 2));
    const off = W - BODY_L * fw + 12;
    // Пламя на месте — вниз, по оси струи (Алексей 03.11 #8629: «на месте пламя назад, а не вниз»).
    const rig = makeRig(bot, { flameDown: true });
    // Наклон и пламя — физика ранца по самому перемещению (Алексей 03.11 #8736); p.tilt у частей — лёгкое
    // покачивание поверх, у нарисованных кадров (удар) — их собственный наклон.
    const jet = makeJet(fw / 320);
    const draw = (x: number, y: number, mirror: boolean, p: BotPose = {}, now = performance.now()) => {
      const k = p.k ?? 1;
      bot.style.transform = `translate(${x + (p.dx ?? 0)}px, ${y + (p.dy ?? 0) - height * ROBOT_PAD}px) scale(${(mirror ? -1 : 1) * k * (p.sx ?? 1)}, ${k * (p.sy ?? 1)})`;
      const j = jet(x, y, now, mirror);
      rig({ ...p, tilt: p.sprite ? p.tilt : j.tilt + (p.tilt ?? 0), flame: j.flame }, now);
      const sb = sayRef.current;
      if (sb) sb.style.transform = `translate(${Math.max(8, x + BODY_L * fw + 2 - sb.offsetWidth)}px, ${y + height * 0.02}px)`;
    };
    // Полёт: полосы по скорости (производная smoothstep, пик в середине пути); наклон и пламя — в draw.
    const flyPose = (u: number): BotPose => ({ speed: 6 * u * (1 - u) / 1.5 });
    const IN = 1300, BONK0 = IN + 150, SAY0 = BONK0 + KNOCK_IN + KNOCKS * KNOCK_MS, OUT0 = SAY0 + 3200, OUT = 1200;
    const smooth = (u: number) => u * u * (3 - 2 * u);
    let raf = 0;
    const t0 = performance.now();
    let said = false, hid = false, buzzed = false, knocked = 0;
    const tick = (now: number) => {
      const t = now - t0;
      const bobS = Math.sin(now / 1800 * 2 * Math.PI);
      const bob = ty + bobS * height * 0.04;
      if (t < IN) {
        const u = smooth(t / IN);
        draw(off + (tx - off) * u, bob, true, flyPose(t / IN), now);
      } else if (t < BONK0) {
        // Разворот — мгновенное зеркало (#2352).
        draw(tx, bob, false, {}, now);
      } else if (t < OUT0) {
        if (!buzzed && t >= BONK0 + KNOCK_IN) {   // вибрация в такт стукам
          buzzed = true;
          try { navigator.vibrate?.(Array.from({ length: KNOCKS * 2 - 1 }, (_, i) => (i % 2 ? KNOCK_MS - 60 : 60))); } catch { /* нет */ }
        }
        // Звук — в момент касания стекла (35 % каждого цикла стука).
        const kt = t - BONK0 - KNOCK_IN - KNOCK_MS * 0.35;
        // Звук пока выключен (#2146: «звук убрать можно всё же пока что»).
        if (KNOCK_SOUND && kt >= 0 && knocked < KNOCKS && kt >= knocked * KNOCK_MS) { knocked++; playKnock(); }
        const pose = knockPose(t - BONK0, height);
        if (pose) draw(tx, bob, false, pose, now);
        else draw(tx, bob, false, { blink: blinkAt(t % 3000, 0) }, now);
        if (!said && t >= SAY0) { said = true; setSay(true); }
      } else if (t < OUT0 + OUT) {
        if (!hid) { hid = true; setSay(false); }
        const u = smooth((t - OUT0) / OUT);
        draw(tx + (off - tx) * u, bob, false, flyPose((t - OUT0) / OUT), now);
      } else {
        onDoneRef.current();
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    draw(off, ty, true);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [targetKey, fw, height]);
  const text = lang === 'en'
    ? `Looks like ${minutes >= 60 ? `${Math.floor(minutes / 60)} h ${minutes % 60} min` : `${minutes} min`} already`
    : `Кажется, уже ${minutesRu(minutes)}`;
  return (
    <div className="pointer-events-none fixed inset-0 z-40 overflow-hidden" aria-live="polite">
      <div ref={botRef} className="absolute left-0 top-0 will-change-transform"
        style={{ width: fw, height: Math.round(height * ROBOT_FH), transformOrigin: `${BODY_C * 100}% 50%` }}>
        <RobotParts />
      </div>
      <div ref={sayRef} data-side="l" className="group absolute left-0 top-0">
        <div className={cn('relative origin-bottom-right whitespace-nowrap rounded-[1.4rem] bg-gradient-to-br from-sky-400 to-indigo-500 px-4 py-2 text-lg font-extrabold tracking-tight text-white shadow-lg shadow-indigo-500/30',
          say === null ? 'opacity-0' : say ? 'robot-say-in' : 'robot-say-out')}>
          {text}
          <span className="absolute -right-1.5 bottom-2 size-3.5 rotate-45 rounded-[3px] bg-indigo-500" />
        </div>
      </div>
    </div>
  );
}

// enterDelay (#2036/#2080): робот не материализуется, а выезжает из-за правого края экрана
// после смены сцены «проблемы → всё в порядке».
// Робот растёт вместе с карточками, а не живёт своим размером (Алексей 03.11 #8615: «при определённых
// масштабах робот оч большой и еле движется по половине экрана»): `height` — потолок, фактический рост
// — не больше 30 % высоты и 18 % ширины своей области (она уже в масштабе дашборда), но не меньше 40 px.
const ROBOT_MIN_H = 40;
function AllDoneRobot({ height: maxH, grow, enterDelay }: { height: number; grow?: boolean; enterDelay?: number }) {
  const { t } = useT();
  const boxRef = useRef<HTMLDivElement>(null);
  const botRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    // Порог 8 px — чтобы робот не перезапускал полёт на каждом пикселе ресайза.
    const ro = new ResizeObserver(() => setBox(b => {
      const w = el.clientWidth, h = el.clientHeight;
      return b && Math.abs(b.w - w) < 8 && Math.abs(b.h - h) < 8 ? b : { w, h };
    }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Без grow высота области сама равна росту робота — по ней не ограничиваем (иначе сжимался бы по кругу).
  const height = box
    ? Math.round(Math.max(ROBOT_MIN_H, Math.min(maxH, box.w * 0.18, grow ? box.h * 0.3 : Infinity)))
    : maxH;
  // Тап (#2043/#2045): по очереди — удар лбом в экран или виляние с «Привет!» в облачке.
  const tapReq = useRef(false);
  const sayRef = useRef<HTMLDivElement>(null);
  const [say, setSay] = useState<boolean | null>(null);
  const sayTimer = useRef<number | null>(null);
  const onTap = () => { tapReq.current = true; };
  const sayHi = () => {
    if (sayTimer.current != null) window.clearTimeout(sayTimer.current);
    setSay(true);
    sayTimer.current = window.setTimeout(() => setSay(false), 2400);
  };
  useEffect(() => () => { if (sayTimer.current != null) window.clearTimeout(sayTimer.current); }, []);
  const fw = Math.round(height * ROBOT_AR);
  useEffect(() => {
    const box = boxRef.current, bot = botRef.current;
    if (!box || !bot) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    // Пламя на месте — вниз, по оси струи (Алексей 03.11 #8629: «на месте пламя назад, а не вниз»).
    const rig = makeRig(bot, { flameDown: true });
    // Наклон корпуса и пламя — физика ранца по самому перемещению (Алексей 03.11 #8736): ускорение →
    // наклон и тяга, скорость → снос хвоста струи. p.tilt у частей — лёгкое покачивание поверх, у
    // нарисованных кадров (удар) — их собственный наклон.
    const jet = makeJet(fw / 320);
    const draw = (x: number, y: number, mirror: boolean, p: BotPose = {}, now = performance.now()) => {
      const k = p.k ?? 1;
      bot.style.transform = `translate(${x + (p.dx ?? 0)}px, ${y + (p.dy ?? 0) - height * ROBOT_PAD}px) scale(${(mirror ? -1 : 1) * k * (p.sx ?? 1)}, ${k * (p.sy ?? 1)})`;
      const j = jet(x, y, now, mirror);
      rig({ ...p, tilt: p.sprite ? p.tilt : j.tilt + (p.tilt ?? 0), flame: j.flame }, now);
      // Облачко — у головы, хвостиком к роботу; сторона — куда хватает места.
      const sb = sayRef.current;
      if (sb) {
        const bw = sb.offsetWidth;
        const right = x + BODY_R * fw + bw <= box.clientWidth;
        sb.dataset.side = right ? 'r' : 'l';
        sb.style.transform = `translate(${right ? x + BODY_R * fw - 2 : Math.max(0, x + BODY_L * fw + 2 - bw)}px, ${y + height * 0.06}px)`;
      }
    };
    if (reduce) { draw(box.clientWidth / 2 - BODY_C * fw, grow ? Math.max(0, (box.clientHeight - height) / 2) : 0, false); return; }
    // #1891: робот в основном висит на месте и моргает, изредка перелетает в другое место.
    // Висит 8–14 с (моргание каждые 2.5–4.5 с), потом выбирает случайную точку не ближе
    // четверти ширины, разворачивается к ней (если надо) и летит ~180 px/с, не короче 4.5 с.
    const rnd = (a: number, b: number) => a + Math.random() * (b - a);
    const bounds = () => {
      const W = box.clientWidth;
      const minX = -BODY_L * fw + 4;
      return { W, minX, maxX: Math.max(minX, W - BODY_R * fw - 4) };
    };
    let x = bounds().minX + (bounds().maxX - bounds().minX) * 0.15;
    let mirror = false;
    type St = { kind: 'hover'; until: number; nextBlink: number; blinkEnd: number }
      | { kind: 'fly'; t0: number; ms: number; from: number; to: number }
      | { kind: 'wave'; t0: number }
      | { kind: 'bonk'; t0: number };
    const startHover = (now: number): St => ({ kind: 'hover', until: now + rnd(8000, 14000), nextBlink: now + rnd(1200, 3000), blinkEnd: 0 });
    let taps = 0;
    let st: St = startHover(performance.now());
    if (enterDelay != null) {
      const { W, minX, maxX } = bounds();
      x = W - BODY_L * fw + 8; // целиком за правым краем
      mirror = true;           // летит влево — смотрит влево
      st = { kind: 'fly', t0: performance.now() + enterDelay, ms: 1800, from: x, to: minX + (maxX - minX) * 0.55 };
    }
    let raf = 0;
    const tick = (now: number) => {
      const { minX, maxX } = bounds();
      x = Math.min(Math.max(x, minX), maxX);
      // grow: посередине освободившейся области (по вертикали — центр).
      const y0 = grow ? Math.max(0, (box.clientHeight - height) / 2) : 0;
      const bobS = Math.sin(now / 1800 * 2 * Math.PI);
      // Покачивание по вертикали само даёт тягу: внизу толкает вверх — струя длиннее (через makeJet).
      const bob = y0 + bobS * height * 0.04;
      if (tapReq.current && st.kind !== 'fly' && st.kind !== 'wave') {
        tapReq.current = false;
        if (taps++ % 2 === 0) st = { kind: 'bonk', t0: now };
        else { st = { kind: 'wave', t0: now }; sayHi(); }
      }
      // Висит — едва покачивается, как в воздухе (±1°); не застывшая картинка.
      const idleTilt = Math.sin(now / 2300 * 2 * Math.PI);
      if (st.kind === 'bonk') {
        const pose = bonkPose(now - st.t0, height);
        if (pose) draw(x, bob, mirror, { ...pose, tilt: (pose.tilt ?? 0) + idleTilt * 0.75 }, now);
        else { st = startHover(now); draw(x, bob, mirror, { tilt: idleTilt }, now); }
      } else if (st.kind === 'wave') {
        const pose = wavePose(now - st.t0, height);
        if (pose) draw(x, bob, mirror, pose, now);
        else { st = startHover(now); draw(x, bob, mirror, { tilt: idleTilt }, now); }
      } else if (st.kind === 'hover') {
        if (now >= st.nextBlink) { st.blinkEnd = now; st.nextBlink = now + rnd(2500, 4500); }
        draw(x, bob, mirror, { tilt: idleTilt, blink: blinkAt(now, st.blinkEnd) }, now);
        if (now >= st.until && maxX - minX > 40) {
          let to = x;
          for (let k = 0; k < 8 && Math.abs(to - x) < (maxX - minX) * 0.25; k++) to = rnd(minX, maxX);
          const goLeft = to < x;
          // Разворот — мгновенное зеркало, без анимации (#2352).
          st = { kind: 'fly', t0: now, ms: Math.max(4500, (Math.abs(to - x) / 180) * 1000), from: x, to };
          if (goLeft !== mirror) mirror = goLeft;
        } else if (now >= st.until) {
          st = startHover(now);
        }
      } else {
        const u = Math.max(0, Math.min(1, (now - st.t0) / st.ms)), e = u * u * (3 - 2 * u);
        x = st.from + (st.to - st.from) * e;
        const v = 6 * u * (1 - u) / 1.5; // скорость smoothstep, 0…1 — полосы за спиной
        draw(x, bob, mirror, { speed: v, tilt: idleTilt * (1 - v) }, now);
        if (u >= 1) { st = startHover(now); tapReq.current = false; }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [fw, height, grow]);
  return (
    <div ref={boxRef} className={cn('relative overflow-hidden', grow ? 'min-h-0 flex-1' : 'shrink-0')} style={grow ? { minHeight: Math.round(ROBOT_MIN_H * 1.1) } : { height: Math.round(height * 1.1) }}>
      <div ref={botRef} data-robot role="button" tabIndex={-1} onClick={onTap} className="absolute left-0 top-0 cursor-pointer will-change-transform"
        style={{ width: fw, height: Math.round(height * ROBOT_FH), transformOrigin: `${BODY_C * 100}% 50%` }}>
        <RobotParts />
      </div>
      <div ref={sayRef} data-side="r" className="group pointer-events-none absolute left-0 top-0">
        <div className={cn('relative rounded-[1.4rem] bg-gradient-to-br from-sky-400 to-indigo-500 px-4 py-2 text-lg font-extrabold tracking-tight text-white shadow-lg shadow-indigo-500/30',
          'group-data-[side=l]:origin-bottom-right group-data-[side=r]:origin-bottom-left', say === null ? 'opacity-0' : say ? 'robot-say-in' : 'robot-say-out')}>
          {t('dashboard.robotHi')}
          <span className="absolute bottom-2 size-3.5 rotate-45 rounded-[3px] bg-indigo-500 group-data-[side=l]:-right-1.5 group-data-[side=r]:-left-1.5 group-data-[side=r]:bg-sky-500" />
        </div>
      </div>
    </div>
  );
}

// Problems-only mode, nothing wrong in this section: a quiet green note
// instead of photos — the section stays visible (so its health is still
// asserted) but cedes its screen share to the sections that DO have problems.
function AllOkBody({ text }: { text: string }) {
  const { t } = useT();
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-1.5 p-3 text-center">
      <CheckCircle2 className="size-6 text-success" />
      <p className="text-sm text-muted-foreground">{text || t('dashboard.filter.allOk')}</p>
    </div>
  );
}

// Per-card load failure: a neutral "couldn't load" line + a retry that re-runs
// just this card's request (the others are unaffected). No endpoint/technical
// wording — operators only see a plain retry. Пока наверху общая плашка о связи (#3224) — без текста,
// только тихий значок: «Не удалось загрузить» в каждой карточке раздражало.
function CardError({ onRetry }: { onRetry: () => void }) {
  const { t } = useT();
  const quiet = useGlobalNoticeActive();
  if (quiet) {
    return (
      <div role="status" aria-label={t('serverStatus.quiet')} className="flex min-h-0 flex-1 items-center justify-center p-4 text-muted-foreground/60">
        <RefreshCw className="size-4" aria-hidden />
      </div>
    );
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 p-4 text-center">
      <p className="text-xs text-muted-foreground">{t('dashboard.apiMissing')}</p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <RefreshCw className="size-3.5" />
        {t('common.retry')}
      </button>
    </div>
  );
}

// ── "Could appear" setup card ───────────────────────────────────────────────

// Per-category NEXT SETUP STEP. Deliberately NOT /management/general (Общие
// настройки) — the ask is to route to the category's own setup step.
const CATEGORY_SETUP: Record<string, { icon: React.ElementType; nameKey: string; href: string }> = {
  checklists: { icon: ListTodo, nameKey: 'management.plugin.checklists', href: '/management/checklists' },
  tables: { icon: UtensilsCrossed, nameKey: 'management.plugin.tables', href: '/management/zoning' },
  layout: { icon: ShoppingBasket, nameKey: 'management.plugin.layout', href: '/management/zoning' },
  queue: { icon: Users, nameKey: 'management.plugin.queue', href: '/management/zoning' },
  // Алексей 28.10 #3095: «не хватает Чеки, План продаж».
  receipts: { icon: Settings2, nameKey: 'features.receipts.name', href: '/management/receipts' },
  sales_plan: { icon: TrendingUp, nameKey: 'features.sales_plan.name', href: '/management/sales-plan' },
};

// Subtle, manager-only hint that more sections could be configured. Slim muted
// row — deliberately not a full card competing with the real data above it.
function CouldAppearHint({
  categories,
  search,
}: {
  categories: string[];
  search: string;
}) {
  const { t } = useT();
  if (categories.length === 0) return null;
  return (
    // По ширине текста, не на всю строку; после заголовка двоеточие, пункты через «·» (Алексей 28.10 #3113).
    <div className="inline-flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-1 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <Settings2 className="size-3.5 shrink-0" />
        {t('dashboard.couldAppear.title')}:
      </span>
      {categories.filter(key => CATEGORY_SETUP[key]).map((key, i) => {
        const def = CATEGORY_SETUP[key];
        return (
          <span key={key} className="flex items-center gap-1.5">
            {i > 0 && <span aria-hidden>·</span>}
            <Link to={{ pathname: def.href, search }} className="underline-offset-2 hover:text-foreground hover:underline">
              {t(def.nameKey)}
            </Link>
          </span>
        );
      })}
    </div>
  );
}

// ── Closed-hours banner ──────────────────────────────────────────────────────

// Slim, always-visible marker that the data below is end-of-shift. Replaces the
// old MoonStar hero that hid the data behind a spoiler — the shift data now
// renders by default; this just labels it.
function ClosedBanner({ opensAt }: { opensAt: string | null }) {
  const { t } = useT();
  const text = opensAt
    ? t('dashboard.closedBannerAt', { time: opensAt })
    : t('dashboard.closedBanner');
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
      <Moon className="size-4 shrink-0 text-muted-foreground/70" strokeWidth={1.75} />
      <span className="font-medium">{text}</span>
    </div>
  );
}

// ── Closed branch: day results ───────────────────────────────────────────────

// Филиал закрыт (Алексей 03.11 #8759/#8761): ночью доска с фото «1 ч назад» и «Чистота столов — Нет данных»
// читается как поломка. Вместо неё — спокойные итоги дня только из того, что уже отдают ручки дашборда:
// средний чек за день и чек-лист «выполнено X из Y». У столов, выкладки и очередей итога за день в ответах
// нет — одна тихая строка «день закрыт», без цифр. Главное сообщение — плашка «Филиал закрыт» сверху;
// робот остаётся, как при «всё выполнено».
function ClosedDayBoard({ zoneId, checklist, photoKeys, robot, row }: {
  zoneId: string;
  /** Чек-лист настроен в филиале. */
  checklist: boolean;
  /** Разделы с фото, которые есть в филиале (tables / layout / queue). */
  photoKeys: string[];
  robot: boolean;
  /** Портрет: робот сверху, итоги — полосой под ним. */
  row: boolean;
}) {
  const { t, lang } = useT();
  const dense = useCompactDash();
  const avgCheck = useContext(AvgCheckContext);
  // Все задачи дня одним запросом (обычный список отдаёт по 10).
  const { data: tasks } = useSWR<{ results: OpsTaskListItem[]; count?: number }>(
    checklist ? ['dashboard:closed-tasks', zoneId] : null,
    async () => (await listOpsTasks({ zoneId, onlyToday: true, pageSize: 200 })).data,
    DASH_SWR,
  );
  const list = tasks?.results ?? [];
  const total = Math.max(list.length, tasks?.count ?? 0);
  const done = list.filter(tk => tk.status === 'done').length;
  const overdue = list.filter(tk => tk.status === 'overdue').length;
  const day = (lang === 'en' ? WEEKDAY_PAST_EN : WEEKDAY_PAST_RU)[new Date().getDay()];
  const rub = (v: number) => money(`${Math.round(v).toLocaleString(lang === 'en' ? 'en-US' : 'ru-RU')} ₽`);
  const avg = avgCheck?.connected && avgCheck.avg_check != null ? avgCheck : null;
  const ch = avg?.change;
  const chPct = ch == null ? null : Math.round(ch * 100);
  const big = dense ? 'text-lg' : 'text-2xl';
  const card = cn(DASH_CARD, 'flex min-w-0 flex-col', dashCardBody(dense));
  const meta: Record<string, { Icon: React.ElementType; title: string }> = {
    tables: { Icon: UtensilsCrossed, title: t('dashboard.cards.tables') },
    layout: { Icon: ShoppingBasket, title: t('dashboard.cards.layout') },
    queue: { Icon: Users, title: t('dashboard.cards.queue') },
  };
  const photo = photoKeys.filter(k => meta[k]);
  const results = (
    <div className={cn('flex min-h-0 flex-col justify-center', dense ? 'gap-1.5' : 'gap-2.5',
      row ? 'w-full' : dense ? 'w-60 shrink-0' : 'w-80 shrink-0')}>
      <div className={cn('px-1 font-semibold uppercase tracking-wide text-muted-foreground', dense ? 'text-[10px]' : 'text-xs')}>
        {t('dashboard.closedDay.title')}
      </div>
      {avg && (
        <div className={card}>
          <CardHead icon={Receipt} title={t('dashboard.closedDay.avgTitle')} dense={dense} />
          <div className="flex items-baseline gap-2">
            <span className={cn('font-semibold tabular-nums', big)}>{rub(avg.avg_check!)}</span>
            {avg.count != null && avg.count > 0 && (
              <span className="text-xs text-muted-foreground">{t('dashboard.closedDay.checks', { n: avg.count })}</span>
            )}
          </div>
          {chPct != null && (
            <p className="text-xs leading-snug text-muted-foreground">
              <span className={cn('font-semibold tabular-nums', chPct > 0 ? 'text-success' : 'text-foreground')}>
                {chPct > 0 ? '+' : chPct < 0 ? '−' : '±'}{Math.abs(chPct)} %
              </span>{' '}
              {t('dashboard.closedDay.vsBaseline', { day })}
            </p>
          )}
        </div>
      )}
      {checklist && total > 0 && (
        <div className={card}>
          <CardHead icon={ListTodo} title={t('dashboard.cards.tasks')} dense={dense} />
          {done === 0 && overdue === 0 ? (
            // После полуночи «сегодня» — уже новый день: задачи только запланированы, не «0 из N».
            <p className="text-sm text-muted-foreground">{t('dashboard.closedDay.tasksPlanned', { n: total })}</p>
          ) : (
            <>
              <div className="flex items-baseline gap-1.5">
                <span className={cn('font-semibold tabular-nums', big)}>{done}</span>
                <span className="text-sm text-muted-foreground">{t('dashboard.closedDay.tasksOf', { total })}</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-success" style={{ width: `${Math.round((done / total) * 100)}%` }} />
              </div>
            </>
          )}
        </div>
      )}
      {photo.length > 0 && (
        <div className={cn(card, dense ? 'gap-1' : 'gap-1.5')}>
          {photo.map(k => {
            const { Icon, title } = meta[k];
            return (
              <div key={k} className={cn('flex min-w-0 items-center justify-between gap-2', dense ? 'text-[12px]' : 'text-sm')}>
                <span className="flex min-w-0 items-center gap-2 text-muted-foreground">
                  <Icon className={cn('shrink-0 text-muted-foreground/70', dense ? 'size-3.5' : 'size-4')} />
                  <span className="truncate">{title}</span>
                </span>
                <span className="shrink-0 text-xs text-muted-foreground/70">{t('dashboard.closedDay.dayClosed')}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
  return (
    <div className={cn('flex min-h-0 flex-1', dense ? 'gap-1.5' : 'gap-3', row && 'flex-col')}>
      {robot && <AllDoneRobot height={dense ? 84 : 110} grow />}
      {results}
    </div>
  );
}

// ── Mobile tab bar ───────────────────────────────────────────────────────────

// Phone-only: the dashboard shows ONE card at a time, switched via a bottom tab
// bar (Выкладка / Столы / Задачи / Очередь). A small tone dot on each icon shows
// that section's health without taking width; the active tab gets a top accent
// (underline-width, not a filled chip). iPads/desktop keep the at-a-glance grid.
const TAB_ORDER = ['sales_plan', 'tasks', 'layout', 'tables', 'queue'] as const;
const TAB_META: Record<string, { Icon: React.ElementType; labelKey: string }> = {
  layout: { Icon: ShoppingBasket, labelKey: 'dashboard.tab.layout' },
  tables: { Icon: UtensilsCrossed, labelKey: 'dashboard.tab.tables' },
  tasks: { Icon: ListTodo, labelKey: 'dashboard.tab.tasks' },
  queue: { Icon: Users, labelKey: 'dashboard.tab.queue' },
  sales_plan: { Icon: TrendingUp, labelKey: 'dashboard.tab.salesPlan' },
};

interface DashTab { key: string; label: string; Icon: React.ElementType; tone: OverallTone }

function DashboardTabBar({
  tabs,
  active,
  onSelect,
  onMeasure,
}: {
  tabs: DashTab[];
  active: string | null;
  onSelect: (key: string) => void;
  /** Reports the clearance (px) the section content must reserve so it never
   *  sits under this floating bar — its measured top-to-viewport-bottom span
   *  plus a small gap, instead of a hardcoded guess that missed on some
   *  devices / safe-area insets. */
  onMeasure?: (clearancePx: number) => void;
}) {
  // Floating, text-only tab strip — a translucent rounded pill detached from the
  // bottom edge (not glued). No icons; the active tab fills, the rest are faint;
  // a tiny tone dot flags each section's health without taking width.
  const navRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const el = navRef.current;
    if (!el || !onMeasure) return;
    const measure = () => {
      // The bar's own rendered height (border-box, incl. its safe-area
      // padding) IS the clearance the content must reserve — it's already
      // pinned to the viewport's bottom edge (fixed + bottom-0). Deriving it
      // instead from `window.innerHeight - rect.top` mixes in the LAYOUT
      // viewport height, which on mobile Safari/Chrome can disagree with the
      // dynamic (dvh) viewport the page is actually sized to while the
      // browser's own toolbar is showing/hiding — occasionally under-
      // reserving clearance so a card would render partly behind the bar.
      const clearance = el.getBoundingClientRect().height + 12;
      onMeasure(clearance);
      // Also published as a CSS var so the fullscreen photo lightbox (mounted
      // several component boundaries away, in ProofGrid/QueueCard/DashTaskItem)
      // can keep its caption clear of this floating bar without prop-drilling.
      document.documentElement.style.setProperty('--dashboard-tab-bar-clear', `${clearance}px`);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
      document.documentElement.style.removeProperty('--dashboard-tab-bar-clear');
    };
  }, [onMeasure, tabs.length]);
  return (
    <nav
      ref={navRef}
      className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center md:hidden"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
    >
      <div className="pointer-events-auto flex max-w-[calc(100vw-1.5rem)] items-center gap-0.5 rounded-full border border-border/40 bg-card/55 p-1 shadow-lg shadow-black/10 backdrop-blur-xl supports-[backdrop-filter]:bg-card/45">
        {tabs.map(tab => {
          const isActive = tab.key === active;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => onSelect(tab.key)}
              className={cn(
                'flex min-w-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors',
                isActive ? 'bg-foreground/10 text-foreground' : 'text-muted-foreground/70 hover:text-foreground',
              )}
              aria-current={isActive ? 'page' : undefined}
            >
              {tab.tone !== 'none' && (
                <span
                  className={cn('size-1.5 shrink-0 rounded-full', TONE_DOT[tab.tone], tab.tone === 'critical' && 'animate-pulse')}
                  aria-hidden
                />
              )}
              <span className="min-w-0 truncate">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// Mobile tab carousel that follows the FINGER. While a horizontal drag is in
// flight the neighbouring sections mount beside the active one in a 300%-wide
// strip that tracks the finger 1:1; on release it snaps to the next/prev tab
// (enough distance or a fast flick) or settles back. Tab-bar taps replay the
// same continuous slide. Transforms are driven imperatively on the strip (no
// per-move re-renders), and the ACTIVE pane keeps a stable position in the
// element tree, so engaging a drag never remounts the live section. Tabs
// cycle: dragging left on the last tab pulls in the first.
function SwipeTabCarousel({
  order,
  active,
  render,
  onCommit,
}: {
  /** Tab keys in bar order. */
  order: string[];
  active: string;
  render: (key: string) => React.ReactNode;
  onCommit: (key: string) => void;
}) {
  const n = order.length;
  const idx = Math.max(0, order.indexOf(active));
  const leftKey = n > 1 ? order[(idx - 1 + n) % n] : null;
  const rightKey = n > 1 ? order[(idx + 1) % n] : null;
  const activeNode = render(active);

  const boxRef = useRef<HTMLDivElement | null>(null);
  const stripRef = useRef<HTMLDivElement | null>(null);
  // Neighbours mounted (drag in flight or settling).
  const [neighbors, setNeighbors] = useState(false);
  // Programmatic (tab-bar tap) slide: the outgoing pane + the side it sits on.
  const [prog, setProg] = useState<{ key: string; node: React.ReactNode; side: 'left' | 'right' } | null>(null);
  const pendingRef = useRef<string | null>(null); // key committed by a drag
  const settleTimer = useRef<number | null>(null);
  const gest = useRef<{
    x: number;
    y: number;
    mode: 'pending' | 'drag' | 'off';
    off: number;
    lastOff: number;
    lastT: number;
    v: number;
  } | null>(null);

  // Detect an `active` change in render phase (same-component setState during
  // render is the sanctioned pattern). A drag-committed change lands silently
  // (the drag already animated); a tap replays the slide.
  const last = useRef<{ key: string; node: React.ReactNode }>({ key: active, node: activeNode });
  if (last.current.key !== active) {
    if (pendingRef.current === active) {
      pendingRef.current = null;
      if (neighbors) setNeighbors(false);
    } else if (n > 1) {
      const from = order.indexOf(last.current.key);
      setProg({
        key: last.current.key,
        node: last.current.node,
        // Went to a LATER tab → the new section enters from the right, so the
        // outgoing pane sits on the LEFT of the strip.
        side: from >= 0 && from > idx ? 'right' : 'left',
      });
      setNeighbors(false);
    }
    last.current = { key: active, node: activeNode };
  } else {
    last.current.node = activeNode; // keep the snapshot fresh
  }

  // Keep the strip parked on the CENTER pane whenever nothing is in flight.
  // (Transforms are imperative everywhere, so React never resets them for us.)
  useLayoutEffect(() => {
    const el = stripRef.current;
    if (el && !prog && !neighbors) {
      el.style.transition = 'none';
      el.style.transform = 'translateX(-33.333%)';
    }
  }, [prog, neighbors, active]);

  // Tap slide: park showing the outgoing pane, then ease to the center pane.
  useLayoutEffect(() => {
    if (!prog) return;
    const el = stripRef.current;
    if (!el) return;
    el.style.transition = 'none';
    el.style.transform = `translateX(${prog.side === 'left' ? '0%' : '-66.666%'})`;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        el.style.transition = 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)';
        el.style.transform = 'translateX(-33.333%)';
      });
    });
    const drop = window.setTimeout(() => setProg(null), 320);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(drop);
    };
  }, [prog?.key, active]);

  useEffect(
    () => () => {
      if (settleTimer.current) window.clearTimeout(settleTimer.current);
    },
    [],
  );

  const settle = (targetPx: number, key: string | null) => {
    const el = stripRef.current;
    if (el) {
      el.style.transition = 'transform 240ms cubic-bezier(0.22, 1, 0.36, 1)';
      el.style.transform = `translateX(calc(-33.333% + ${targetPx}px))`;
    }
    if (settleTimer.current) window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => {
      settleTimer.current = null;
      if (key) {
        pendingRef.current = key;
        onCommit(key);
      } else {
        setNeighbors(false);
      }
    }, 250);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length > 1 || n < 2) {
      gest.current = null;
      return;
    }
    if (neighbors || settleTimer.current) return; // still settling the previous one
    const t = e.touches[0];
    gest.current = t
      ? { x: t.clientX, y: t.clientY, mode: 'pending', off: 0, lastOff: 0, lastT: performance.now(), v: 0 }
      : null;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const g = gest.current;
    if (!g || g.mode === 'off') return;
    if (e.touches.length > 1) {
      // A pinch started mid-gesture: abandon the drag where it is.
      if (g.mode === 'drag') settle(0, null);
      gest.current = null;
      return;
    }
    const t = e.touches[0];
    if (!t) return;
    const dx = t.clientX - g.x;
    const dy = t.clientY - g.y;
    if (g.mode === 'pending') {
      if (Math.abs(dy) > 14 && Math.abs(dy) > Math.abs(dx) * 1.2) {
        g.mode = 'off'; // clearly vertical → page swipe / scroll owns it
        return;
      }
      if (!(Math.abs(dx) > 14 && Math.abs(dx) > Math.abs(dy) * 1.2)) return;
      g.mode = 'drag';
      setProg(null); // cut a tap slide short — the finger takes over
      setNeighbors(true);
    }
    const w = boxRef.current?.clientWidth || 1;
    const off = Math.max(-w, Math.min(w, dx));
    const now = performance.now();
    if (now > g.lastT) g.v = (off - g.lastOff) / (now - g.lastT);
    g.lastOff = off;
    g.lastT = now;
    g.off = off;
    const el = stripRef.current;
    if (el) {
      el.style.transition = 'none';
      el.style.transform = `translateX(calc(-33.333% + ${off}px))`;
    }
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const g = gest.current;
    gest.current = null;
    if (!g || g.mode !== 'drag') return;
    if (e.touches.length > 0) {
      settle(0, null); // other fingers still down → was a pinch
      return;
    }
    const w = boxRef.current?.clientWidth || 1;
    if (rightKey && (g.off < -w * 0.28 || (g.v < -0.35 && g.off < -30))) settle(-w, rightKey);
    else if (leftKey && (g.off > w * 0.28 || (g.v > 0.35 && g.off > 30))) settle(w, leftKey);
    else settle(0, null);
  };
  const onTouchCancel = () => {
    const g = gest.current;
    gest.current = null;
    if (g?.mode === 'drag') settle(0, null);
  };

  return (
    <div
      ref={boxRef}
      className="relative min-h-0 flex-1 overflow-hidden"
      // Let the browser keep vertical panning (scroll / pull-to-refresh);
      // horizontal movement is ours.
      style={{ touchAction: 'pan-y' }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchCancel}
    >
      <div
        ref={stripRef}
        className="flex h-full will-change-transform"
        style={{ width: '300%', transform: 'translateX(-33.333%)' }}
      >
        {/* px here (not on the outer wrapper — see its -mx cancel above)
            so each pane keeps its own left/right gutter: at rest it lines up
            with the old single-pane inset, but mid-drag the incoming/outgoing
            panes each bring their own padding to the seam instead of butting
            together with zero gap. */}
        <div className="flex h-full w-1/3 min-w-0 flex-col overflow-hidden px-3 md:px-4">
          {neighbors && leftKey ? render(leftKey) : prog?.side === 'left' ? prog.node : null}
        </div>
        <div className="flex h-full w-1/3 min-w-0 flex-col overflow-hidden px-3 md:px-4">{activeNode}</div>
        <div className="flex h-full w-1/3 min-w-0 flex-col overflow-hidden px-3 md:px-4">
          {neighbors && rightKey ? render(rightKey) : prog?.side === 'right' ? prog.node : null}
        </div>
      </div>
    </div>
  );
}

// ── Desktop board: content-weighted panels ───────────────────────────────────

// `resolved` = this section's summary API has actually answered. While false,
// the panel is sized from last-visit persisted counts (or a flat guess), so the
// board must be allowed ONE re-derive when the real data lands — otherwise a
// first visit mounts an even grid and the important panel stays squeezed in a
// corner forever (order/size are otherwise frozen between problem flips).
// `okLabel` = the encouragement shown over the panel when it flips
// problem→all-good ("Очереди в норме").
type DashSection = {
  key: string;
  hasProblems: boolean;
  resolved: boolean;
  okLabel: string;
  node: React.ReactNode;
  /** Всегда первым (слева сверху) и всегда картой, не плашкой: задачи с планом продаж (#1969). */
  pinned?: boolean;
};

// Normalize weights into shares, clamped so no panel collapses or monopolises.
/** Доля первой колонки из «NNNfr MMMfr». */
function sharesOf(cols: React.CSSProperties['gridTemplateColumns']): number | null {
  const m = typeof cols === 'string' ? /^(\d+)fr (\d+)fr$/.exec(cols) : null;
  if (!m) return null;
  const a = +m[1], b = +m[2];
  return a + b > 0 ? a / (a + b) : null;
}

function shares(weights: number[], min: number, max: number): number[] {
  const sum = weights.reduce((a, b) => a + b, 0) || 1;
  const clamped = weights.map(w => Math.min(max, Math.max(min, w / sum)));
  const total = clamped.reduce((a, b) => a + b, 0);
  return clamped.map(x => x / total);
}

// CALM panel order: re-sorting the board by a live weight reshuffled the cards
// on every 10s poll (a dirty count or rack status nudging the weight ranking
// past a neighbour). Policy: panel locations are CONSTANT for the whole visit —
// the order seeds when the screen opens (problems lead), corrects itself once
// per section when its summary FIRST answers (loading→data, so a board seeded
// from stale/absent persisted counts fixes itself), and re-seeds on section
// membership changes. Problem flips do NOT move panels anymore: operators keep
// their spatial memory ("очереди живут справа сверху"), and the section's
// status dot + per-tile flashes carry the alert signal.
function useStableBoardOrder(
  sections: DashSection[],
  weightOf: (key: string) => number,
): DashSection[] {
  const ref = useRef<{
    order: string[];
    resolved: Map<string, boolean>;
  } | null>(null);
  const st = ref.current;
  const keys = sections.map(s => s.key);

  let order: string[];
  const sameSet =
    !!st && st.order.length === keys.length && keys.every(k => st.order.includes(k));
  // A section's data landing is a one-shot event (resolved never goes back to
  // false for a mounted key), so this re-seeds at most once per section.
  const dataArrived =
    !!st && sections.some(s => s.resolved && st.resolved.get(s.key) === false);
  if (!st || !sameSet || dataArrived) {
    const prior = st ? new Map(st.order.map((k, i) => [k, i])) : new Map<string, number>();
    order = [...sections]
      .sort(
        (a, b) =>
          (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) ||
          (b.hasProblems ? 1 : 0) - (a.hasProblems ? 1 : 0) ||
          weightOf(b.key) - weightOf(a.key) ||
          (prior.get(a.key) ?? 99) - (prior.get(b.key) ?? 99),
      )
      .map(s => s.key);
  } else {
    order = st.order.filter(k => keys.includes(k));
  }

  // order is a pure function of (sections set, resolved states, prior order);
  // sig covers the inputs that can change it (sections is a fresh array each
  // render, so it can't be the dep).
  const sig = sections
    .map(s => `${s.key}:${s.resolved ? 1 : 0}`)
    .join(',');
  useEffect(() => {
    ref.current = {
      order,
      resolved: new Map(sections.map(s => [s.key, s.resolved])),
    };
  }, [sig]);

  const byKey = new Map(sections.map(s => [s.key, s]));
  return order.map(k => byKey.get(k)).filter((s): s is DashSection => !!s);
}

// Grid placement for the stable-ordered panels: one CSS grid with FLAT children
// (keyed by section, so a reorder REPOSITIONS — FLIP-animated — instead of
// remounting a nested-flex subtree). Track sizes follow the (flip-stable)
// weights via fr units. index 0 is the most prominent slot.
//  • 2 → weighted columns;
//  • 3 → index 0 spans the full-height left column, the other two stack right;
//  • 4 → 2×2, reading order, weighted columns + rows.
function boardGrid(
  ordered: DashSection[],
  weightOf: (key: string) => number,
  minShare?: number,
): { style: React.CSSProperties; place: React.CSSProperties[] } {
  const lo = (d: number) => (minShare != null ? Math.min(d, minShare) : d);
  const hi = (d: number) => (minShare != null ? Math.max(d, 1 - minShare) : d);
  const n = ordered.length;
  const w = ordered.map(s => weightOf(s.key));
  const fr = (x: number) => `${Math.round(x * 1000)}fr`;

  if (n <= 1) {
    // Single panel renders without the grid (see DesktopBoard early return);
    // this keeps the memo from computing NaN tracks before that return.
    return { style: {}, place: [{}] };
  }

  if (n === 2) {
    const [a, b] = shares([w[0], w[1]], lo(0.3), hi(0.7));
    return {
      style: { gridTemplateColumns: `${fr(a)} ${fr(b)}`, gridTemplateRows: '1fr' },
      place: [{ gridColumn: '1', gridRow: '1' }, { gridColumn: '2', gridRow: '1' }],
    };
  }

  if (n === 3) {
    const [wL, wR] = shares([w[0], w[1] + w[2]], lo(0.4), hi(0.62));
    const [r1, r2] = shares([w[1], w[2]], lo(0.32), hi(0.68));
    return {
      style: { gridTemplateColumns: `${fr(wL)} ${fr(wR)}`, gridTemplateRows: `${fr(r1)} ${fr(r2)}` },
      place: [
        { gridColumn: '1', gridRow: '1 / span 2' },
        { gridColumn: '2', gridRow: '1' },
        { gridColumn: '2', gridRow: '2' },
      ],
    };
  }

  // n === 4 — reading-order 2×2, но КАЖДЫЙ РЯД делится по своим весам (Алексей 28.10 #3105, Ратуша:
  // «задачи широко занимают место, у них много пустого снизу, а выкладка ужата»). Раньше колонки были
  // общими: ширина задач была привязана к ширине очереди под ними и не опускалась ниже 35 %. Теперь три
  // трека: x1 = меньшая из двух левых долей, x2 = разница, x3 = остаток; ряд с узкой левой частью берёт
  // x1 и x2+x3, с широкой — x1+x2 и x3.
  const [a1] = shares([w[0], w[1]], lo(0.25), hi(0.75));
  const [a2] = shares([w[2], w[3]], lo(0.25), hi(0.75));
  const [rT, rB] = shares([w[0] + w[1], w[2] + w[3]], lo(0.35), hi(0.65));
  const x1 = Math.min(a1, a2);
  const x2 = Math.abs(a1 - a2);
  const x3 = 1 - Math.max(a1, a2);
  // Ряд с бо́льшей левой долей (при равных — оба, x2 = 0) занимает x1+x2 слева и x3 справа.
  const cols = (wide: boolean) => (wide ? ['1 / span 2', '3'] : ['1', '2 / span 2']);
  const [l1, r1] = cols(a1 >= a2);
  const [l2, r2] = cols(a2 >= a1);
  return {
    style: { gridTemplateColumns: `${fr(x1)} ${fr(x2)} ${fr(x3)}`, gridTemplateRows: `${fr(rT)} ${fr(rB)}` },
    place: [
      { gridColumn: l1, gridRow: '1' },
      { gridColumn: r1, gridRow: '1' },
      { gridColumn: l2, gridRow: '2' },
      { gridColumn: r2, gridRow: '2' },
    ],
  };
}

// All sections on one screen, each panel sized to its CONTENT weight (a section
// with many monitored units gets the screen share its photos need, so tiles
// come out roughly the same size everywhere) and a flip-stable ORDER.
//
// The board is a STATIC grid between flips: the grid template + per-panel
// placement are memoised on the problem-state signature, so across the 10s
// polls the inline styles are byte-identical and React mutates nothing — the
// panels literally cannot move or resize. They change only when a section flips
// good↔bad; that one change resizes smoothly via a CSS grid-track transition
// (no JS measurement → no animation-feedback loop). A reorder on a flip is a
// keyed move (snaps to the new cell) — rare and intended.
// Section-level celebration: when a RESOLVED section flips problem→all-good,
// float its okLabel ("Очереди в норме") with a done tick over the panel for a
// few seconds. Fires only on an observed transition — never on load, never
// while a section is still on persisted-guess data.
function useSectionOkFlash(sections: DashSection[]): Set<string> {
  const prev = useRef(new Map<string, boolean>());
  const [flashing, setFlashing] = useState<Set<string>>(() => new Set());
  const sig = sections.map(s => `${s.key}:${s.resolved ? 1 : 0}${s.hasProblems ? 1 : 0}`).join(',');
  useEffect(() => {
    const fixed: string[] = [];
    for (const s of sections) {
      if (!s.resolved) continue;
      const was = prev.current.get(s.key);
      prev.current.set(s.key, s.hasProblems);
      if (was === true && !s.hasProblems) fixed.push(s.key);
    }
    if (!fixed.length) return;
    setFlashing(cur => new Set([...cur, ...fixed]));
    const timer = window.setTimeout(
      () => setFlashing(cur => {
        const next = new Set(cur);
        fixed.forEach(k => next.delete(k));
        return next;
      }),
      6100,
    );
    return () => window.clearTimeout(timer);
  }, [sig]);
  return flashing;
}

// Рисующаяся галка: круг, потом «птичка» (CSS .ok-check*, globals.css).
function AnimatedCheck({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 52 52" className={cn('ok-check text-green-500', className)} aria-hidden>
      <circle cx="26" cy="26" r="24" className="fill-green-500/10" />
      <circle className="ok-check-circle" cx="26" cy="26" r="24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" transform="rotate(-90 26 26)" />
      <path className="ok-check-mark" d="M15 27 l7 7 l15 -16" fill="none" stroke="currentColor" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const CELEBRATE_KEY: Record<string, string> = {
  tables: 'dashboard.celebrate.tables',
  layout: 'dashboard.celebrate.layout',
  queue: 'dashboard.celebrate.queue',
  tasks: 'dashboard.celebrate.tasks',
};

// Секция только что стала «всё хорошо» (Алексей 20.10 #1639/#1645/#1651): не окно поверх
// карточки, а сама карточка — её тело на 6 с сменяется галкой и «Выкладка в порядке»
// (заголовок карточки остаётся). Какие секции празднуют — из DesktopBoard через контекст.
const CelebrateContext = React.createContext<Set<string>>(new Set());
// Проба для плашек «в норме» (#2061): доска говорит панели «ширина станет ×k», панель отвечает,
// во сколько раз от этого изменится размер её плиток (1 — не изменится: по бокам и так пусто).
type ScaleProbe = { k: number; report: (scale: number | null) => void };
const SlackContext = React.createContext<ScaleProbe | null>(null);
// Спрос панели на ширину (#2100): «чтобы фото заняли высоту и ширину без пустых полей, моей
// ячейке нужна ширина ×r» (r > 1 — фото упёрлись в ширину и сверху/снизу пусто, r < 1 — по
// бокам пусто). Доска двух колонок по этим r делит ширину между панелями.
const DemandContext = React.createContext<((r: number | null) => void) | null>(null);
// Ряд задач в колонке из двух панелей подгоняется под длину списка: список сообщает, сколько px у
// него лишних (+) или не хватает (−), доска двигает границу рядов (Алексей 28.10 #3130: «столы
// ограничены по высоте, у задач есть свободное место по высоте, классик»).
const RowFitContext = React.createContext<((slackPx: number | null) => void) | null>(null);

function InCardCelebrate({ sectionKey }: { sectionKey: string }) {
  const { t } = useT();
  const key = CELEBRATE_KEY[sectionKey];
  if (!key) return null;
  return (
    <div className="ok-overlay pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-card text-card-foreground">
      <AnimatedCheck className="size-14 md:size-16" />
      <span className="ok-rise text-center text-base font-semibold md:text-lg">{t(key)}</span>
    </div>
  );
}

function useCelebrating(sectionKey?: string): boolean {
  const keys = React.useContext(CelebrateContext);
  return !!sectionKey && keys.has(sectionKey);
}

// После перезагрузки по кнопке «обновите страницу» (Алексей 20.10 #1639): по центру
// «Обновлено успешно» и большая кнопка «Развернуть на весь экран» — перезагрузка в Chrome
// всегда выходит из полноэкранного, а вернуть его браузер даёт только по нажатию.
// Снаружи увеличенного блока дашборда — своего обратного zoom не нужно (#1901).
function JustUpdatedOverlay({ onFullscreen, onDismiss }: { onFullscreen: () => void; onDismiss: () => void }) {
  const { t } = useT();
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('dashboard.updated')}
      // Низкий экран (#2065): окно прокручивается внутри экрана, а не обрезается; стиль — как у
      // обычных диалогов (заголовок text-lg semibold, без «жирно-чёрного» крупного текста).
      className="fixed inset-0 z-50 flex overflow-y-auto bg-black/40 p-4 backdrop-blur-sm"
    >
      <div className="m-auto flex w-full max-w-sm flex-col items-center gap-3 rounded-xl border bg-card p-6 text-center text-card-foreground shadow-lg [@media(max-height:480px)]:gap-2 [@media(max-height:480px)]:p-4">
        <AnimatedCheck className="size-14 [@media(max-height:480px)]:size-10" />
        <h2 className="ok-rise text-lg font-semibold leading-tight">{t('dashboard.updated')}</h2>
        <Button type="button" onClick={onFullscreen} size="lg" className="mt-1 h-12 w-full gap-2 text-base">
          <Maximize2 className="size-5" />
          {t('dashboard.updatedFullscreen')}
        </Button>
        <button type="button" onClick={onDismiss} className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
          {t('dashboard.updatedDismiss')}
        </button>
      </div>
    </div>
  );
}

function DesktopBoard({
  sections,
  weightOf,
  problemsOnly = false,
  onSlack,
  probeK,
}: {
  sections: DashSection[];
  weightOf: (key: string) => number;
  problemsOnly?: boolean;
  onSlack?: (key: string, scale: number | null, k: number) => void;
  probeK?: number;
}) {
  const ordered = useStableBoardOrder(sections, weightOf);
  const onSlackRef = useRef(onSlack);
  onSlackRef.current = onSlack;
  const probes = useRef(new Map<string, ScaleProbe>());
  const probeFor = (key: string): ScaleProbe | null => {
    if (!onSlack || probeK == null) return null;
    const p = probes.current.get(key);
    if (p && p.k === probeK) return p;
    const k = probeK;
    const n = { k, report: (scale: number | null) => onSlackRef.current?.(key, scale, k) };
    probes.current.set(key, n);
    return n;
  };
  const okFlash = useSectionOkFlash(sections);
  const dense = useCompactDash();
  const boardGap = dense ? 'gap-1.5' : 'gap-3 md:gap-4';
  // Keys+resolved-state fully determine the layout — problem flips no longer
  // move OR resize panels (spatial memory wins; the dot + tile flashes carry
  // the signal). The signature changes only on membership change / a section's
  // summary landing (which corrects the persisted-count guess the panel was
  // mounted with).
  // «Только проблемы»: секция «в норме» — узкая плашка, с проблемой — полноразмерная панель
  // с фото, поэтому здесь флип хорошо↔плохо ОБЯЗАН пересчитать сетку (AI310, Алексей 20.10:
  // «layout карточек не пересчитался при приходе нового фото, фото упало оч маленьким»).
  const layoutKey =
    ordered.map(s => `${s.key}:${s.resolved ? 1 : 0}${problemsOnly ? (s.hasProblems ? 'p' : 'o') : ''}`).join('|') +
    `#${ordered.length}`;
  // ordered/weightOf are fresh each render; layoutKey captures everything that
  // can change the computed grid, so the memo holds the styles byte-stable
  // across polls (exhaustive-deps is unconfigured in this repo).
  // «Только проблемы»: здоровая секция — маленькая карточка «… в норме» (иконка и заголовок
  // те же), ей хватает узкой ячейки; минимальная доля трека падает до 12 % (Алексей 20.10 #1635).
  const grid = useMemo(() => boardGrid(ordered, weightOf, problemsOnly ? 0.12 : undefined), [layoutKey, problemsOnly]);

  // Две колонки (#2100: «у одной пустые поля по ширине, у второй по высоте и она сжата»): доли
  // ширины подгоняются под спрос панелей с фото (DemandContext). Каждая панель меряет спрос при
  // текущей доле → новая доля ∝ доля × r; пока колонки едут (transition 500 мс), отчёты не берём.
  const [demands, setDemands] = useState<Record<string, number>>({});
  const [colShare, setColShare] = useState<number | null>(null);
  const settleUntil = useRef(0);
  const demandFns = useRef(new Map<string, (r: number | null) => void>());
  const demandFor = (key: string) => {
    let f = demandFns.current.get(key);
    if (!f) {
      f = (r: number | null) => {
        if (r != null && performance.now() < settleUntil.current) return;
        setDemands(d => {
          if (r == null) {
            if (!(key in d)) return d;
            const n = { ...d };
            delete n[key];
            return n;
          }
          return d[key] === r ? d : { ...d, [key]: r };
        });
      };
      demandFns.current.set(key, f);
    }
    return f;
  };
  useEffect(() => { setColShare(null); setDemands({}); }, [layoutKey]);
  const pair = ordered.length === 2 ? ordered : null;
  const rA = pair ? demands[pair[0].key] : undefined;
  const rB = pair ? demands[pair[1].key] : undefined;
  useEffect(() => {
    if (!pair || rA == null || rB == null) return;
    const lo = problemsOnly ? 0.12 : 0.3;
    const cur = colShare ?? sharesOf(grid.style.gridTemplateColumns);
    if (cur == null) return;
    const a = cur * rA;
    const b = (1 - cur) * rB;
    const next = Math.min(1 - lo, Math.max(lo, a / (a + b)));
    if (Math.abs(next - cur) < 0.03) return;
    settleUntil.current = performance.now() + 700;
    setDemands({});
    setColShare(Math.round(next * 100) / 100);
  }, [rA, rB]);
  // Три панели: справа две друг над другом. Если одна из них — задачи, граница рядов следует за
  // длиной списка задач (лишнее место — соседу с фото, нехватка — забираем у него до 70 %).
  const gridRef = useRef<HTMLDivElement>(null);
  const tasksRow = ordered.length === 3 ? ordered.findIndex((s, i) => i > 0 && s.key === 'tasks') : -1;
  const [rowShare, setRowShare] = useState<number | null>(null);
  const rowSettle = useRef(0);
  useEffect(() => { setRowShare(null); }, [layoutKey]);
  const rowFit = useMemo(() => (tasksRow < 0 ? null : (slack: number | null) => {
    if (slack == null || performance.now() < rowSettle.current || Math.abs(slack) < 12) return;
    const H = (gridRef.current?.clientHeight ?? 0) - 16;
    if (H <= 0) return;
    setRowShare(cur => {
      const base = cur ?? (() => { const f = sharesOf(grid.style.gridTemplateRows); return f == null ? null : tasksRow === 1 ? f : 1 - f; })();
      if (base == null) return cur;
      const next = Math.min(0.7, Math.max(0.18, (base * H - slack) / H));
      if (Math.abs(next - base) < 0.02) return cur;
      rowSettle.current = performance.now() + 700;
      return Math.round(next * 100) / 100;
    });
  }), [tasksRow, layoutKey]);
  let style = pair && colShare != null
    ? { ...grid.style, gridTemplateColumns: `${Math.round(colShare * 1000)}fr ${Math.round((1 - colShare) * 1000)}fr` }
    : grid.style;
  if (tasksRow > 0 && rowShare != null) {
    const top = tasksRow === 1 ? rowShare : 1 - rowShare;
    style = { ...style, gridTemplateRows: `${Math.round(top * 1000)}fr ${Math.round((1 - top) * 1000)}fr` };
  }


  if (ordered.length <= 1) {
    return (
      <CelebrateContext.Provider value={okFlash}>
      <div className={cn('flex min-h-0 flex-1 flex-col', boardGap)}>
        <div className="relative flex min-h-0 flex-1 flex-col">
          {ordered[0] && <SlackContext.Provider value={probeFor(ordered[0].key)}>{ordered[0].node}</SlackContext.Provider>}
        </div>
      </div>
      </CelebrateContext.Provider>
    );
  }

  return (
    <CelebrateContext.Provider value={okFlash}>
    <div className={cn('flex min-h-0 flex-1 flex-col', boardGap)}>
      <div
        ref={gridRef}
        className={cn('grid min-h-0 min-w-0 flex-1', boardGap)}
        style={{
          ...style,
          transition: 'grid-template-columns 500ms ease, grid-template-rows 500ms ease',
        }}
      >
        {ordered.map((s, i) => (
          <div key={s.key} className="relative min-h-0 min-w-0 overflow-hidden" style={grid.place[i]}>
            <SlackContext.Provider value={probeFor(s.key)}>
              <DemandContext.Provider value={ordered.length === 2 ? demandFor(s.key) : null}>
                <RowFitContext.Provider value={s.key === 'tasks' ? rowFit : null}>{s.node}</RowFitContext.Provider>
              </DemandContext.Provider>
            </SlackContext.Provider>
          </div>
        ))}
      </div>
    </div>
    </CelebrateContext.Provider>
  );
}

const DASH_MIN_W = 640, DASH_MIN_H = 330;
function dashMaxZoom(): number {
  if (typeof window === 'undefined') return 1.5;
  return Math.max(1, Math.floor(Math.min(1.5, window.innerWidth / DASH_MIN_W, window.innerHeight / DASH_MIN_H) * 20) / 20);
}

// Last-known board counts per zone — lets the next visit mount the desktop
// panels at (close to) their final proportions before any summary responds.
interface PersistedCounts {
  items?: Record<string, number>;
  problems?: Record<string, number>;
}

function readPersistedCounts(zoneId: string | null): PersistedCounts {
  if (!zoneId) return {};
  try {
    return JSON.parse(localStorage.getItem(`dash:counts:${zoneId}`) ?? '{}') as PersistedCounts;
  } catch {
    return {};
  }
}

// ── Multi-branch landing ────────────────────────────────────────────────────

// A single clean "pick a branch first" prompt — replaces the old state where
// every card rendered its own "select a branch" warning hint (felt broken).
// The branch is picked via the existing top selector; this is just one tidy
// prompt instead of a wall of warning signs.
// ── Page ────────────────────────────────────────────────────────────────────

// Экран не гаснет, пока открыт дашборд смены (Алексей 23.10, #2065: «гаснет экран по таймауту»).
// Screen Wake Lock: браузер сам отпускает его, когда вкладка скрыта, — берём заново при возврате.
function useScreenWakeLock() {
  useEffect(() => {
    type Lock = { release: () => Promise<void>; released?: boolean };
    const wl = (navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<Lock> } }).wakeLock;
    if (!wl) return;
    let lock: Lock | null = null;
    let alive = true;
    const take = () => {
      if (document.visibilityState !== 'visible' || (lock && !lock.released)) return;
      wl.request('screen').then(l => { if (alive) lock = l; else void l.release(); }).catch(() => { /* нет разрешения/батарея */ });
    };
    take();
    document.addEventListener('visibilitychange', take);
    // Система отпускает блокировку и сама (экономия заряда) — проверяем раз в минуту (#2393).
    const retry = window.setInterval(take, 60_000);
    // Safari не даёт без жеста пользователя — пробуем ещё раз на первом касании.
    document.addEventListener('pointerdown', take, { once: true });
    return () => {
      alive = false;
      window.clearInterval(retry);
      document.removeEventListener('visibilitychange', take);
      document.removeEventListener('pointerdown', take);
      void lock?.release().catch(() => {});
    };
  }, []);
}

export default function WorkerDashboardPage() {
  useTelegramAutoLogin();
  useScreenWakeLock();
  const remind = useProblemReminders();
  const { t } = useT();
  const { zoneId, currentZone, zones } = useCurrentZone();
  const { data: avgCheck } = useSWR<ReceiptsTodayDto>(
    zoneId ? ['dashboard:avg-check', zoneId] : null,
    async () => (await getReceiptsToday(zoneId!)).data,
    { revalidateOnFocus: false, refreshInterval: 60_000, shouldRetryOnError: false },
  );
  // «Все филиалы» mode: render the portfolio board instead of the per-branch
  // cards. Checked after all hooks have fired (rules of hooks) — see the early
  // return next to the access gate below.
  const { allBranches } = useAppState().zone;
  const openZone = useOpenZone();
  // Managers/admins (zone_admin/zone_manager/org_owner/branch_manager/…) see the
  // setup affordances — not just superusers. Called unconditionally to keep the
  // hook order stable across the access-denied early return below.
  const canConfigure = useCan('zone.config.edit', zoneId);
  const search = typeof window !== 'undefined' ? window.location.search : '';
  usePageTitle(t('dashboard.title'), t('dashboard.subtitle'));
  // «На весь экран» (Алексей 20.10, #1610/#1611): дашборд на весь планшет — без
  // сайдбара и шапки + браузерный fullscreen. Плавающая кнопка в углу, та же
  // кнопка (или Esc) возвращает обратно.
  const { kiosk, toggleKiosk, setKiosk } = useKiosk();
  // Обновление фронта: кнопка в панели вместо самовольной перезагрузки (Алексей 20.10 #1639).
  const updateReady = useUpdateReady();
  const [justUpdated, setJustUpdated] = useState<boolean>(() => consumeJustUpdated());
  // Масштаб = системный масштаб браузера (Алексей 20.10: «кнопка масштаб системный масштаб
  // браузера меняла и всё»). Страница не может крутить настоящий Ctrl+/−, ближайший
  // эквивалент — CSS zoom на <html> (Chrome/Safari/Firefox 126+), шаг 10 %, 70–150 %.
  // Запоминается на этом устройстве (Алексей 28.10 #3107: «настроил масштаб, обновил страницу —
  // бац, он исчез»; было наоборот по #1884 от 22.10). Ключ новый: старое значение из до-#1884
  // времён ('dashboard:zoom') по-прежнему стираем.
  // Ключ — пользователь на этом устройстве (Алексей 28.10 #3117: «юзеру-устройству в целом одинаковый
  // масштаб»): все филиалы и перезагрузки — один масштаб.
  const zoomKey = `dash:zoom:${getCurrentUsername() ?? ''}`;
  const [zoomPref, setZoomRaw] = useState<number>(() => {
    try {
      const v = Number(localStorage.getItem(zoomKey) ?? localStorage.getItem('dash:zoom'));
      return v >= 0.7 && v <= 1.5 ? v : 1;
    } catch { return 1; }
  });
  const setZoom: React.Dispatch<React.SetStateAction<number>> = (v) => setZoomRaw(prev => {
    const next = typeof v === 'function' ? v(prev) : v;
    try { localStorage.setItem(zoomKey, String(next)); } catch { /* приватный режим */ }
    return next;
  });
  // Ширина нижней панели масштаба — подсказка «Что ещё…» встаёт с ней в одну строку слева (#3100).
  const [barEl, barRef] = useState<HTMLDivElement | null>(null);
  const [barW, setBarW] = useState(0);
  useEffect(() => {
    if (!barEl) { setBarW(0); return; }
    const measure = () => setBarW(barEl.offsetWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(barEl);
    return () => ro.disconnect();
  }, [barEl]);
  useEffect(() => { try { localStorage.removeItem('dashboard:zoom'); } catch { /* ignore */ } }, []);
  // Потолок масштаба по экрану (Алексей 03.11 #8615: «при увеличении карточки вылезают за пределы экрана»):
  // в увеличенном дашборде должно оставаться не меньше DASH_MIN_W×DASH_MIN_H CSS-пикселей — иначе
  // 2×2 панели физически не вмещают даже одну задачу и одно фото. Сохранённый масштаб не трогаем:
  // на экране побольше (поворот, планшет) он вернётся сам. 100 % доступны всегда.
  const [maxZoom, setMaxZoom] = useState(dashMaxZoom);
  useEffect(() => {
    const onResize = () => setMaxZoom(dashMaxZoom());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const zoom = Math.min(zoomPref, maxZoom);
  const zoomCap = Math.min(1.5, maxZoom);
  const stepZoom = (d: number) => setZoom(z => Math.round(Math.min(zoomCap, Math.max(0.7, Math.min(z, maxZoom) + d)) * 100) / 100);
  // Смена масштаба (#2150 «фотки дёргаются, хотя размер не меняется»): плитки пересчитываются в
  // новых CSS-пикселях и ехали анимацией 300 мс. На время смены анимации раскладки выключены.
  const [zooming, setZooming] = useState(false);
  const firstZoom = useRef(true);
  useEffect(() => {
    if (firstZoom.current) { firstZoom.current = false; return; }
    setZooming(true);
    const id = window.setTimeout(() => setZooming(false), 700);
    return () => window.clearTimeout(id);
  }, [zoom]);
  // Панель масштаба/полного экрана гаснет через 5 с без касаний (Алексей 20.10 #1631) —
  // теперь и вне полноэкранного режима: там она «отказывалась исчезать» (#1880 п.3).
  // Тап в пустое место переключает: видна → прячется сразу, спрятана → появляется.
  // Кнопка «спрятать» на самой панели делает то же, не дожидаясь таймаута.
  // Робот «всё выполнено» прячется кнопкой в панели — и это запоминается (Алексей 22.10, #1889/#1890).
  const [robotHidden, setRobotHiddenState] = useState<boolean>(() => {
    try { return localStorage.getItem('dash:robotHidden') === '1'; } catch { return false; }
  });
  const setRobotHidden = (v: boolean) => {
    setRobotHiddenState(v);
    try { localStorage.setItem('dash:robotHidden', v ? '1' : '0'); } catch { /* private mode */ }
  };
  const [barShown, setBarShown] = useState(true);
  const barShownRef = useRef(true);
  const barTimer = useRef<number | null>(null);
  const setBar = (v: boolean) => { barShownRef.current = v; setBarShown(v); };
  const hideBar = () => {
    if (barTimer.current != null) window.clearTimeout(barTimer.current);
    setBar(false);
  };
  const pokeBar = () => {
    setBar(true);
    if (barTimer.current != null) window.clearTimeout(barTimer.current);
    barTimer.current = window.setTimeout(() => setBar(false), 5000);
  };
  useEffect(() => {
    pokeBar();
    const onTap = (e: PointerEvent) => {
      const el = e.target as HTMLElement | null;
      if (el?.closest('[data-dash-bar], [data-robot], button, a, input, select, [role="button"], [role="dialog"], img, video')) return;
      if (barShownRef.current) hideBar(); else pokeBar();
    };
    document.addEventListener('pointerdown', onTap);
    return () => {
      document.removeEventListener('pointerdown', onTap);
      if (barTimer.current != null) window.clearTimeout(barTimer.current);
    };
  }, []);
  // Phone = one card at a time + a bottom tab bar; iPad/desktop = the at-a-glance
  // grid. 767px is the md breakpoint boundary (Tailwind md = 768px).
  const isMobile = useMediaQuery('(max-width: 767px)');
  const denseDash = useCompactDash();
  // Панель (масштаб / на весь экран / обновление) — только на горизонтальных экранах, не на
  // телефоне и не в портрете (Алексей 20.10 #1653).
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const showControlPanel = !isMobile && isLandscape;
  // Без панели кнопку «обновите страницу» не нажать → обновляемся тихо (на телефоне нечего
  // терять: полноэкранного режима там нет). Телефон в полноэкранном (остался с прошлого раза)
  // выводим из него — кнопки выхода на телефоне больше нет.
  // Кнопка «Пришли обновления» нужна только чтобы не выбить из полноэкранного (Алексей
  // 22.10, #1909): вне полноэкранного дашборд обновляется сам, как остальные страницы.
  const updateButton = updateReady && showControlPanel && kiosk;
  useEffect(() => {
    if (!updateReady || updateButton) return;
    const safe = () => document.visibilityState === 'visible' && !document.querySelector('[role="dialog"]');
    if (safe()) { window.location.reload(); return; }
    const id = window.setInterval(() => { if (safe()) window.location.reload(); }, 15_000);
    return () => window.clearInterval(id);
  }, [updateReady, updateButton]);
  useEffect(() => { if (isMobile && kiosk) setKiosk(false); }, [isMobile, kiosk]);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  // Measured clearance (px) under the floating tab bar — set by the bar itself
  // (see DashboardTabBar onMeasure) so the section never sits under it,
  // regardless of device font scale / safe-area inset.
  const [tabBarClear, setTabBarClear] = useState(0);
  // Crops at once (with full-on-hold) or full frames at once. Defaults to CROP
  // — the justified layout tiles mixed crop aspects tightly, and the
  // client-side crops are cut from the freshest frame, so crops are now the
  // denser AND fresher view. The choice sticks per device.
  const [displayMode, setDisplayModeState] = useState<DisplayMode>(() => {
    try {
      const saved = localStorage.getItem('dash:displayMode');
      return saved === 'full' || saved === 'crop' ? saved : 'crop';
    } catch {
      return 'crop';
    }
  });
  const setDisplayMode = (m: DisplayMode) => {
    setDisplayModeState(m);
    try { localStorage.setItem('dash:displayMode', m); } catch { /* quota/private mode */ }
  };
  // "Только проблемы": sections show only their problem units; healthy sections
  // collapse to a quiet "all clear" note (and give up their screen share).
  // Запоминается на устройстве: автообновление после нового билда не должно сбрасывать
  // режим (Алексей 20.10 #1633).
  const [problemsOnly, setProblemsOnlyState] = useState<boolean>(() => {
    try { return localStorage.getItem('dash:problemsOnly') === '1'; } catch { return false; }
  });
  const setProblemsOnly = (v: boolean) => {
    setProblemsOnlyState(v);
    try { localStorage.setItem('dash:problemsOnly', v ? '1' : '0'); } catch { /* private mode */ }
  };
  // The view controls (crop/full switch + problems filter). Desktop: centered
  // in the top bar. Mobile: there's no room in the bar, so they go into the
  // (i) popup via `extra` instead of being dropped.
  const viewControlsProps: ViewControlsProps = {
    mode: displayMode,
    onMode: setDisplayMode,
    problemsOnly,
    onProblemsOnly: setProblemsOnly,
  };
  // No branch picked (or «Все филиалы» active) → the crop/full + problems
  // controls are meaningless and must not render in the header at all.
  const showViewControls = !!zoneId && !allBranches;
  usePageHeaderSlots(
    {
      center: showViewControls && !isMobile ? <DashboardViewControls {...viewControlsProps} /> : null,
      extra: showViewControls && isMobile ? <DashboardViewControlsField {...viewControlsProps} /> : null,
    },
    [isMobile, displayMode, problemsOnly, showViewControls],
  );
  const sectionMode: DisplayMode = displayMode;

  // Access gate: if the requested zone exists but isn't in the user's
  // visible zones list, the dashboard renders a hard 404 instead of
  // the per-card content (decision deferred to the return — we still
  // call the hooks below to keep render-count stable). When denied,
  // we ALSO null out the SWR keys so no requests fire — the BE 404
  // responses (which are correct) were piling up in the console as
  // useless network noise.
  const accessDenied = !!(zoneId && zones.length > 0 && !currentZone);
  const liveZoneId = accessDenied ? null : zoneId;

  // Page-level SWR — same cache keys as the cards; SWR deduplicates to one
  // network request each. We read these to derive configuration status for
  // the teaser section and welcome card.
  const { data: tablesData, isLoading: tablesLoading, error: tablesError } = useSWR<TablesSummary>(
    liveZoneId ? ['dashboard:tables', liveZoneId] : null,
    async () => (await getTablesSummary(liveZoneId!, { limit: 0 })).data,
    DASH_SWR,
  );
  const { data: layoutData, isLoading: layoutLoading, error: layoutError } = useSWR<LayoutSummary>(
    liveZoneId ? ['dashboard:layout', liveZoneId] : null,
    async () => (await getLayoutSummary(liveZoneId!)).data,
    DASH_SWR,
  );
  const { data: queueData, isLoading: queueLoading, error: queueError } = useSWR<QueueSummary>(
    liveZoneId ? ['dashboard:queues', liveZoneId] : null,
    async () => holdQueueProblems(liveZoneId!, (await getQueueSummary(liveZoneId!)).data),
    DASH_SWR,
  );
  // Pull all checklists (with their task items) so the "has actual tasks" gate
  // below isn't fooled by an empty first checklist masking a populated second.
  const { data: checklistsData, isLoading: checklistsLoading } = useSWR<Paginated<ChecklistDto>>(
    liveZoneId ? ['dashboard:checklists-status', liveZoneId] : null,
    async () => (await listChecklists(liveZoneId!, 100)).data,
    DASH_SWR,
  );
  // Page-level tasks SWR (same key as MyTasksCard — SWR deduplicates to one request)
  const { data: tasksData } = useSWR<{ results: OpsTaskListItem[] }>(
    liveZoneId ? ['dashboard:ops_tasks', liveZoneId] : null,
    async () => (await listOpsTasks({ zoneId: liveZoneId!, onlyToday: true })).data,
    DASH_SWR,
  );
  // План продаж (#1942): только если функция включена; «сейчас» меняется по часам → раз в 5 мин.
  const { features: zoneFeatures } = useZoneFeatures(liveZoneId);
  // …и только с тогглом «выводить на дашборд смены» (#2962): план можно завести, не показывая.
  const salesPlanOn = !!zoneFeatures?.sales_plan?.enabled && !!zoneFeatures.sales_plan.dashboard;
  const { data: salesPlanData } = useSWR<SalesPlanTodayDto>(
    liveZoneId && salesPlanOn ? ['dashboard:sales-plan', liveZoneId] : null,
    async () => (await getSalesPlanToday(liveZoneId!)).data,
    { ...DASH_SWR, refreshInterval: 300_000 },
  );
  // Refresh every 5 minutes — opens_at changes when the clock crosses midnight.
  const { data: openStatus } = useSWR<ZoneOpenStatus>(
    liveZoneId ? ['dashboard:open-status', liveZoneId] : null,
    async () => (await getZoneOpenStatus(liveZoneId!)).data,
    { ...DASH_SWR, refreshInterval: 300_000 },
  );

  // Remember the board counts once everything has answered — the NEXT visit
  // pre-sizes the panels from these (see readPersistedCounts below). Must live
  // above the access-denied early return (rules of hooks).
  useEffect(() => {
    if (!liveZoneId || !tasksData || !tablesData || !layoutData || !queueData) return;
    const snapshot: PersistedCounts = {
      items: {
        tasks: (tasksData.results ?? []).length,
        tables: (tablesData.sample?.length ?? 0) + (tablesData.clean_sample?.length ?? 0),
        layout: layoutData.sample?.length ?? 0,
        queue: queueData.queues?.length ?? 0,
      },
      problems: {
        tasks: (tasksData.results ?? []).filter(tk => tk.status !== 'done').length,
        tables: tableSummaryStatus(tablesData).dirtyCount,
        layout:
          (layoutData.red_count ?? 0) + (layoutData.yellow_count ?? 0) + (layoutData.orange_count ?? 0),
        queue: (queueData.over_limit_count ?? 0) + (queueData.at_limit_count ?? 0),
      },
    };
    try {
      localStorage.setItem(`dash:counts:${liveZoneId}`, JSON.stringify(snapshot));
    } catch { /* quota/private mode */ }
  }, [liveZoneId, tasksData, tablesData, layoutData, queueData]);

  // «Все филиалы»: the whole page body is the portfolio board; clicking a
  // card drills down into that branch's regular dashboard.
  if (allBranches) {
    return <AllBranchesDashboard onOpenZone={(id) => openZone(id, '/usage/dashboard')} />;
  }

  // After all hooks have fired, render the 404 page when denied —
  // moving this above the useSWR calls violated rules-of-hooks and
  // produced "Rendered fewer hooks than expected" (#300) on re-render.
  if (accessDenied) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-2xl font-semibold">404</p>
        <p className="text-sm text-muted-foreground">
          {t('dashboard.zoneNotFound')}
        </p>
        <Link to="/usage/portfolio" className="text-sm text-primary underline-offset-2 hover:underline">
          {t('dashboard.backToPortfolio')}
        </Link>
      </div>
    );
  }

  // Only show teasers / welcome when we have a definitive "not configured"
  // answer — successful API response with zero items / configured=false.
  // The predicates themselves live in lib/zoneComponents (shared with the
  // all-branches dashboard's BE flags) — do not re-derive them inline.
  const tablesNotConfigured =
    !tablesLoading && !tablesError && tablesData !== undefined && !tablesConfigured(tablesData);
  const layoutNotConfigured =
    !layoutLoading && !layoutError && layoutData !== undefined && !layoutConfigured(layoutData);
  const queueNotConfigured =
    !queueLoading && !queueError && queueData !== undefined && !queuesConfigured(queueData);
  const checklistNotConfigured =
    !checklistsLoading && checklistsData !== undefined && !checklistsConfigured(checklistsData);

  // Manager/admin gate for setup affordances (the "could appear" hint + the
  // welcome card CTAs). Uses the role-aware permission check rather than a
  // superuser-only flag so branch/zone managers see configuration prompts.
  const canManage = canConfigure;

  // All four APIs have returned (data or error) and none are configured.
  const allApisDone = !tablesLoading && !layoutLoading && !queueLoading && !checklistsLoading;
  const allNotConfigured =
    tablesNotConfigured && layoutNotConfigured && queueNotConfigured && checklistNotConfigured;
  const showWelcome = !!zoneId && allApisDone && allNotConfigured;

  // Per-section "has problems" — used to float problem cards to top on mobile.
  // `tasksData` may briefly land as a non-paginated shape (e.g. an empty body
  // when the BE returns 204 / a partial response during a deploy). Guard
  // `.results` defensively instead of trusting the typed shape — the crash
  // here breaks the whole dashboard.
  const tasksHasProblems = (tasksData?.results ?? []).some(t => t.status !== 'done');
  // No tasks for today ("На сегодня задач нет") → don't show the Ближайшие
  // задачи card/tab at all. Only once the tasks response has actually landed
  // (undefined = still loading → keep the card so it can show its skeleton).
  const tasksEmptyToday = tasksData !== undefined && (tasksData.results ?? []).length === 0;
  // Zone produced no frames this (zone-local) day → the photo sections'
  // statuses are stale. Their panels collapse to a grey «Нет кадров за
  // сегодня» note (never "problems", never "в норме") and the tasks panel
  // takes the freed space via weightOf below.
  const tablesNoFrames = !!tablesData && tablesData.has_frames_today === false;
  const layoutNoFrames = !!layoutData && layoutData.has_frames_today === false;
  const queueNoFrames = !!queueData && queueData.has_frames_today === false;
  const tablesStatus = tableSummaryStatus(tablesData);
  // «Перепроверяем» / «неизвестно» — не проблема: в «только проблемы» секция всплывала пустой, с одной
  // надписью «Перепроверяем» без фото (Алексей 03.11 #8626).
  const tablesHasProblems = !!tablesError || tablesStatus.dirtyCount > 0 || tablesStatus.unavailable;
  const layoutHasProblems = !layoutNoFrames && !!layoutData && (layoutData.red_count + layoutData.yellow_count) > 0;
  const queueHasProblems =
    !queueNoFrames && !!queueData && queueData.configured && queueData.over_limit_count + queueData.at_limit_count > 0;
  const noFramesLabel = t('dashboard.noFramesToday');
  // План продаж — раздел дня, если на сегодня в нём хоть что-то есть.
  const salesPlanToday = salesPlanData && (salesPlanData.offers_now.length + salesPlanData.offers_later.length
    + salesPlanData.reminders.length + salesPlanData.targets.length) > 0 ? salesPlanData : null;

  // «Только проблемы» и задачи все выполнены (#2057): задачи уходят иконкой вбок, план продаж —
  // своей карточкой, без пустой «Все задачи выполнены» под ним на полэкрана.
  const planWithTasks = !!salesPlanToday && !(problemsOnly && tasksData !== undefined && !tasksHasProblems);
  const dashSections: DashSection[] = (
    [
      // План продаж — слева сверху, над задачами из чек-листа (Алексей 22.10, #1969).
      !checklistNotConfigured && !tasksEmptyToday
        ? { key: 'tasks', hasProblems: tasksHasProblems, resolved: tasksData !== undefined, okLabel: t('dashboard.sectionOk.tasks'), pinned: planWithTasks,
            node: planWithTasks && salesPlanToday ? (
              <div className="flex h-full min-h-0 flex-col gap-2">
                <div className="max-h-[55%] shrink-0 overflow-hidden"><SalesPlanCard data={salesPlanToday} bare={isMobile} /></div>
                <div className="min-h-0 flex-1"><MyTasksCard zoneId={zoneId} search={search} bare={isMobile} problemsOnly={problemsOnly} /></div>
              </div>
            ) : <MyTasksCard zoneId={zoneId} search={search} bare={isMobile} problemsOnly={problemsOnly} /> }
        : null,
      !tablesNotConfigured
        ? { key: 'tables', hasProblems: tablesHasProblems, resolved: tablesData !== undefined && !tablesError, okLabel: t('dashboard.sectionOk.tables'), node: <TablesCleanlinessCard zoneId={zoneId} bare={isMobile} mode={sectionMode} problemsOnly={problemsOnly} /> }
        : null,
      !layoutNotConfigured
        ? { key: 'layout', hasProblems: layoutHasProblems, resolved: layoutData !== undefined, okLabel: layoutNoFrames ? noFramesLabel : t('dashboard.sectionOk.layout'), node: <LayoutSummaryCard zoneId={zoneId} bare={isMobile} mode={sectionMode} problemsOnly={problemsOnly} /> }
        : null,
      !queueNotConfigured
        ? { key: 'queue', hasProblems: queueHasProblems, resolved: queueData !== undefined, okLabel: queueNoFrames ? noFramesLabel : t('dashboard.sectionOk.queue'), node: <QueueCard zoneId={zoneId} bare={isMobile} problemsOnly={problemsOnly} /> }
        : null,
      // Задач на сегодня нет — план продаж своей карточкой, тоже слева сверху.
      salesPlanToday && (checklistNotConfigured || tasksEmptyToday || !planWithTasks)
        ? { key: 'sales_plan', hasProblems: false, resolved: true, okLabel: '', pinned: true, node: <SalesPlanCard data={salesPlanToday} bare={isMobile} /> }
        : null,
    ] as (DashSection | null)[]
  )
    .filter((s): s is DashSection => s !== null)
    .sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) || (b.hasProblems ? 1 : 0) - (a.hasProblems ? 1 : 0));

  // Desktop board: all section cards on screen at once, but each panel's SIZE
  // follows its content (see DesktopBoard) — a section with 12 monitored units
  // gets proportionally more screen than one with 2, so tiles come out roughly
  // the same size across sections. Weight = item count + a problem boost
  // (problem tiles render bigger and need the room); tasks panel weights by
  // its clamped row count.
  // While a summary is still loading, its counts come from the LAST visit
  // (localStorage per zone, saved above) — the board mounts at (close to) its
  // final proportions instead of opening as an even grid and violently
  // re-balancing as each response lands.
  const persisted = readPersistedCounts(zoneId);
  const itemCountByKey: Record<string, number> = {
    tasks: tasksData ? (tasksData.results ?? []).length : persisted.items?.tasks ?? 0,
    tables: tablesData
      ? (tablesData.sample?.length ?? 0) + (tablesData.clean_sample?.length ?? 0)
      : persisted.items?.tables ?? 0,
    layout: layoutData ? layoutData.sample?.length ?? 0 : persisted.items?.layout ?? 0,
    queue: queueData ? queueData.queues?.length ?? 0 : persisted.items?.queue ?? 0,
  };
  const problemCountByKey: Record<string, number> = {
    tasks: tasksData
      ? (tasksData.results ?? []).filter(tk => tk.status !== 'done').length
      : persisted.problems?.tasks ?? 0,
    tables: tablesData ? tablesStatus.dirtyCount : persisted.problems?.tables ?? 0,
    layout: layoutData
      ? (layoutData.red_count ?? 0) + (layoutData.yellow_count ?? 0) + (layoutData.orange_count ?? 0)
      : persisted.problems?.layout ?? 0,
    queue: queueData
      ? (queueData.over_limit_count ?? 0) + (queueData.at_limit_count ?? 0)
      : persisted.problems?.queue ?? 0,
  };
  // Panel weight drives BOTH the screen share and (via the order tiebreak) the
  // prominence — so it must be STABLE between polls or the board reshuffles on
  // every tick. The bad-state contribution is therefore BINARY (does the
  // section have a problem?) rather than proportional to the fluctuating
  // problem count, and the content part (item count) is stable across a shift.
  // Net: a panel's weight changes only when it actually flips good↔bad.
  const noFramesByKey: Record<string, boolean> = {
    tasks: false, tables: tablesNoFrames, layout: layoutNoFrames, queue: queueNoFrames,
  };
  // «Всё везде выполнено» — каждая секция ответила, проблем нет и кадры есть (секция без
  // кадров не «в порядке», а слепая). Тогда сверху летает Линза-робот (Алексей 22.10, #1834/#1864).
  // План продаж — не «в норме/проблема», а подсказка смены: в оценке и в плашках не участвует.
  const statusSections = dashSections.filter(s => s.key !== 'sales_plan');
  const allGood = statusSections.length > 0 && statusSections.every(s => s.resolved && !s.hasProblems && !noFramesByKey[s.key]);
  // «Только проблемы» с проблемами (#1941): разделы без проблем — плашками сбоку, на доске
  // только то, что требует действия. Неотвеченные (грузятся) остаются на доске.
  const sidePills = statusSections.filter(s => s.resolved && !s.hasProblems && !s.pinned);
  const boardSections = dashSections.filter(s => !sidePills.includes(s));
  // Телефон боком (Алексей 03.11 #8665: «задачи запагинировались… в правую колонку, там место есть»):
  // задачи — строкой на задачу в колонке под средним чеком, а не отдельной панелью доски. Только если на
  // доске остаётся что показать; «в норме» в «только проблемы» — как раньше, плашкой.
  const tasksSec = dashSections.find(s => s.key === 'tasks');
  const tasksInColumn = denseDash && !isMobile && !!tasksSec && !tasksSec.pinned
    && !(problemsOnly && sidePills.includes(tasksSec))
    && (problemsOnly ? boardSections : dashSections).some(s => s.key !== 'tasks');
  const columnTasks = tasksInColumn
    ? <MyTasksCard zoneId={zoneId} search={search} problemsOnly={problemsOnly} column /> : undefined;
  const noTasks = (xs: DashSection[]) => (tasksInColumn ? xs.filter(s => s.key !== 'tasks') : xs);
  const weightOf = (key: string) => {
    // A frameless section is just a one-line note — a sliver, so the sections
    // that still have content (usually Ближайшие задачи) take the space.
    if (noFramesByKey[key]) return 0.6;
    // План продаж: по числу строк (крупное «сейчас» + напоминания + цели), чтобы не обрезалось.
    const sp = salesPlanToday;
    const planW = sp ? Math.min(4, 1 + (sp.offers_now.length * 1.4 + (sp.reminders.length ? 1 : 0) + (sp.offers_later.length ? 1 : 0) + sp.targets.length) * 0.45) : 0;
    // При проблемах план — справка, не действие: не шире ~40 % (#2057).
    if (key === 'sales_plan') return problemsOnly ? Math.min(planW, 2.5) : planW;
    // Задачи с планом продаж над ними — место под обе части.
    if (key === 'tasks' && sp && planWithTasks) return planW + Math.max(1, Math.min(Math.max(itemCountByKey.tasks ?? 0, 2), 8) * 0.45);
    const items = itemCountByKey[key] ?? 0;
    const probs = problemCountByKey[key] ?? 0;
    const hasProb = probs > 0;
    if (problemsOnly) {
      // Healthy sections collapse to a one-line note → a sliver; bad sections
      // size by their (stable) total content so they read large.
      // Все задачи выполнены — новых сегодня не будет: карточка минимальная (#1880 п.7).
      if (key === 'tasks' && !hasProb) return 0.3;
      // Просроченные задачи — строки текста, не фото: вес как у задач в обычном режиме. С весом
      // 1 + число задач три просрочки забирали пол-экрана, а столам оставалась щель с крошечным
      // фото (Алексей 28.10 #3116, Ратуша: «старая проблема, фото не видно совсем»).
      if (key === 'tasks') return Math.max(1, Math.min(Math.max(probs, 2), 8) * 0.45);
      // На доске видны только проблемные плитки — делим место по ним, а не по всем единицам
      // раздела: иначе столы (11 единиц, 1 проблема) сжимали очередь в щель (#2054).
      return hasProb ? 1 + probs : 0.6;
    }
    if (key === 'tasks') {
      // Task rows are compact constant-height strips (~60px — a fraction of a
      // photo tile), so the per-item rate sits well below the photo sections'.
      // NO problem boost: an open task renders the SAME size as a done one
      // (unlike photo sections, where a problem tile IS bigger), and
      // problems-first prominence already comes from the hasProblems order
      // sort — the old +60% boost only manufactured empty space inside the
      // panel ("карточка могла бы быть меньше").
      return Math.max(1, Math.min(Math.max(items, 2), 8) * 0.45);
    }
    // Photo sections weight by what their tiles actually need: a problem tile
    // renders big (bigScale rows) and must stay on page 1, a green proof tile
    // is small. The old items+flat-boost formula let a mostly-green section
    // with many units out-weigh a section with several ACTIVE problems (zone
    // 47: Чистота столов with 1 problem among 11 tables dwarfed Выкладка with
    // 3 problems, pushing a problem to page 2). Counts only materialise at
    // flip boundaries (the grid memo is keyed on the problem flags), so this
    // stays calm between polls.
    return Math.max(1, probs * 2.6 + Math.max(0, items - probs) * 0.9);
  };

  // Categories that COULD appear if configured — listed in the bottom
  // "could appear" card. Only surface a category once its API has returned a
  // definitive "not configured" answer (avoids flashing it during load).
  const couldAppear: string[] = [
    checklistNotConfigured ? 'checklists' : null,
    tablesNotConfigured ? 'tables' : null,
    layoutNotConfigured ? 'layout' : null,
    queueNotConfigured ? 'queue' : null,
    zoneFeatures && !zoneFeatures.receipts?.enabled ? 'receipts' : null,
    // План есть, но не выводится на дашборд — та же подсказка: тоггл в его настройках.
    zoneFeatures && !salesPlanOn ? 'sales_plan' : null,
  ].filter((c): c is string => c !== null);
  // Manager/admin-only: the "could appear" hint is a setup affordance, not
  // operator data — line workers shouldn't see configuration prompts.
  const showCouldAppear = !!zoneId && !showWelcome && couldAppear.length > 0 && canManage;
  const isClosed = !!openStatus && !openStatus.is_open;
  const robotSlot = problemsOnly && allGood && !isClosed;
  const showRobot = robotSlot && !robotHidden;
  const boardReady = statusSections.length > 0 && statusSections.every(s => s.resolved);

  // Mobile tabs: a health tone per section (drives the tab dot) + the available
  // sections in a fixed display order, with the active section resolved/clamped.
  const layoutTone: OverallTone = !layoutData || layoutData.total === 0 || layoutNoFrames ? 'none'
    : layoutData.red_count > 0 ? 'critical'
    : (layoutData.red_count + layoutData.yellow_count) > 0 ? 'attention' : 'ok';
  const tablesTone: OverallTone = tablesError ? 'none' : tablesStatus.tone;
  const tasksTone: OverallTone = !tasksData ? 'none'
    : (tasksData.results ?? []).some(tk => tk.status === 'overdue') ? 'critical'
    : tasksHasProblems ? 'attention' : 'ok';
  const queueTone: OverallTone = !queueData || !queueData.configured || queueNoFrames ? 'none'
    : queueData.health === 'red' ? 'critical'
    : queueData.health === 'yellow' ? 'attention' : 'ok';
  const toneByKey: Record<string, OverallTone> = {
    layout: layoutTone, tables: tablesTone, tasks: tasksTone, queue: queueTone, sales_plan: 'ok',
  };
  const availableKeys = dashSections.map(s => s.key);
  const tabs: DashTab[] = TAB_ORDER.filter(k => availableKeys.includes(k)).map(k => ({
    key: k,
    label: t(TAB_META[k].labelKey),
    Icon: TAB_META[k].Icon,
    tone: toneByKey[k] ?? 'none',
  }));
  // Default to the most-urgent available section (dashSections is sorted
  // problems-first); clamp when the active tab is no longer available.
  const resolvedActive = activeTab && availableKeys.includes(activeTab)
    ? activeTab
    : (dashSections[0]?.key ?? null);
  const showTabBar = isMobile && allApisDone && !showWelcome && tabs.length > 1 && !isClosed;

  // R4/R6: tab switching. The carousel derives the slide direction from the
  // tab order (a later tab enters from the right) and handles the horizontal
  // finger-drag itself — see SwipeTabCarousel.
  const goToTab = (nextKey: string) => setActiveTab(nextKey);

  // No branch selected yet: show a single clean "pick a branch" prompt instead
  // of a grid of cards each showing its own "select a branch" warning hint.
  // Single-branch users auto-select (the BranchSelector does this), so they
  // never linger here.
  if (!zoneId) {
    return <BranchRequiredState />;
  }

  return (
    // Fill the scroll container exactly and clip — the board fits the screen
    // (no page scroll); the adaptive grids inside paginate to fit their cells.
    // Масштаб −/+ — CSS zoom только на самом дашборде, сайдбар и шапка остаются в масштабе
    // браузера (Алексей 22.10, #1897).
    // Панель и оверлей «обновлено» — СНАРУЖИ увеличенного блока: position:fixed внутри
    // элемента с zoom уезжал в середину экрана и за его край (#1901/#1903).
    <TableGalleryProvider key={zoneId} zoneId={zoneId}>
    <RemindContext.Provider value={remind.api}>
    <AvgCheckContext.Provider value={avgCheck ?? null}>
    <div className="flex h-full w-full flex-col overflow-hidden">
      <h1 className="sr-only">{t('dashboard.title')}</h1>
      {remind.active && !robotHidden && (
        <ReminderRobot key={`${remind.active.key}:${remind.active.minutes}`} targetKey={remind.active.key} minutes={remind.active.minutes} onDone={remind.done} />
      )}
      {showControlPanel && !showWelcome && !accessDenied && (
        // Плавающий кластер в правом НИЖНЕМ углу (Алексей 20.10): масштаб −/+ и «на весь
        // экран»; в полноэкранном режиме шапки нет — туда же переезжают «кроп / полное фото»
        // и «только проблемы» (только в полноэкранном, в обычном они в шапке).
        // position:fixed + обратный zoom: панель не прыгает и не меняет размер при
        // масштабе страницы — всегда на месте под пальцем (Алексей 20.10 #1631).
        // Панель управления прикреплена к правому краю экрана (Алексей 20.10 #1639: «сливается
        // с контентом… хороший оверлей должен быть attached к краю экрана»): сплошной фон,
        // рамка и тень, скругление только со стороны контента; в полноэкранном режиме уезжает
        // за край и выезжает по тапу. Пришло обновление — панель видна всегда, с кнопкой.
        // position:fixed + обратный zoom: не прыгает и не меняет размер при масштабе страницы.
        <div
          data-dash-bar
          ref={barRef}
          onPointerDown={() => pokeBar()}
          // AI308 (#1641): не «окно, обрезанное краем», а полупрозрачная полоса во всю ширину
          // нижнего края (как панель видеоплеера): градиент от фона к прозрачному, кнопки справа,
          // страница просвечивает. Над нижними вкладками телефона — полупрозрачная пилюля.
          // #1880 п.2: размытие только вокруг самой панели — компактная пилюля в углу, а не
          // полоса во всю ширину (та закрывала и профиль в сайдбаре).
          className={cn(
            // Tailwind 4: translate-y-* — это CSS-свойство `translate`, не `transform`; без него в
            // transition панель прыгала, а не выезжала (Алексей 22.10, #1885).
            'fixed right-3 z-40 flex items-center justify-end gap-1 rounded-full border border-border/40 bg-background/60 p-1 text-foreground shadow-lg backdrop-blur-md transition-[opacity,translate,transform] duration-300 ease-out',
            // Вне полноэкранного — видна всегда (Алексей #2056), гаснет только в полноэкранном.
            kiosk && !barShown && !updateButton && 'pointer-events-none translate-y-[150%] opacity-0',
          )}
          style={{
            bottom: showTabBar ? `calc(${tabBarClear > 0 ? `${Math.round(tabBarClear * zoom)}px` : 'env(safe-area-inset-bottom) + 5.5rem'} + 0.5rem)` : 'max(0.75rem, env(safe-area-inset-bottom))',
          } as React.CSSProperties}
        >
          {updateButton && (
            <>
              <Button type="button" size="sm" onClick={applyUpdate} className="h-9 max-w-[22rem] gap-2 whitespace-normal rounded-lg px-3 text-left text-xs font-semibold leading-tight shadow-sm">
                <RefreshCw className="size-4 shrink-0" />
                {t('dashboard.updateReady')}
              </Button>
              <span className="mx-1 h-6 w-px shrink-0 bg-border" aria-hidden />
            </>
          )}
          {kiosk && showViewControls && (
            <>
              <div className="pr-0.5"><DashboardViewControls {...viewControlsProps} bar /></div>
              <span className="mx-1 h-6 w-px shrink-0 bg-border" aria-hidden />
            </>
          )}
          {robotSlot && (
            <>
              {/* Текстом, не иконкой (Алексей 22.10, #1898). */}
              <button type="button" onClick={() => setRobotHidden(!robotHidden)} aria-pressed={!robotHidden}
                className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground">
                {robotHidden ? <Bot className="size-4 shrink-0" /> : <BotOff className="size-4 shrink-0" />}
                {t(robotHidden ? 'dashboard.robotShow' : 'dashboard.robotHide')}
              </button>
              <span className="mx-1 h-6 w-px shrink-0 bg-border" aria-hidden />
            </>
          )}
          <Tooltip content={t('dashboard.zoomOut')} side="top">
            <Button type="button" variant="ghost" size="icon-sm" onClick={() => stepZoom(-0.1)} disabled={zoom <= 0.7}
              aria-label={t('dashboard.zoomOut')} className="rounded-full text-muted-foreground hover:text-foreground">
              <ZoomOut className="size-4" />
            </Button>
          </Tooltip>
          <Tooltip content={t('dashboard.zoomReset')}><button type="button" onClick={() => setZoom(1)}
            className="min-w-10 text-center text-[11px] tabular-nums text-muted-foreground hover:text-foreground">
            {Math.round(zoom * 100)}%
          </button></Tooltip>
          <Tooltip content={t('dashboard.zoomIn')} side="top">
            <Button type="button" variant="ghost" size="icon-sm" onClick={() => stepZoom(0.1)} disabled={zoom >= zoomCap - 0.001}
              aria-label={t('dashboard.zoomIn')} className="rounded-full text-muted-foreground hover:text-foreground">
              <ZoomIn className="size-4" />
            </Button>
          </Tooltip>
          <span className="mx-1 h-6 w-px shrink-0 bg-border" aria-hidden />
          <Tooltip content={t(kiosk ? 'dashboard.fullscreenExit' : 'dashboard.fullscreen')} side="top">
            <Button type="button" variant="ghost" size="icon-sm" onClick={toggleKiosk}
              aria-label={t(kiosk ? 'dashboard.fullscreenExit' : 'dashboard.fullscreen')} className="rounded-full text-muted-foreground hover:text-foreground">
              {kiosk ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
            </Button>
          </Tooltip>
          {/* Спрятать сразу, не дожидаясь 5 с (#1880 п.3); вернуть — тап в пустое место. */}
          {kiosk && !updateButton && (
            <Tooltip content={t('dashboard.hideBar')} side="top">
              <Button type="button" variant="ghost" size="icon-sm" onPointerDown={e => e.stopPropagation()} onClick={hideBar}
                aria-label={t('dashboard.hideBar')} className="rounded-full text-muted-foreground hover:text-foreground">
                <ChevronDown className="size-4" />
              </Button>
            </Tooltip>
          )}
        </div>
      )}

      {justUpdated && showControlPanel && !showWelcome && !accessDenied && (
        <JustUpdatedOverlay
          onFullscreen={() => { setKiosk(true); setJustUpdated(false); }}
          onDismiss={() => setJustUpdated(false)}
        />
      )}

      {/* width/height 100 %: в Chrome проценты у элемента с zoom уже считаются от родителя
          после масштаба — деление на zoom давало блок в 1/zoom места (проверено скриншотом). */}
      <div className="flex h-full w-full flex-col overflow-hidden" data-zooming={zooming ? '' : undefined} style={{ zoom } as React.CSSProperties}>
      {/* No all-or-nothing gate: the card grid renders immediately and each card
          fills/drops as its OWN request lands (structural skeleton → photos),
          so one stalled summary can never freeze the whole board. WelcomeCard
          shows only once every plugin has definitively answered "not configured". */}
      {showWelcome ? (
        <div className="p-3 md:p-4"><WelcomeCard search={search} canManage={canManage} /></div>
      ) : (
        <div
          className={cn('flex min-h-0 flex-1 flex-col', denseDash ? 'gap-1.5 p-1.5' : 'gap-2 p-2 md:gap-2.5 md:p-2.5')}
          // Reserve clearance for the floating tab bar so the card area never
          // sits under it. Uses the bar's MEASURED height (tabBarClear, set via
          // onMeasure); falls back to ~5.5rem until the first measurement lands.
          // Вне полноэкранного панель масштаба видна всегда и закрывала галку «Убрали» на нижней
          // правой плитке — резервируем под неё низ (делим на zoom: панель вне масштаба).
          style={showTabBar ? { paddingBottom: tabBarClear > 0 ? `${tabBarClear}px` : 'calc(env(safe-area-inset-bottom) + 5.5rem)' }
            : showControlPanel && !kiosk && !showWelcome && !accessDenied ? { paddingBottom: `${Math.round(60 / zoom)}px` } : undefined}
        >
          {/* Closed: data renders by default; a slim banner marks it end-of-shift. */}
          {isClosed && <ClosedBanner opensAt={openStatus!.opens_at} />}
          {/* Робот — только в режиме «только проблемы» (#1878/#1879): там зелёные секции сжаты, место есть. */}
          {showRobot && isMobile && <AllDoneRobot height={64} />}

          {isClosed ? (
            // Филиал закрыт (#8759/#8761): итоги дня вместо доски — без «Нет данных» и «1 ч назад».
            <ClosedDayBoard zoneId={zoneId!} checklist={!checklistNotConfigured}
              photoKeys={availableKeys.filter(k => k === 'tables' || k === 'layout' || k === 'queue')}
              robot={!isMobile} row={isMobile || !isLandscape} />
          ) : isMobile ? (
            // Phone: one section, full screen — the bottom tab bar switches it,
            // and a horizontal drag pulls the neighbouring section in under the
            // finger. flex-col + overflow-hidden so the section is a
            // hard-bounded box: a child can never paint past it into the
            // tab-bar clearance. -mx cancels this parent's own horizontal
            // padding — SwipeTabCarousel's panes each carry that same inset
            // themselves (see its className), so the resting view lines up
            // exactly as before, but while two panes are visible mid-drag they
            // keep a real gutter between them instead of touching edge-to-edge.
            <div className="-mx-3 flex min-h-0 flex-1 flex-col overflow-hidden md:-mx-4">
              <SwipeTabCarousel
                order={tabs.map(tb => tb.key)}
                active={resolvedActive ?? 'none'}
                render={key => dashSections.find(s => s.key === key)?.node ?? null}
                onCommit={goToTab}
              />
            </div>
          ) : (
            // iPad/desktop: all cards at once, content-weighted to fit the viewport.
            // SceneSwitch смонтирован всегда — помнит прошлую сцену и играет переход (#2036/#2080).
            <SceneSwitch
              showRobot={showRobot}
              ready={boardReady}
              robot={(celebrate) => {
                const robotEnter = celebrate !== 'off' ? 1100 : undefined;
                return (
              // Всё выполнено, «только проблемы» (#1877): большие пустые «всё ок»-карточки
              // сжимаются в ряд плашек снизу, а освободившееся место — роботу.
              // Всё выполнено (#1899/#1911/#1912): плашки разделов — колонкой вдоль правого края,
              // робот летает в широкой области слева; ему не всё внимание.
              // С планом продаж (#2049): план — главная карточка слева сверху, под ним летает
              // робот чуть меньше обычного, разделы «в норме» — плашками справа.
              // Портретный планшет (#8615): плашки — полосой под роботом, а не колонкой в треть ширины.
              <div className={cn('relative flex min-h-0 flex-1', denseDash ? 'gap-1.5' : 'gap-3', !isLandscape && 'flex-col')}>
                {salesPlanToday ? (
                  <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
                    <div className="max-h-[70%] shrink-0 overflow-hidden"><SalesPlanCard data={salesPlanToday} hero /></div>
                    <AllDoneRobot height={84} grow enterDelay={robotEnter} />
                  </div>
                ) : <AllDoneRobot height={110} grow enterDelay={robotEnter} />}
                <OkSectionPills sections={statusSections} noFrames={noFramesByKey} row={!isLandscape}
                  className={celebrate !== 'off' ? 'animate-in fade-in slide-in-from-right-24 duration-700 ease-out' : undefined} />
                {celebrate !== 'off' && (
                  <div className={cn('pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-background/80 backdrop-blur-sm transition-opacity duration-500',
                    celebrate === 'fade' ? 'opacity-0' : 'opacity-100 animate-in fade-in duration-300')}>
                    <AnimatedCheck className="size-24" />
                    <div className="ok-rise text-xl font-semibold">{t('dashboard.allFixed')}</div>
                  </div>
                )}
              </div>
                );
              }}
              board={problemsOnly && sidePills.length > 0 && boardSections.length > 0 ? (
              // Есть проблемы (#1941): проблемные разделы — на доске, разделы «в норме» и слепые
              // (нет кадров) — той же колонкой плашек справа, что и при «всё выполнено».
              <ProblemsBoard boardSections={noTasks(boardSections)} sidePills={sidePills} weightOf={weightOf} noFrames={noFramesByKey} row={!isLandscape} tasks={columnTasks} />
            ) : columnTasks || (avgCheck?.connected && (avgCheck.avg_check != null || !!avgCheck.tips?.length)) ? (
              // «Все данные»: средний чек и «Обычно сейчас берут» — той же колонкой справа (Алексей 03.11 #3592).
              <div className={cn('flex min-h-0 flex-1', denseDash ? 'gap-1.5' : 'gap-3', !isLandscape && 'flex-col')}>
                <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                  <DesktopBoard sections={noTasks(dashSections)} weightOf={weightOf} problemsOnly={problemsOnly} />
                </div>
                <OkSectionPills sections={[]} noFrames={noFramesByKey} row={!isLandscape} tasks={columnTasks} />
              </div>
            ) : (
              <DesktopBoard sections={dashSections} weightOf={weightOf} problemsOnly={problemsOnly} />
            )}
            />
          )}

          {/* "Что ещё" hint: desktop only (on mobile it moves into the header
              section-info popup). Kept off the fit-grid as a slim shrink-0 row. */}
          {!isMobile && showCouldAppear && !kiosk && (
            // В полноэкранном не показываем: он для сотрудников (Алексей 28.10 #3104).
            // Пунктиром слева, в одной строке с нижней панелью масштаба (Алексей 28.10 #3095/#3100):
            // строка опускается в запас под панель и не заходит под неё справа. Панель вне масштаба
            // страницы (position:fixed), строка — внутри, поэтому отступы делятся на zoom.
            <div className="shrink-0" style={showControlPanel && !kiosk && barW > 0
              ? { marginBottom: `${-Math.round(43 / zoom)}px`, marginRight: `${Math.round((barW + 12) / zoom)}px` }
              : undefined}>
              <CouldAppearHint categories={couldAppear} search={search} />
            </div>
          )}

          {showTabBar && (
            <DashboardTabBar tabs={tabs} active={resolvedActive} onSelect={goToTab} onMeasure={setTabBarClear} />
          )}
        </div>
      )}
      </div>
    </div>
    </AvgCheckContext.Provider>
    </RemindContext.Provider>
    </TableGalleryProvider>
  );
}
